import { FunctionComponent } from 'react';
import type { CancelTokenSource } from 'axios';
import { LS_IQ11_TOKEN } from '../../../constants';
import { arrayToObject, sanitizeToNumeric, toBool } from '../../../utils/utils';
import * as types from './VoPayApi.types';
import { VoPayResponse } from './VoPayApi.types';
import { createHttp } from '../../../services/http';
import { verifyResponseType } from './VoPayApi.validation';
import contactVerification, { type ContactVerificationApi } from './contactVerification';
import address, { type AddressApi } from './address';

const createVoPayApi = (baseUrl: string) => {
  const http = createHttp(baseUrl);
  const learningHttp = createHttp('https://vopay.com/wp-json/learning-centre/v1');
  const ss = window.sessionStorage;

  const api: Api = {
    ...address(http),
    ...contactVerification(http),
    fetchAccountTranslationsUrl: async (params, options) => {
      const url = '/portal/translations';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source: options?.source,
        bearer: options?.bearer,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.FetchTranslationsUrlResponse>(url, data, possibleResTypes);
    },
    generateEmployeeOTP: async (params, source?) => {
      const url = '/services/employee/login';

      const { data } = await http.post<typeof params, unknown>({
        url,
        // config: {},
        config: {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
        },
        body: new URLSearchParams(params).toString(),
        // body: JSON.stringify(params),
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse, VoPayResponse.GenerateEmployeeOTPResponse];

      return verifyResponseType<types.GenerateEmployeeOTPResponse>(url, data, possibleResTypes);
    },
    validateEmployeeOTP: async (params, source?) => {
      const url = '/services/employee/validate-otp';

      const { data } = await http.post<typeof params, unknown>({
        url,
        // config: {},
        config: {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
        },
        body: new URLSearchParams(params).toString(),
        // body: JSON.stringify(params),
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse, VoPayResponse.ValidateEmployeeOTPResponse];

      return verifyResponseType<types.ValidateEmployeeOTPResponse>(url, data, possibleResTypes);
    },
    fetchDomain: async (source) => {
      const url = '/account/domain';

      const { data } = await http.get({
        url,
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.FetchDomainApiResponse];

      const res = verifyResponseType<types.FetchDomainApiResponse>(url, data, possibleResTypes);

      return {
        ...res,
        Domain: hasDomain(res.Domain) ? res.Domain : null,
      };

      function hasDomain(domain: types.FetchDomainApiResponse['Domain']): domain is types.Domain {
        return !!domain && Object.keys(domain).length > 0;
      }
    },
    addDomain: async (params, source) => {
      const url = '/account/domain/add';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.AddDomainResponse];

      return verifyResponseType<types.AddDomainResponse>(url, data, possibleResTypes);
    },
    verifyDomain: async (params, source) => {
      const url = '/account/domain/verify';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.VerifyDomainResponse];

      return verifyResponseType<types.VerifyDomainResponse>(url, data, possibleResTypes);
    },
    convertFile: async (params, source) => {
      const url = '/file-conversion/convert';

      const { data } = await http.post<typeof params, unknown>({
        url,
        body: JSON.stringify(params),
        config: {
          headers: {
            'Content-Type': 'application/json',
          },
        },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.FileConversionResponse>(url, data, possibleResTypes);
    },
    getFileConversionConfigurations: async (source) => {
      const url = '/file-conversion/configurations';

      const { data } = await http.get<undefined, unknown>({
        url,
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      const res = verifyResponseType<types.FileConversionConfigurationsApiResponse>(url, data, possibleResTypes);

      return {
        ...res,
        Configurations: Object.values(res.Configurations || {}),
      };
    },
    getFileConversionHistory: async (params, source) => {
      const url = '/file-conversion/history';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      const resp = verifyResponseType<types.FileConversionHistoryResponseApi>(url, data, possibleResTypes);
      return {
        ...resp,
        Conversions: Object.values(resp.Conversions || {}).map((conversion) => ({
          ...conversion,
          IsSubmitted: toBool(conversion.IsSubmitted || '0'),
        })),
      };
    },
    downloadFileConversion: async (params, source) => {
      const url = `/file-conversion/download`;

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.FileConversionDownloadResponse>(url, data, possibleResTypes);
    },
    downloadSourceFileConversion: async (params, source) => {
      const url = `/file-conversion/download-source`;

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.FileConversionDownloadResponse>(url, data, possibleResTypes);
    },
    submitFileConversion: async (params, source) => {
      const url = '/file-conversion/submit';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.FileConversionSubmitResponse>(url, data, possibleResTypes);
    },
    uploadFileConversion: async (params, source) => {
      const url = '/file-conversion/upload';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: {},
        source,
        body: JSON.stringify(params),
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.FileConversionUploadResponse>(url, data, possibleResTypes);
    },
    getFileConversionPreprocessingConfigs: async (params, source) => {
      const url = '/file-conversion/preprocessing/configurations';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: {},
        source,
        body: JSON.stringify(params),
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      const res = verifyResponseType<types.FileConversionPreprocessingConfigsApiResponse>(url, data, possibleResTypes);

      return {
        ...res,
        Configs: Object.values(res.Configs || {}).map((config) => ({
          ...config,
          IsDefault: toBool(config.IsDefault),
        })),
      };
    },
    deleteDomain: async (params, source) => {
      const url = '/account/domain/delete';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.DeleteDomainResponse];

      return verifyResponseType<types.DeleteDomainResponse>(url, data, possibleResTypes);
    },
    linkGooglePay: async (params, source) => {
      const url = '/google-pay/link-google-pay';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: {},
        body: JSON.stringify(params),
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.LinkGooglePayResponse];

      return verifyResponseType<types.LinkGooglePayResponse>(url, data, possibleResTypes);
    },
    fetchAccountBillingSettings: async (params, source) => {
      const url = '/partner/account/billing-settings';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.AccountBillingSettingsResponse];

      return verifyResponseType<types.AccountBillingSettingsResponse>(url, data, possibleResTypes);
    },
    fetchAccountTransactions: async (params, bearer, source) => {
      const url = '/account/transactions';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.AccountTransactions];
      return verifyResponseType<types.AccountTransactions>(url, data, possibleResTypes);
    },
    fetchAccountUsers: async (params, bearer, source) => {
      const url = '/account/users';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      const res = verifyResponseType<types.FetchAccountUsersApiResponse>(url, data, possibleResTypes);

      return {
        ...res,
        Users: Object.values(res.Users),
      };
    },
    fetchBusinessTypes: async (source) => {
      const url = 'partner/account/business-types';

      const { data } = await http.get<Record<never, never>, unknown>({
        url,
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.BusinessTypesResponse>(url, data, possibleResTypes);
    },
    fetchBillingSettingsDetails: async (params, source) => {
      const url = '/partner/billing-settings-details';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.PartnerBillingSettingsDetailsResponse];

      return verifyResponseType<types.PartnerBillingSettingsDetailsResponse>(url, data, possibleResTypes);
    },
    fetchBillingPackageDetails: async (params, source) => {
      const url = '/partner/billing-package-details';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.PartnerBillingPackageDetailsResponse];

      return verifyResponseType<types.PartnerBillingPackageDetailsResponse>(url, data, possibleResTypes);
    },
    fetchBillingPackages: async (source) => {
      const url = '/partner/billing-packages';

      const { data } = await http.get<Record<never, never>, unknown>({
        url,
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.PartnerBillingPackagesResponse];

      return verifyResponseType<types.PartnerBillingPackagesResponse>(url, data, possibleResTypes);
    },
    fetchCreditCardTransaction: async (params, bearer, source) => {
      const url = '/credit-card/charge/transaction';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.CreditCardTransactionResponse];
      return verifyResponseType<types.CreditCardTransactionResponse>(url, data, possibleResTypes);
    },
    fetchEFTFundTransaction: async (params, bearer, source) => {
      const url = '/eft/fund/transaction';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.EftFundingTransactionResponse];
      return verifyResponseType<types.EftFundingTransactionResponse>(url, data, possibleResTypes);
    },
    fetchEFTWithdrawTransaction: async (params, bearer, source) => {
      const url = '/eft/withdraw/transaction';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.EftWithdrawTransactionResponse];
      return verifyResponseType<types.EftWithdrawTransactionResponse>(url, data, possibleResTypes);
    },
    fetchFinancialInstitutionBranches: async (params, source) => {
      const url = '/iq11/financial-institutions-branches';

      const { data } = await http.get<typeof params, types.FinancialInstitutionBranchesApiResponse>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      const res = verifyResponseType<types.FinancialInstitutionBranchesApiResponse>(url, data, possibleResTypes);

      return {
        ...res,
        Branches: Object.values(res.Branches || {}),
        FinancialInstitutions: res.FinancialInstitutions ? Object.values(res.FinancialInstitutions || {}) : undefined,
      };
    },
    fetchFinancialInstitutions: async (params, source) => {
      const url = '/iq11/financial-institutions';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.FinancialInstitutionsResponse>(url, data, possibleResTypes);
    },
    fetchGlobalPayToken: async (params, bearer, source) => {
      const url = '/ucp/payment-methods';
      const GPVersion = '2021-03-22';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: {},
        source,
        bearer,
        GPVersion,
        body: JSON.stringify(params),
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.GlobalPayTokenResponse];

      return verifyResponseType<types.GlobalPayTokenResponse>(url, data, possibleResTypes);
    },
    fetchInteracMoneyRequestTransaction: async (params, bearer, source) => {
      const url = '/interac/money-request/transaction';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.InteracTransactionResponse];
      return verifyResponseType<types.InteracTransactionResponse>(url, data, possibleResTypes);
    },
    fetchInteracBulkPayoutTransaction: async (params, bearer, source) => {
      const url = '/interac/bulk-payout/transaction';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.InteracBulkPayoutTransactionResponse];
      return verifyResponseType<types.InteracBulkPayoutTransactionResponse>(url, data, possibleResTypes);
    },
    fetchRTRWithdrawTransaction: async (params, bearer, source) => {
      const url = '/rtr/withdraw/transaction';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.RTRWithdrawTransactionResponse];
      return verifyResponseType<types.RTRWithdrawTransactionResponse>(url, data, possibleResTypes);
    },
    fetchFedwireWithdrawTransaction: async (params, bearer, source) => {
      const url = '/fedwire/withdraw/transaction';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.FedwireTransactionResponse>(url, data, possibleResTypes);
    },
    fetchFedwireFundTransaction: async (params, bearer, source) => {
      const url = '/fedwire/fund/transaction';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.FedwireTransactionResponse>(url, data, possibleResTypes);
    },
    fetchRTPWithdrawTransaction: async (params, bearer, source) => {
      const url = '/rtp/withdraw/transaction';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.RTPWithdrawTransactionResponse>(url, data, possibleResTypes);
    },
    fetchiQ11TokenInfo: async (params, source) => {
      const url = '/iq11/embed-url';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const resp = verifyResponseType<types.TokenInfoApiResponse>(url, data, possibleResTypes);
      return {
        ...resp,
        AcceptedCardBrands: (resp.AcceptedCardBrands
          ? resp.AcceptedCardBrands.split(',')
          : []) as types.AcceptedCardBrand[],
      };
    },
    fetchLeadsAgentId: async (params, source) => {
      const url = '/partner/hubspot/leads-by-agentid';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.PartnerBillingPackagesResponse>(url, data, possibleResTypes);
    },
    fetchLeadsPartnerId: async (params, source) => {
      const url = '/partner/hubspot/leads-by-partnerid';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.PartnerBillingPackagesResponse>(url, data, possibleResTypes);
    },
    fetchLeadsDetails: async (params, source) => {
      const url = '/partner/hubspot/lead';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      // add new response to verify
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.LeadsDetailsResponse>(url, data, possibleResTypes);
    },
    fetchPartnerAccounts: async (params, source) => {
      const { data } = await http.get<types.PartnerAccountsApi>({
        url: '/partner/account',
        config: { params },
        source,
      });

      const transformAccount = (account: types.AccountApi): types.Account => {
        const { Balances, ParentAccounts, SubAccounts, ...accountPermissionsAndAccountInfo } = account;

        // provides type protection we want
        const transformed: types.AccountWithoutPermissions = {
          ...accountPermissionsAndAccountInfo,
          IsActive:
            typeof accountPermissionsAndAccountInfo.IsActive === 'boolean'
              ? accountPermissionsAndAccountInfo.IsActive
              : toBool(accountPermissionsAndAccountInfo.IsActive || '0'),
          Balances: Object.values(Balances || {}),
          ParentAccounts: Object.values(ParentAccounts || {}),
          SubAccounts: Object.values(SubAccounts || {}).map(transformAccount),
        };

        // AccountPermissions type is making the above three to be boolean | string | number
        return transformed as types.Account;
      };

      return {
        ...data,
        Accounts: Object.values(data.Accounts || {}).map(transformAccount),
      };
    },
    fetchPartnerInvoices: async (params, source) => {
      const url = '/partner/invoices';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.PartnerInvoicesResponse];

      return verifyResponseType<types.PartnerInvoicesResponse>(url, data, possibleResTypes);
    },
    fetchPartnerAccountSummary: async (params, source) => {
      const url = '/partner/account-summary';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.PartnerAccountSummaryResponse>(url, data, possibleResTypes);
    },
    fetchPartnerAccountSummaryDetails: async (params, source) => {
      const url = '/partner/account-summary-detail';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.PartnerAccountSummaryDetailsResponse>(url, data, possibleResTypes);
    },
    fetchPartnerInvoiceDetails: async (params, source) => {
      const url = '/partner/invoice-details';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.PartnerInvoiceDetailsResponse];

      return verifyResponseType<types.PartnerInvoiceDetailsResponse>(url, data, possibleResTypes);
    },
    fetchPartnerInvoicePdf: async (params, source) => {
      const url = '/partner/invoice-pdf';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.PartnerInvoicePdfResponse];

      return verifyResponseType<types.InvoicePdfResponse>(url, data, possibleResTypes);
    },
    fetchPartnerStatementPdf: async (params, source) => {
      const url = '/partner/statement-pdf';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.PartnerStatementPdfResponse];

      return verifyResponseType<types.PartnerStatementPdfResponse>(url, data, possibleResTypes);
    },
    fetchPartnerStatementCsv: async (params, source) => {
      const url = '/partner/statement-csv';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.StatementCsvResponse>(url, data, possibleResTypes);
    },
    fetchPartnerStatistics: async (source) => {
      const url = '/partner/statistics';

      const { data } = await http.get<Record<string, never>, unknown>({
        url,
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.PartnerStatisticsResponse];

      return verifyResponseType<types.PartnerStatisticsResponse>(url, data, possibleResTypes);
    },
    fetchSingleUseToken: async (params, source) => {
      const url = '/portal/retrieve-single-use-token';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.SingleUseTokenResponse];

      return verifyResponseType<types.SingleUseTokenResponse>(url, data, possibleResTypes);
    },
    fetchOnboardingAppUrl: async (params, source) => {
      const url = '/partner/account/onboarding-url';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.OnboardingAppUrlResponse];

      return verifyResponseType<types.OnboardingAppUrlResponse>(url, data, possibleResTypes);
    },
    fetchTicketDetails: async (params, source) => {
      const url = '/partner/ticket-detail';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.TicketDetailsResponse>(url, data, possibleResTypes);
    },
    fetchTickets: async (params, source) => {
      const url = '/partner/tickets';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.TicketsResponse>(url, data, possibleResTypes);
    },
    fetchAccountsOverviewActive: async (source) => {
      const url = '/partner/statistics';
      const params = { StatType: 'AccountsOverviewActive' };
      const { data } = await http.get<Record<string, never>, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.PartnerStatisticsDataResponse>(url, data, possibleResTypes);
    },
    fetchTransactionsOverview: async (source) => {
      const url = '/partner/statistics';
      const params = { StatType: 'TransactionsOverview' };
      const { data } = await http.get<Record<string, never>, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.PartnerStatisticsDataResponse>(url, data, possibleResTypes);
    },
    fetchPerformanceOverview: async (source) => {
      const url = '/partner/statistics';
      const params = { StatType: 'PerformanceOverview' };
      const { data } = await http.get<Record<string, never>, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.PartnerStatisticsDataResponse>(url, data, possibleResTypes);
    },
    fetchUserDataOverview: async (source) => {
      const url = '/partner/statistics';
      const params = { StatType: 'UserDataOverview' };
      const { data } = await http.get<Record<string, never>, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.UserDataOverviewResponse>(url, data, possibleResTypes);
    },
    fetchActivityOverview: async (baseParams, source) => {
      const url = '/partner/statistics';
      const params = { ...baseParams, StatType: 'ActivityOverview' };

      const { data } = await http.get<Record<string, never>, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.ActivityOverviewResponse>(url, data, possibleResTypes);
    },
    fetchPartnerAccountUsers: async (source) => {
      const url = '/partner/users';

      const { data } = await http.get<unknown>({
        url,
        config: {},
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      const res = verifyResponseType<types.FetchPartnerAccountUsersApiResponse>(url, data, possibleResTypes);

      return {
        ...res,
        Users: Object.values(res.Users),
      };
    },

    fetchCreditCards: async (params, bearer, source) => {
      const url = '/credit-card';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.CreditCardResponse];

      const res = verifyResponseType<types.CreditCardsApiResponse>(url, data, possibleResTypes);

      return {
        ...res,
        CreditCards: Object.values(res.CreditCards || {}).map((c) => ({
          ...c,
          SendingEligibility: c.SendingEligibility === '0' ? true : c.SendingEligibility === '1' ? false : undefined,
          ReceivingEligibility:
            c.ReceivingEligibility === '0' ? true : c.ReceivingEligibility === '1' ? false : undefined,
          IsDefault: toBool(c.IsDefault),
        })),
      };
    },
    fetchBankAccounts: async (params, bearer, source) => {
      const url = '/bank-account';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.BankAccountsResponse];

      return verifyResponseType<types.BankAccountsResponse>(url, data, possibleResTypes);
    },

    fetchDebitCards: async (params, source) => {
      const url = '/debit-card';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.GetDebitCardsApiResponse];

      const res = verifyResponseType<types.GetDebitCardsApiResponse>(url, data, possibleResTypes);

      return {
        ...res,
        DebitCards: Object.values(res.DebitCards || {}).map((c) => ({
          ...c,
          SendingEligibility: c.SendingEligibility === '0' ? true : c.SendingEligibility === '1' ? false : undefined,
          ReceivingEligibility:
            c.ReceivingEligibility === '0' ? true : c.ReceivingEligibility === '1' ? false : undefined,
          IsDefault: toBool(c.IsDefault),
        })),
      };
    },

    fetchPaylinkSingleUseToken: async (params, source) => {
      const url = '/portal/retrieve-single-use-token';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ELinxSingleUseTokenResponse];

      const resp = verifyResponseType<types.ELinxSingleUseTokenApiResponse>(url, data, possibleResTypes);
      return {
        ...resp,
        ExtraInfo: {
          ...resp.ExtraInfo,
          PayLinkDetails: {
            ...resp.ExtraInfo.PayLinkDetails,
            BankAccountRail: (resp.ExtraInfo?.PayLinkDetails?.BankAccountRail.split(',') ||
              []) as types.BankingPaymentRail[],
          },
        },
      };
    },
    fetchAccountUserPermissions: async (params, bearer, source) => {
      const url = '/account/users/permissions';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.AccountUserPermissionsResponse>(url, data, possibleResTypes);
    },
    fetchAccountUserPermissionsNew: async (params, bearer, source) => {
      const url = '/account/users/permissions-new';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.AccountUserPermissionsNewResponse>(url, data, possibleResTypes);
    },
    fetchAgreementDetails: async (bearer, source) => {
      const url = '/account/agreement/status';

      const { data } = await http.get<unknown>({
        url,
        config: {},
        bearer,
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.AgreementDetailsResponse>(url, data, possibleResTypes);
    },
    fetchAgreementDownload: async (params, bearer, source) => {
      const url = '/account/agreement/download';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.AgreementDownloadResponse>(url, data, possibleResTypes);
    },
    fetchSigningAuthorityInfo: async (bearer, source) => {
      const url = '/account/submit-extended-info/signing-authority-info';

      const { data } = await http.get<unknown>({
        url,
        config: {},
        source,
        bearer,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.SigningAuthorityInfoResponse>(url, data, possibleResTypes);
    },
    fetchAccountLimits: async (params, source) => {
      const url = '/account/remaining-limit';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.AccountLimitsResponse>(url, data, possibleResTypes);
    },
    fetchBankHolidays: async (source) => {
      const url = '/portal/bank-account-holidays';

      const { data } = await http.get<Record<never, never>, unknown>({
        url,
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      const res = verifyResponseType<types.BankHolidaysApiResponse>(url, data, possibleResTypes);
      return {
        ...res,
        BankHolidays: Object.values(res.BankHolidays || {}),
      };
    },
    fetchClientBankAccounts: async (params, source) => {
      const url = '/bank-account';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.ClientBankAccountsResponse>(url, data, possibleResTypes);
    },
    fetchClientCreditCards: async (params, bearer, source) => {
      const url = '/credit-card';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        bearer,
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.ClientCreditCardsResponse>(url, data, possibleResTypes);
    },
    fetchClientDebitCards: async (params, source) => {
      const url = '/oct';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.ClientDebitCardsResponse>(url, data, possibleResTypes);
    },

    fetchClientTransactions: async (params, source) => {
      const url = '/iq11/transactions';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const res = verifyResponseType<types.ClientTransactionsApiResponse>(url, data, possibleResTypes);
      const transactions =
        typeof res.AggregatorTransactions === 'string'
          ? []
          : Object.values(res.AggregatorTransactions ?? {}).map((transaction) => {
              return {
                ...transaction,
                IsDataEnriched: toBool(transaction.IsDataEnriched),
              };
            });

      return {
        ...res,
        AggregatorTransactions: transactions,
      };
    },
    fetchClientBalance: async (params, source) => {
      const url = '/iq11/balance';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.ClientBalanceResponse>(url, data, possibleResTypes);
    },
    fetchAccountPermissions: async (params, source) => {
      const url = '/account/users/permissions';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.AccountPermissionsResponse>(url, data, possibleResTypes);
    },
    fetchAutoBalanceTransfer: async (params, source) => {
      const url = '/account/auto-balance-transfer';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.AutoBalanceTransferResponse>(url, data, possibleResTypes);
    },
    fetchSubAccounts: async (params, source) => {
      const url = '/account/subaccount';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      const response = verifyResponseType<types.SubAccountsResponseApi>(url, data, possibleResTypes);

      const transformAccount = (account: types.SubAccountsListsApi): types.SubAccountsLists => {
        const { ParentAccounts, SubAccounts, ...rest } = account;
        return {
          ...rest,
          ParentAccounts: Object.values(ParentAccounts || {}),
          SubAccounts: Object.values(SubAccounts || {}).map(transformAccount),
        };
      };

      return {
        ...response,
        Subaccounts: Object.values(response.Subaccounts || {}).map(transformAccount),
      };
    },
    fetchContactGroups: async (params, source) => {
      const url = '/group';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.ContactGroupsResponse>(url, data, possibleResTypes);
    },
    fetchGroupContact: async (params, source) => {
      const url = '/group/contacts';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.GroupContactResponse>(url, data, possibleResTypes);
    },
    fetchScheduledTransactions: async (params, source) => {
      const url = '/eft/scheduled-transactions';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.ScheduledTransactionsResponse>(url, data, possibleResTypes);
    },
    fetchInboundETransferTransaction: async (params, bearer, source) => {
      const url = '/interac/inbound/transaction';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.InboundETransferTransactionResponse>(url, data, possibleResTypes);
    },
    fetchVisaDirectPushTransaction: async (params, bearer, source) => {
      const url = '/push-to-card/push-funds/transaction';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.VisaDirectPushTransactionResponse>(url, data, possibleResTypes);
    },
    fetchCurrencyConversionTransaction: async (params, bearer, source) => {
      const url = '/gcm/conversion/transaction';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.CurrencyConversionTransactionResponse>(url, data, possibleResTypes);
    },
    fetchMyBankAccount: async (params, bearer, source) => {
      const url = 'my-bank-account';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        bearer,
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.MyBankAccountResponse>(url, data, possibleResTypes);
    },
    fetchClientAccounts: async (params, bearer, source) => {
      const url = '/account/client-accounts';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.ClientAccountsResponse>(url, data, possibleResTypes);
    },
    fetchClientAccountList: async (params, source) => {
      const url = '/account/client-accounts';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const res = verifyResponseType<types.ClientAccountListApiResponse>(url, data, possibleResTypes);

      return {
        ...res,
        ClientAccounts: Object.values(res.ClientAccounts || {}).map((account) => ({
          ...account,
          IsActive: toBool(account.IsActive),
          Labels: account.Labels.split(',').filter((label) => label !== ''),
          Balances: Object.values(account.Balances || {}).map((balance) => ({
            ...balance,
            Balance: Number(sanitizeToNumeric(balance.Balance)),
            PendingBalance: Number(sanitizeToNumeric(balance.PendingBalance)),
            AvailableBalance: Number(sanitizeToNumeric(balance.AvailableBalance)),
          })),
          AdditionalContacts: {
            email: Object.values(account.AdditionalContacts.email || {}),
          },
        })),
      };
    },

    fetchClientAccountBalanceRange: async (source) => {
      const url = '/portal/client-account/balance-range';

      const { data } = await http.get<Record<never, never>, unknown>({
        url,
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const res = verifyResponseType<types.ClientAccountBalanceRangeResponse>(url, data, possibleResTypes);

      return {
        ...res,
      };
    },

    fetchClientAccountPaymentMethods: async (params, source) => {
      const url = '/account/client-accounts/payment-methods';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const res = verifyResponseType<types.ApiPaymentMethodResponse>(url, data, possibleResTypes);

      return {
        ...res,
        PaymentMethods: Object.values(res.PaymentMethods || {}).map((m) => ({
          ...m,
          IsDefault: toBool(m.IsDefault),
        })),
      };
    },

    fetchContactPaymentMethods: async (params, source) => {
      const url = '/contacts/payment-methods';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const res = verifyResponseType<types.ApiPaymentMethodResponse>(url, data, possibleResTypes);

      return {
        ...res,
        PaymentMethods: Object.values(res.PaymentMethods || {}).map((m) => ({
          ...m,
          IsDefault: toBool(m.IsDefault),
        })),
      };
    },

    fetchClientBusinessTypes: async (source) => {
      const url = '/account/business-types';

      const { data } = await http.get<Record<never, never>, unknown>({
        url,
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.BusinessTypesResponse>(url, data, possibleResTypes);
    },
    fetchClientAccountBalance: async (params, source) => {
      const url = '/account/client-accounts/balance';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.ClientAccountBalanceResponse>(url, data, possibleResTypes);
    },
    fetchDefaultBankAccount: async (params, source) => {
      const url = '/bank-account/default-bank-account';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.DefaultBankAccountResponse>(url, data, possibleResTypes);
    },

    postWithdrawMyAccount: async (params, source) => {
      const url = '/account/withdraw-my-account';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.WithdrawMyAccountResponse>(url, data, possibleResTypes);
    },
    postFundMyAccount: async (params, source) => {
      const url = '/account/fund-my-account';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.FundMyAccountResponse>(url, data, possibleResTypes);
    },
    postVoidACHTransaction: async (params, bearer, source) => {
      const url = '/ach/void';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        bearer,
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.VoidACHTransactionResponse>(url, data, possibleResTypes);
    },
    postRefundACHTransaction: async (params, bearer, source) => {
      const url = '/ach/refund';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        bearer,
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.RefundACHTransactionResponse>(url, data, possibleResTypes);
    },
    postACHFund: async (params, source) => {
      const url = '/ach/fund';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.ACHFundResponse>(url, data, possibleResTypes);
    },
    postACHWithdraw: async (params, source) => {
      const url = '/ach/withdraw';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.ACHWithdrawResponse>(url, data, possibleResTypes);
    },
    postACHFundSchedule: async (params, source) => {
      const url = '/ach/fund/schedule';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.ACHFundScheduleResponse>(url, data, possibleResTypes);
    },
    postACHWithdrawSchedule: async (params, source) => {
      const url = '/ach/withdraw/schedule';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.ACHFundScheduleResponse>(url, data, possibleResTypes);
    },
    fetchACHFundTransaction: async (params, bearer, source) => {
      const url = '/ach/fund/transaction';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.ACHFundTransactionResponse>(url, data, possibleResTypes);
    },
    fetchACHWithdrawTransaction: async (params, bearer, source) => {
      const url = '/ach/withdraw/transaction';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.ACHWithdrawTransactionResponse>(url, data, possibleResTypes);
    },
    fetchACHSheduleTransaction: async (params, source) => {
      const url = '/ach/scheduled-transactions';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.ACHSheduleTransactionResponse>(url, data, possibleResTypes);
    },
    postRefundCreditCardTransaction: async (params, bearer, source) => {
      const url = '/credit-card/refund';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        bearer,
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.RefundCreditCardTransactionResponse>(url, data, possibleResTypes);
    },
    voidCreditCardTransaction: async (params, bearer, source) => {
      const url = '/credit-card/void';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        bearer,
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.VoidCreditCardTransactionResponse>(url, data, possibleResTypes);
    },

    fetchPaasTransactions: async (params, source) => {
      const url = '/iq11/paas/transactions';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.PaasTransactionsResponse>(url, data, possibleResTypes);
    },
    fetchBatchFileTransactions: async (params, source) => {
      const url = '/account/transactions/batch-file';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.BatchFileTransactionsResponse>(url, data, possibleResTypes);
    },
    fetchInteracEmail: async (params, source) => {
      const url = '/interac/email';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.InteracEmailResponse>(url, data, possibleResTypes);
    },
    fetchSendLegalAgreement: async (bearer, source) => {
      const url = '/vopay/onboarding-app/legal-agreement';
      const { data } = await http.get<unknown>({
        url,
        source,
        bearer,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.SendLegalAgreementResponse>(url, data, possibleResTypes);
    },
    fetchAccountVerification: async (params, source) => {
      const url = '/account-verification';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.AccountVerificationResponse>(url, data, possibleResTypes);
    },

    postVoidAgreement: async (params, bearer, source) => {
      const url = '/account/agreement/void';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.ApiResponse>(url, data, possibleResTypes);
    },
    postDeleteSigner: async (params, bearer, source) => {
      const url = '/account/submit-extended-info/signing-authority-info/delete';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.DeleteSignerResponse>(url, data, possibleResTypes);
    },
    postEditSigner: async (params, bearer, source) => {
      const url = `/account/submit-extended-info/signing-authority-info/edit`;
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.EditSignerResponse>(url, data, possibleResTypes);
    },
    postAddSigner: async (params, bearer, source) => {
      const url = `/account/submit-extended-info/signing-authority-info/add`;
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.AddSignerResponse>(url, data, possibleResTypes);
    },
    postCreateUser: async (params, source) => {
      const url = '/partner/user';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.CreateUserResponse>(url, data, possibleResTypes);
    },
    postDeleteUser: async (params, source) => {
      const url = '/partner/users/delete';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.DeleteUserResponse>(url, data, possibleResTypes);
    },
    postSendOnboardingEmail: async (params, source) => {
      const url = '/partner/send-onboarding-url';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.ApiResponse>(url, data, possibleResTypes);
    },
    postUserChangePassword: async (params, source) => {
      const url = '/partner/users/change-password';
      const { data } = await http.post<typeof params, unknown>({
        url,
        body: JSON.stringify(params),
        config: {},
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.UserChangePasswordResponse>(url, data, possibleResTypes);
    },
    postUserChangeRole: async (params, source) => {
      const url = '/partner/users/role';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.ApiResponse>(url, data, possibleResTypes);
    },
    generateSingleUseToken: async (params, source) => {
      const url = '/partner/generate-single-use-token';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.ApiResponse>(url, data, possibleResTypes);
    },
    postAccountUsersChangePassword: async (params, bearer?, source?) => {
      const url = '/account/users/change-password';
      const { data } = await http.post<typeof params, unknown>({
        url,
        body: JSON.stringify(params),
        config: {},
        source,
        bearer,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.AccountUsersChangePasswordResponse];

      return verifyResponseType<types.AccountUsersChangePasswordResponse>(url, data, possibleResTypes);
    },
    postCreateAccountUser: async (params, bearer, source) => {
      const url = '/account/user';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.CreateAccountUserResponse>(url, data, possibleResTypes);
    },
    postCreateLead: async (params, source) => {
      const url = '/partner/hubspot/create-lead';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.ApiResponse>(url, data, possibleResTypes);
    },
    postDeleteAccountUser: async (params, bearer, source) => {
      const url = '/account/users/delete';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.DeleteAccountUserResponse>(url, data, possibleResTypes);
    },
    postIFrameEvent: async (params, source) => {
      const url = '/iq11/log-iframe-event';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.ApiResponse>(url, data, possibleResTypes);
    },
    postIQ11Complete: async (params, source) => {
      const Token = ss.getItem(LS_IQ11_TOKEN) || '';
      const url = `/iq11/complete/${Token}`;
      const { data } = await http.post<typeof params, unknown>({
        url,
        body: JSON.stringify(params),
        config: {},
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.IQ11TokenResponse>(url, data, possibleResTypes);
    },
    postLinkBankAccountManually: async (params, source) => {
      const url = '/iq11/link-bank-account-manually';

      const { data } = await http.post<typeof params, unknown>({
        url,
        body: JSON.stringify(params),
        config: {},
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.IQ11TokenResponse>(url, data, possibleResTypes);
    },
    postLinkCreditCard: async (params, source) => {
      const url = '/credit-card/link-credit-card';

      const { data } = await http.post<typeof params, unknown>({
        url,
        body: JSON.stringify(params),
        config: {},
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.IQ11TokenResponse];

      return verifyResponseType<types.IQ11TokenResponse>(url, data, possibleResTypes);
    },
    postLinkPushToCard: async (params, source) => {
      const url = '/debit-card/link-card';

      const { data } = await http.post<typeof params, unknown>({
        url,
        body: JSON.stringify(params),
        config: {},
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.IQ11TokenResponse>(url, data, possibleResTypes);
    },
    postPartnerAccount: async (params, source, overrideAuth) => {
      const url = '/partner/account';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: {},
        body: JSON.stringify(params),
        source,
        overrideAuth,
      });

      const possibleResTypes: VoPayResponse[] = [
        VoPayResponse.PartnerAccountCreationLinkResponse,
        VoPayResponse.PartnerAccountCreationResponse,
      ];

      return verifyResponseType<types.PartnerAccountCreationResponse | types.ApiResponse>(url, data, possibleResTypes);
    },
    postPartnerAccountBusinessCase: async (params, source?) => {
      const url = '/partner/account/business-case';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.ApiResponse>(url, data, possibleResTypes);
    },
    postPartnerAccountMatrix: async (params, source?) => {
      const url = '/partner/account/account-matrix';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.ApiResponse>(url, data, possibleResTypes);
    },
    postPartnerAccountPermissions: async (params, source?) => {
      const { data } = await http.post<typeof params, types.ApiResponse>({
        url: '/partner/account/set-permissions',
        body: JSON.stringify(params),
        config: {},
        source,
      });

      return data;
    },
    postAccountUserPermissions: async (params, bearer, source?) => {
      const { data } = await http.post<typeof params, types.ApiResponse>({
        url: '/account/users/permissions',
        config: { params },
        source,
        bearer,
      });

      return data;
    },
    postPartnerAccountUsersChangePassword: async (params, source?) => {
      const url = '/partner/account/users/change-password';
      const { data } = await http.post<typeof params, unknown>({
        url,
        body: JSON.stringify(params),
        config: {},
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.PartnerAccountUsersChangePasswordResponse];

      return verifyResponseType<types.PartnerAccountUsersChangePasswordResponse>(url, data, possibleResTypes);
    },
    postPartnerAuthPing: async (source?) => {
      const url = '/partner/auth/ping';

      const { data } = await http.post<Record<string, never>, unknown>({
        url,
        config: {},
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.ApiResponse>(url, data, possibleResTypes);
    },
    postPartnerCustomPackage: async (params, source?) => {
      const url = '/partner/custom-package';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.PartnerCustomPackageResponse];

      return verifyResponseType<types.PartnerCustomPackageResponse>(url, data, possibleResTypes);
    },
    postPaylinkDecline: async (params, source) => {
      const url = '/elinx/decline';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ELinxSingleUseTokenResponse];

      return verifyResponseType<types.ELinxSingleUseTokenResponse>(url, data, possibleResTypes);
    },
    postPaylinkSetAccount: async (params, source) => {
      const url = '/elinx/set-account';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.ApiResponse>(url, data, possibleResTypes);
    },
    postPortalPartnerLogin: async (params, source?) => {
      const url = '/portal/partner/login';

      const { data } = await http.post<typeof params, unknown>({
        url,
        body: JSON.stringify(params),
        config: {},
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.AuthorizationResponse];

      return verifyResponseType<types.AuthorizationResponse>(url, data, possibleResTypes);
    },

    postPortalGenerateQRCode: async (params, source?) => {
      const url = '/portal/generate-qr-code';

      const { data } = await http.post<typeof params, unknown>({
        url,
        body: JSON.stringify(params),
        config: {},
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.PortalGenerateQRCodeResponse>(url, data, possibleResTypes);
    },
    postPortalAuthenticate: async (params, source?) => {
      const url = '/portal/authenticate';

      const { data } = await http.post<typeof params, unknown>({
        url,
        body: JSON.stringify(params),
        config: {},
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.PortalAuthenticateResponse>(url, data, possibleResTypes);
    },
    postPortalChangePassword: async (params, source?) => {
      const url = '/portal/change-password';

      const { data } = await http.post<typeof params, unknown>({
        url,
        body: JSON.stringify(params),
        config: {},
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.ApiResponse>(url, data, possibleResTypes);
    },
    postPortalResetPassword: async (params, source?) => {
      const url = '/portal/reset-password';

      const { data } = await http.post<typeof params, unknown>({
        url,
        body: JSON.stringify(params),
        config: {},
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.ApiResponse>(url, data, possibleResTypes);
    },

    postIQ11Login: async (params, source?) => {
      const url = '/account/client-accounts/login';
      const { data } = await http.post<typeof params, unknown>({
        url,
        body: JSON.stringify(params),
        config: {},
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.IQ11AuthorizationResponse];

      return verifyResponseType<types.IQ11AuthorizationResponse>(url, data, possibleResTypes);
    },
    postIQ11AuthPing: async (bearer, source?) => {
      const url = '/account/client-accounts/ping';
      const { data } = await http.post<Record<string, never>, unknown>({
        url,
        config: {},
        source,
        bearer,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.ApiResponse>(url, data, possibleResTypes);
    },
    postBankAccountConnection: async (params, source) => {
      const url = '/iq11/bank-account-connection';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.BankAccountConnectionResponse>(url, data, possibleResTypes);
    },
    postLinkBankAccount: async (params, source) => {
      const url = '/iq11/link-bank-account';
      const { data } = await http.post<typeof params, unknown>({
        url,
        body: JSON.stringify(params),
        config: {},
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.IQ11TokenResponse>(url, data, possibleResTypes);
    },
    submitPartnerSupportTicket: async (params, source) => {
      const url = '/partner/submit-ticket';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: {},
        body: JSON.stringify(params),
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.SubmitTicketResponse>(url, data, possibleResTypes);
    },
    postTicketAttachment: async (params, source) => {
      const url = '/partner/ticket-attachment';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: {},
        body: JSON.stringify(params),
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.UploadFileAttachmentResponse];

      return verifyResponseType<types.ApiResponse>(url, data, possibleResTypes);
    },
    postSubmitTicketComment: async (params, source) => {
      const url = '/partner/submit-ticket-comment';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: {},
        body: JSON.stringify(params),
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.ApiResponse>(url, data, possibleResTypes);
    },
    postIQ11CreditCardEmbedUrl: async (params, bearer, source) => {
      const url = '/credit-card/generate-embed-url';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.IQ11EmbedUrlResponse];

      return verifyResponseType<types.IQ11EmbedUrlResponse>(url, data, possibleResTypes);
    },
    postIQ11EmbedUrl: async (params, bearer, source) => {
      const url = '/iq11/generate-embed-url';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.IQ11EmbedUrlResponse];

      return verifyResponseType<types.IQ11EmbedUrlResponse>(url, data, possibleResTypes);
    },
    postDeleteCreditCard: async (params, bearer, source) => {
      const url = '/credit-card/delete';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.ApiResponse>(url, data, possibleResTypes);
    },
    postDeleteBankAccount: async (params, bearer, source) => {
      const url = '/bank-account/delete';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.ApiResponse>(url, data, possibleResTypes);
    },
    postDeleteBankAccountDocuments: async (params, bearer, source) => {
      const url = '/bank-account/documents/delete';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.ApiResponse>(url, data, possibleResTypes);
    },
    postTransactionRefund: async (params, bearer, source) => {
      const url = 'account/transactions/refund';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.TransactionsRefundResponse];

      return verifyResponseType<types.TransactionsRefundResponse>(url, data, possibleResTypes);
    },
    postTransactionCancel: async (params, bearer, source) => {
      const url = 'account/transactions/cancel';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.TransactionsCancelResponse>(url, data, possibleResTypes);
    },
    postSetMyBankAccount: async (params, bearer, source) => {
      const url = 'bank-account/set-my-bank-account';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.SetMyBankAccountResponse];

      return verifyResponseType<types.SetMyBankAccountResponse>(url, data, possibleResTypes);
    },
    postSetMyBankAccountWithPurpose: async (params, bearer, source) => {
      const url = 'bank-account/set-my-bank-account';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.SetMyBankAccountPurposeResponse];

      return verifyResponseType<types.SetMyBankAccountPurposeResponse>(url, data, possibleResTypes);
    },
    postSetMyCreditCard: async (params, bearer, source) => {
      const url = 'credit-card/set-my-credit-card';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.SetMyCreditCardResponse];

      return verifyResponseType<types.SetMyCreditCardResponse>(url, data, possibleResTypes);
    },
    postPortalClientLogin: async (params, source?) => {
      const url = '/portal/login';
      const { data } = await http.post<typeof params, unknown>({
        url,
        body: JSON.stringify(params),
        config: {},
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      const resp = verifyResponseType<types.ClientAuthorizationBaseResponse>(url, data, possibleResTypes);
      return {
        ...resp,
        ChatSupportEnabled: toBool(resp.ChatSupportEnabled || '0'),
        InteracVerificationEnabled: toBool(resp.InteracVerificationEnabled || '0'),
        SupportTicketEnabled: toBool(resp.SupportTicketEnabled || '0'),
        GooglePayEnabled: toBool(resp.GooglePayEnabled || '0'),
        MicroDepositBankAccountVerificationEnabled: toBool(resp.MicroDepositBankAccountVerificationEnabled || '0'),
        CardBasicVerificationEnabled: toBool(resp.CardBasicVerificationEnabled || '0'),
        CardEnhancedVerificationEnabled: toBool(resp.CardEnhancedVerificationEnabled || '0'),
        FileConversionEnabled: toBool(resp.FileConversionEnabled || '0'),
        FileSubmissionEnabled: toBool(resp.FileSubmissionEnabled || '0'),
        BillPayEnabled: toBool(resp.BillPayEnabled || '0'),
        VirtualAccountsEnabled: toBool(resp.VirtualAccountsEnabled || '0'),
        AccountSummaryEnabled: toBool(resp.AccountSummaryEnabled || '0'),
        AccountAndBillingEnabled: toBool(resp.AccountAndBillingEnabled || '0'),
        FundMyAccountEnabled: toBool(resp.FundMyAccountEnabled || '0'),
        WithdrawMyAccountEnabled: toBool(resp.WithdrawMyAccountEnabled || '0'),
        BankAccountTransferEnabled: toBool(resp.BankAccountTransferEnabled || '0'),
        OperationalAccountsEnabled: toBool(resp.OperationalAccountsEnabled || '0'),
        PartnerBillsAccount: toBool(resp.PartnerBillsAccount || '0'),
        PaymentsEnabled: toBool(resp.PaymentsEnabled || '0'),
        CollectionsEnabled: toBool(resp.CollectionsEnabled || '0'),
        ShowSubAccountOrgChart: toBool(resp.ShowSubAccountOrgChart || '0'),
        DigitalWalletsEnabled: toBool(resp.DigitalWalletsEnabled || '0'),
        VenmoWithdrawEnabled: toBool(resp.VenmoWithdrawEnabled || '0'),
        PaypalWithdrawEnabled: toBool(resp.PaypalWithdrawEnabled || '0'),
        AFTEnabled: toBool(resp.AFTEnabled || '0'),
      };
    },
    postPortalClientLoginHistory: async (params, source?) => {
      const url = '/account/user/log-access';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.PortalClientLoginHistoryResponse>(url, data, possibleResTypes);
    },
    fetchGeolocation: async () => {
      const url = 'https://ipinfo.io/json?token=f8d866afa09bc6';
      const { data } = await http.get<Record<never, never>, unknown>({
        url,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.GeoLocationResponse];

      return verifyResponseType<types.GeoLocationResponse>(url, data, possibleResTypes);
    },

    postClientChangePassword: async (params, source) => {
      const url = '/account/client-accounts/change-password';
      const { data } = await http.post<typeof params, unknown>({
        url,
        body: JSON.stringify(params),
        config: {},
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.ApiResponse>(url, data, possibleResTypes);
    },

    postClientAuthPing: async (source?) => {
      const url = '/auth/ping';
      const { data } = await http.post<Record<string, never>, unknown>({
        url,
        config: {},
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.ApiResponse>(url, data, possibleResTypes);
    },
    postClientResetPasswordEmail: async (params, source) => {
      const url = '/portal/request-password-reset';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.ClientResetPasswordEmailResponse>(url, data, possibleResTypes);
    },
    postVerifyRecaptcha: async (params, source) => {
      const url = '/portal/verify-recaptcha';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.VerifyRecaptchaResponse>(url, data, possibleResTypes);
    },
    postAccountBalance: async (params, source) => {
      const url = '/account/balance';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.AccountBalanceResponse>(url, data, possibleResTypes);
    },
    fetchAccountBalances: async (params, source) => {
      const url = '/account/balances';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const res = verifyResponseType<types.FetchAccountBalancesApiResponse>(url, data, possibleResTypes);
      return {
        ...res,
        AccountBalances: Object.values(res.AccountBalances || {}).map((balance) => ({
          AccountBalance: parseFloat(balance.AccountBalance),
          PendingFunds: parseFloat(balance.PendingFunds),
          SecurityDeposit: parseFloat(balance.SecurityDeposit),
          Reserve: parseFloat(balance.Reserve),
          AvailableImmediately: parseFloat(balance.AvailableImmediately),
          AvailableFunds: parseFloat(balance.AvailableFunds),
          OffbookBalance: parseFloat(balance.OffbookBalance),
          Currency: balance.Currency,
          AsOfDate: balance.AsOfDate,
          VirtualAccountNumber: balance.VirtualAccountNumber,
          RollingReserveNSF: balance.RollingReserveNSF ? parseFloat(balance.RollingReserveNSF) : undefined,
          RollingReserveReturns: balance.RollingReserveReturns ? parseFloat(balance.RollingReserveReturns) : undefined,
          RollupAvailableBalance: balance.RollupAvailableBalance
            ? parseFloat(balance.RollupAvailableBalance)
            : undefined,
          RollupAccountBalance: balance.RollupAccountBalance ? parseFloat(balance.RollupAccountBalance) : undefined,
        })),
      };
    },
    fetchVirtualAccounts: async (params, source) => {
      const url = '/virtual-account';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const res = verifyResponseType<types.FetchVirtualAccountsApiResponse>(url, data, possibleResTypes);
      return {
        ...res,
        VirtualAccounts: Object.values(res.VirtualAccounts || {}),
      };
    },
    createVirtualAccount: async (params, source) => {
      const url = '/virtual-account';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.CreateVirtualAccountResponse>(url, data, possibleResTypes);
    },
    fetchVirtualAccountCurrencies: async (source?: CancelTokenSource) => {
      const url = '/virtual-account/currencies';
      const { data } = await http.get<unknown, unknown>({
        url,
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const res = verifyResponseType<types.FetchVirtualAccountCurrenciesApiResponse>(url, data, possibleResTypes);
      return {
        ...res,
        VirtualAccounts: Object.values(res.VirtualAccounts || {}).map((currency) => ({
          ShortForm: currency.ShortForm,
          Currency: currency.Currency,
          HasVirtualAccount: toBool(currency.HasVirtualAccount),
        })),
      };
    },
    fetchMasterVirtualAccounts: async (source?: CancelTokenSource) => {
      const url = '/virtual-account/master-accounts';
      const { data } = await http.get<unknown, unknown>({
        url,
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const res = verifyResponseType<types.FetchMasterVirtualAccountsApiResponse>(url, data, possibleResTypes);
      return {
        ...res,
        MasterAccounts: Object.values(res.MasterAccounts || {}).map((account) => ({
          ...account,
          IsActive: toBool(account.IsActive),
        })),
      };
    },
    simulateVirtualAccountCredit: async (
      params: types.SimulateVirtualAccountCreditRequest,
      source?: CancelTokenSource,
    ) => {
      const url = '/virtual-account/simulate-credit';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.SimulateVirtualAccountCreditResponse>(url, data, possibleResTypes);
    },
    fetchReconciliationTransactions: async (
      params: types.ReconciliationTransactionsRequest,
      source?: CancelTokenSource,
    ) => {
      const url = '/reconciliation/transactions';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const response = verifyResponseType<types.ReconciliationTransactionsApiResponse>(url, data, possibleResTypes);
      return {
        ...response,
        Transactions: response.Transactions
          ? Object.values(response.Transactions).map((transaction) => ({
              ...transaction,
              IsReconciled: toBool(transaction.IsReconciled),
            }))
          : [],
      };
    },
    postDeleteClientBankAccount: async (params, source) => {
      const url = '/bank-account/delete';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.ApiResponse>(url, data, possibleResTypes);
    },
    postDeletePaypalAccount: async (params, source) => {
      const url = '/paypal/delete';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.ApiResponse>(url, data, possibleResTypes);
    },
    postDeleteVenmoAccount: async (params, source) => {
      const url = '/venmo/delete';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.ApiResponse>(url, data, possibleResTypes);
    },
    postSetMyClientBankAccount: async (params, source) => {
      const url = 'bank-account/set-my-bank-account';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.SetMyBankAccountResponse];
      return verifyResponseType<types.SetMyBankAccountResponse>(url, data, possibleResTypes);
    },
    postSetClientMyCreditCard: async (params, source) => {
      const url = 'credit-card/set-my-credit-card';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.SetMyCreditCardResponse];

      return verifyResponseType<types.SetMyCreditCardResponse>(url, data, possibleResTypes);
    },
    postSetClientMyDebitCard: async (params, source) => {
      const url = '/debit-card/set-debit-card';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.SetMyCreditCardResponse];

      return verifyResponseType<types.SetMyDebitCardResponse>(url, data, possibleResTypes);
    },

    postSetClientEmail: async (params, source) => {
      const url = '/interac/email/set-my-email';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.SetClientEmailResponse];

      return verifyResponseType<types.SetClientEmailResponse>(url, data, possibleResTypes);
    },
    postSetClientGooglePay: async (params, source) => {
      const url = '/google-pay/set-my-google-pay';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.SetMyGooglePayResponse];

      return verifyResponseType<types.SetMyGooglePayResponse>(url, data, possibleResTypes);
    },
    postSetClientApplePay: async (params, source) => {
      const url = '/apple-pay/set-my-apple-pay';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.SetMyApplePayResponse];

      return verifyResponseType<types.SetMyApplePayResponse>(url, data, possibleResTypes);
    },
    postSetClientPayPal: async (params, source) => {
      const url = '/paypal/set-my-recipient';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.SetMyPayPalResponse];

      return verifyResponseType<types.SetMyPayPalResponse>(url, data, possibleResTypes);
    },
    postSetClientVenmo: async (params, source) => {
      const url = '/venmo/set-my-recipient';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.SetMyVenmoResponse];

      return verifyResponseType<types.SetMyVenmoResponse>(url, data, possibleResTypes);
    },

    postDeleteClientCreditCard: async (params, source) => {
      const url = '/credit-card/delete';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.ApiResponse>(url, data, possibleResTypes);
    },
    postDeleteDebitCard: async (params, source) => {
      const url = '/oct/delete';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.ApiResponse>(url, data, possibleResTypes);
    },
    postEmbedUrl: async (params, source, bearer) => {
      const url = '/iq11/generate-embed-url';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: {
          params: {
            ...params,
            Version: 'v2',
          },
        },
        source,
        bearer,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.EmbedUrlResponse>(url, data, possibleResTypes);
    },
    postSendPaylink: async (params, source) => {
      const url = '/elinx';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.SendELinxResponse>(url, data, possibleResTypes);
    },
    postEftFund: async (params, source) => {
      const url = '/eft/fund';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.EftFundResponse>(url, data, possibleResTypes);
    },
    postScheduledEftFund: async (params, source) => {
      const url = '/eft/fund/schedule';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.ScheduledEftFundResponse>(url, data, possibleResTypes);
    },
    postCancelAutoBalanceTransfer: async (params, source) => {
      const url = '/account/auto-balance-transfer/cancel';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.ApiResponse>(url, data, possibleResTypes);
    },
    postChargeCreditCard: async (params, source) => {
      const url = '/credit-card/charge';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.ChargeCreditCardResponse>(url, data, possibleResTypes);
    },
    postIdVerificationRequest: async (params, source) => {
      const url = '/qa/onfido-verification-link';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.IdVerificationResponse>(url, data, possibleResTypes);
    },
    postInteracMoneyRequest: async (params, source) => {
      const url = '/interac/money-request';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.InteracMoneyRequestResponse>(url, data, possibleResTypes);
    },
    postBulkPayoutRequest: async (params, source) => {
      const url = '/interac/bulk-payout';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.InteracBulkPayoutResponse>(url, data, possibleResTypes);
    },
    postCancelInteracMoneyRequest: async (params, bearer, source) => {
      const url = '/interac/money-request/transaction/request-cancellation';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        bearer,
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.CancelInteracMoneyResponse>(url, data, possibleResTypes);
    },
    postCancelInteracBulkPayout: async (params, bearer, source) => {
      const url = '/interac/bulk-payout/transaction/request-cancellation';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        bearer,
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.CancelInteracMoneyResponse>(url, data, possibleResTypes);
    },
    postCancelVoPayInstant: async (params, bearer, source) => {
      const url = '/rtr/withdraw/transaction/request-cancellation';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        bearer,
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.CancelVoPayInstantResponse>(url, data, possibleResTypes);
    },
    postEftWithdraw: async (params, source) => {
      const url = '/eft/withdraw';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.EftWithdrawResponse>(url, data, possibleResTypes);
    },
    postRtrWithdraw: async (params, source) => {
      const url = '/rtr/withdraw';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.RtrWithdrawResponse>(url, data, possibleResTypes);
    },
    postScheduledEftWithdraw: async (params, source) => {
      const url = '/eft/withdraw/schedule';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.ScheduledEftWithdrawResponse>(url, data, possibleResTypes);
    },
    postCreateAutoBalanceTransfer: async (params, source) => {
      const url = '/account/auto-balance-transfer';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.ApiResponse>(url, data, possibleResTypes);
    },
    saveSubAccountPermissions: async (params, source) => {
      const url = '/account/subaccount/set-permissions';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.ApiResponse>(url, data, possibleResTypes);
    },
    postAccountTransferFrom: async (params, source) => {
      const url = '/account/transfer-from';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.AccountTransferToResponse>(url, data, possibleResTypes);
    },
    postAccountTransferTo: async (params, source) => {
      const url = '/account/transfer-to';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.AccountTransferToResponse>(url, data, possibleResTypes);
    },
    postAccountTransferFund: async (params, source) => {
      const url = '/account/client-accounts/fund-transfer';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.AccountTransferFundResponse>(url, data, possibleResTypes);
    },
    postAccountTransferWithdraw: async (params, source) => {
      const url = '/account/client-accounts/transfer-withdraw';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.AccountTransferWithdrawResponse>(url, data, possibleResTypes);
    },
    postAccountTransferFundWithdraw: async (params, source) => {
      const url = '/account/client-accounts/fund-transfer-withdraw';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.AccountTransferFundWithdrawResponse>(url, data, possibleResTypes);
    },
    postAccountTransferFundSchedule: async (params, source) => {
      const url = '/account/client-accounts/fund-transfer/schedule';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.AccountTransferScheduleResponse>(url, data, possibleResTypes);
    },
    postAccountTransferWithdrawSchedule: async (params, source) => {
      const url = '/account/client-accounts/transfer-withdraw/schedule';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.AccountTransferScheduleResponse>(url, data, possibleResTypes);
    },
    postAccountTransferFundWithdrawSchedule: async (params, source) => {
      const url = '/account/client-accounts/fund-transfer-withdraw/schedule';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.AccountTransferScheduleResponse>(url, data, possibleResTypes);
    },
    fetchAccountTransferSchedules: async (params, source) => {
      const url = '/account/client-accounts/scheduled-transfers';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.AccountTransferSchedulesResponse>(url, data, possibleResTypes);
    },
    postAccountTransferScheduleCancel: async (params, source) => {
      const url = '/account/client-accounts/transfer/schedule/cancel';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.AccountTransferScheduleCancelResponse>(url, data, possibleResTypes);
    },
    fetchTransactionsHistory: async (params, source) => {
      const url = '/account/transactions';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: {
          params: {
            ...params,
            ExportParams: params.ExportParams ? JSON.stringify(params.ExportParams) : undefined,
          },
        },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const response = verifyResponseType<types.TransactionsHistoryApiResponse>(url, data, possibleResTypes);
      return {
        ...response,
        Transactions: response.Transactions
          ? Object.values(response.Transactions).map((transaction) => ({
              ...transaction,
              IsRefund: toBool(transaction.IsRefund),
              IsRefunded: toBool(transaction.IsRefunded),
              ChildTransactionIDs: transaction.ChildTransactionIDs
                ? Object.values(transaction.ChildTransactionIDs)
                : [],
            }))
          : [],
      };
    },
    getSubAccountTransactionsHistory: async (params, source) => {
      const url = '/account/subaccount/transactions';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.SubAccountTransactionsHistoryResponse>(url, data, possibleResTypes);
    },
    postCreateSubAccount: async (params, source) => {
      const url = '/account/subaccount';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.CreateSubAccountResponse>(url, data, possibleResTypes);
    },
    postCreateGroup: async (params, source) => {
      const url = '/group';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.CreateGroupResponse>(url, data, possibleResTypes);
    },
    postEditGroup: async (params, source) => {
      const url = '/group/edit';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.ApiResponse>(url, data, possibleResTypes);
    },
    postDeleteGroup: async (params, source) => {
      const url = '/group/delete';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.ApiResponse>(url, data, possibleResTypes);
    },
    postAddGroupContact: async (params, source) => {
      const url = '/group/contact';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.AddGroupContactResponse>(url, data, possibleResTypes);
    },
    postDeleteGroupContact: async (params, source) => {
      const url = '/group/contact/delete';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.ApiResponse>(url, data, possibleResTypes);
    },
    postBulkPayment: async (params, source) => {
      const url = '/batch/eft/withdraw';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: {},
        body: JSON.stringify(params),
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.BulkResponse>(url, data, possibleResTypes);
    },
    postBulkCollection: async (params, source) => {
      const url = '/batch/eft/fund';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: {},
        body: JSON.stringify(params),
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.BulkResponse>(url, data, possibleResTypes);
    },

    postBulkRTRPayment: async (params, source) => {
      const url = '/batch/rtr/withdraw';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: {},
        body: JSON.stringify(params),
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.BulkResponse>(url, data, possibleResTypes);
    },
    postBulkRTRCollection: async (params, source) => {
      const url = '/batch/rtr/fund';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: {},
        body: JSON.stringify(params),
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.BulkResponse>(url, data, possibleResTypes);
    },
    postBulkMoneyRequest: async (params, source) => {
      const url = '/batch/interac/money-request';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: {},
        body: JSON.stringify(params),
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.BulkResponse>(url, data, possibleResTypes);
    },
    postBulkBulkPayout: async (params, source) => {
      const url = '/batch/interac/bulk-payout';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: {},
        body: JSON.stringify(params),
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.BulkResponse>(url, data, possibleResTypes);
    },
    postBulkPaylink: async (params, source) => {
      const url = '/batch/elinx';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: {},
        body: JSON.stringify(params),
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.BulkResponse>(url, data, possibleResTypes);
    },
    postBulkPushToCardPayment: async (params, source) => {
      const url = '/batch/debit-card/push-funds';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: {},
        body: JSON.stringify(params),
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.BulkResponse>(url, data, possibleResTypes);
    },
    postBulkCreditCardCollection: async (params, source) => {
      const url = '/batch/credit-card/charge';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: {},
        body: JSON.stringify(params),
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.BulkResponse>(url, data, possibleResTypes);
    },
    postBulkACHCollection: async (params, source) => {
      const url = '/batch/ach/fund';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: {},
        body: JSON.stringify(params),
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.BulkResponse>(url, data, possibleResTypes);
    },
    postBulkACHPayment: async (params, source) => {
      const url = '/batch/ach/withdraw';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: {},
        body: JSON.stringify(params),
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.BulkResponse>(url, data, possibleResTypes);
    },
    PostBulkRTPPayment: async (params, source) => {
      const url = '/batch/rtp/withdraw';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: {},
        body: JSON.stringify(params),
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.BulkResponse>(url, data, possibleResTypes);
    },
    postBulkFedwireCollection: async (params, source) => {
      const url = '/batch/fedwire/fund';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: {},
        body: JSON.stringify(params),
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.BulkResponse>(url, data, possibleResTypes);
    },
    postBulkFedwirePayment: async (params, source) => {
      const url = '/batch/fedwire/withdraw';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: {},
        body: JSON.stringify(params),
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.BulkResponse>(url, data, possibleResTypes);
    },
    postBulkClientAccountTransfer: async (params, source) => {
      const url = '/batch/client-account/transfer';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: {},
        body: JSON.stringify(params),
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.BulkResponse>(url, data, possibleResTypes);
    },
    fetchUploadedFiles: async (params, source) => {
      const url = '/batch';

      const { data } = await http.get<unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      const res = verifyResponseType<types.UploadBatchFileApiResponse>(url, data, possibleResTypes);
      return {
        ...res,
        Batches: Object.values(res.Batches || {}),
      };
    },
    fetchUploadedFileDetails: async (params, source) => {
      const url = '/batch/details';

      const { data } = await http.get<unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.UploadBatchFileDetailseResponse>(url, data, possibleResTypes);
    },
    postCancelScheduledWithdraw: async (params, source) => {
      const url = '/eft/withdraw/schedule/cancel';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.CancelScheduledWithdrawResponse>(url, data, possibleResTypes);
    },
    postCancelScheduledFund: async (params, source) => {
      const url = '/eft/fund/schedule/cancel';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.CancelScheduledFundResponse>(url, data, possibleResTypes);
    },
    fetchClientAccountLabels: async (params, source) => {
      const url = '/account/client-accounts/label';

      const { data } = await http.get<unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      const res = verifyResponseType<types.FetchLabelsApiResponse>(url, data, possibleResTypes);

      return {
        ...res,
        Labels: Object.values(res.Labels || {}),
      };
    },
    addClientAccountLabel: async (params, source) => {
      const url = '/account/client-accounts/label/add';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.AddLabelResponse>(url, data, possibleResTypes);
    },
    deleteClientAccountLabel: async (params, source) => {
      const url = '/account/client-accounts/label/delete';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.DeleteLabelResponse>(url, data, possibleResTypes);
    },
    fetchContactLabels: async (params, source) => {
      const url = '/contacts/label';

      const { data } = await http.get<unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      const res = verifyResponseType<types.FetchLabelsApiResponse>(url, data, possibleResTypes);

      return {
        ...res,
        Labels: Object.values(res.Labels || {}),
      };
    },
    addContactLabel: async (params, source) => {
      const url = '/contacts/label/add';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.AddLabelResponse>(url, data, possibleResTypes);
    },
    deleteContactLabel: async (params, source) => {
      const url = '/contacts/label/delete';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.DeleteLabelResponse>(url, data, possibleResTypes);
    },
    postEditScheduledWithdraw: async (params, source) => {
      const url = '/eft/withdraw/schedule/edit';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.ApiResponse>(url, data, possibleResTypes);
    },
    postEditScheduledFund: async (params, source) => {
      const url = '/eft/fund/schedule/edit';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.ApiResponse>(url, data, possibleResTypes);
    },
    fetchTransactionCounts: async (params, source) => {
      const url = 'account/transaction/counts';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.GetTransactionCountsResponse>(url, data, possibleResTypes);
    },
    postConfirmTransaction: async (params, source) => {
      const url = '/account/transaction/confirm';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.ApiResponse>(url, data, possibleResTypes);
    },
    postSendLegalAgreement: async (bearer, source) => {
      const url = '/vopay/onboarding-app/legal-agreement';
      const { data } = await http.post<Record<string, never>, unknown>({
        url,
        config: {},
        source,
        bearer,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.ApiResponse>(url, data, possibleResTypes);
    },

    template: async (params, source) => {
      const url = '/template';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<unknown>(url, data, possibleResTypes);
    },

    fetchPartners: async (source) => {
      const url = '/partner/list';

      const { data } = await http.get<unknown>({
        url,
        config: {},
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.PartnersResponse>(url, data, possibleResTypes);
    },

    fetchUserLists: async (params, source) => {
      const url = '/partner/user-list';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.UserListsResponse>(url, data, possibleResTypes);
    },
    fetchPartnerAccountTransactions: async (params, source) => {
      const url = '/partner/account/transactions';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.PartnerAccountTransactions>(url, data, possibleResTypes);
    },
    fetchHubSpotDropdownData: async (source) => {
      const url = '/partner/account/hubspot/dropdown/data';

      const { data } = await http.get<Record<never, never>, unknown>({
        url,
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.HubSpotDropdownDataResponse];
      return verifyResponseType<types.HubSpotDropdownDataResponse>(url, data, possibleResTypes);
    },
    fetchHubSpotDealData: async (params, source) => {
      const url = '/partner/account/hubspot/deal/id';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.HubSpotDealDetailsResponse];
      return verifyResponseType<types.HubSpotDealDetailsResponse>(url, data, possibleResTypes);
    },
    postAccountBillingSavePaymentInformation: async (params, source, bearer) => {
      const url = '/vopay/onboarding-app/account-billing/save-payment-information';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        bearer,
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.AccountBillingSavePaymentInformationResponse];
      return verifyResponseType<types.AccountBillingSavePaymentInformationResponse>(url, data, possibleResTypes);
    },
    fetchAccountBillingDetails: async (params, source, bearer) => {
      const url = '/vopay/onboarding-app/account-billing';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        bearer,
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.AccountBillingDetailsResponse];
      return verifyResponseType<types.AccountBillingDetailsResponse>(url, data, possibleResTypes);
    },
    postAccountBillingDetails: async (params, source, bearer) => {
      const url = '/vopay/onboarding-app/account-billing';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        bearer,
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.ApiResponse>(url, data, possibleResTypes);
    },
    postAccountBillingForDisconnection: async (params, source, bearer) => {
      const url = '/vopay/onboarding-app/account-billing/disconnect-account';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        bearer,
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.ApiResponse>(url, data, possibleResTypes);
    },
    fetchAccountDocument: async (params, source, bearer) => {
      const url = '/document/file/content';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        bearer,
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.AccountDocumentResponse];
      return verifyResponseType<types.AccountDocumentResponse>(url, data, possibleResTypes);
    },
    postHubSpotDealUpdate: async (params, source?) => {
      const url = '/partner/account/hubspot/deal/update';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.ApiResponse>(url, data, possibleResTypes);
    },
    postAccountDocumentUpload: async (params, bearer, source?) => {
      const url = '/document';

      const { data } = await http.post<typeof params, unknown>({
        url,
        body: JSON.stringify(params),
        config: {},
        bearer,
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.DocumentUploadResponse];

      return verifyResponseType<types.DocumentUploadResponse>(url, data, possibleResTypes);
    },

    postBankAccountDocumentUpload: async (params, bearer, source?) => {
      const url = '/vopay/onboarding-app/bank-account/document';

      const { data } = await http.post<typeof params, unknown>({
        url,
        body: JSON.stringify(params),
        config: {},
        bearer,
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.DocumentUploadResponse];

      return verifyResponseType<types.DocumentUploadResponse>(url, data, possibleResTypes);
    },
    postBankAccountDocumentDelete: async (params, bearer, source?) => {
      const url = '/vopay/onboarding-app/bank-account/document/delete';

      const { data } = await http.post<typeof params, unknown>({
        url,
        body: JSON.stringify(params),
        config: {},
        bearer,
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.ApiResponse>(url, data, possibleResTypes);
    },
    postPartnerAccountTransfer: async (params, source) => {
      const url = '/partner/account/transfer';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.PartnerAccountTransferResponse>(url, data, possibleResTypes);
    },
    postPartnerAccountFundAndTransfer: async (params, source) => {
      const url = '/partner/account/fund-transfer';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.PartnerAccountFundAndTransferResponse>(url, data, possibleResTypes);
    },
    postPartnerAccountTransferAndWithdraw: async (params, source) => {
      const url = '/partner/account/transfer-withdraw';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.PartnerAccountTransferAndWithdrawResponse>(url, data, possibleResTypes);
    },
    postPartnerAccountFundTransferAndWithdraw: async (params, source) => {
      const url = '/partner/account/fund-transfer-withdraw';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.PartnerAccountFundTransferAndWithdrawResponse>(url, data, possibleResTypes);
    },
    postRenewAccount: async (params, source) => {
      const url = '/account/renew';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.ApiResponse>(url, data, possibleResTypes);
    },

    fetchTicketCategories: async (source) => {
      const url = '/partner/get-ticket-categories';

      const { data } = await http.get<Record<never, never>, unknown>({
        url,
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const res = verifyResponseType<types.TicketCategoriesResponse>(url, data, possibleResTypes);

      return {
        ...res,
        SupportTicketCategory: Object.values(res.SupportTicketCategory || {}),
        SupportTicketSubCategory: Object.values(res.SupportTicketSubCategory || {}),
      };
    },

    fetchPortalVideos: async (params, source) => {
      const url = '/media?media_type=video';

      const formattedParams = { ...params, IsPartner: true };
      const { data } = await learningHttp.get<Record<never, never>, unknown>({
        url,
        config: { params: formattedParams },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.PortalVideoResponse];
      return verifyResponseType<types.PortalVideoResponse>(url, data, possibleResTypes);
    },

    fetchClientDashboardTicketCategories: async (source) => {
      const url = '/account/get-ticket-categories';

      const { data } = await http.get<Record<never, never>, unknown>({
        url,
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const res = verifyResponseType<types.TicketCategoriesResponse>(url, data, possibleResTypes);

      return {
        ...res,
        SupportTicketCategory: Object.values(res.SupportTicketCategory || {}),
        SupportTicketSubCategory: Object.values(res.SupportTicketSubCategory || {}),
      };
    },

    fetchDefaultPaymentMethod: async (params, source) => {
      const url = '/account/default-payment-method';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.DefaultPaymentMethodResponse>(url, data, possibleResTypes);
    },

    postSetDefaultPaymentMethod: async (params, source, bearer) => {
      const url = '/account/set-default-payment-method';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: {},
        body: JSON.stringify(params),
        bearer,
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.ApiResponse>(url, data, possibleResTypes);
    },
    postCreateClientAccountIndividual: async (params, source) => {
      const url = '/account/client-accounts/individual';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.CreateClientAccountResponse>(url, data, possibleResTypes);
    },
    postCreateClientAccountBusiness: async (params, source) => {
      const url = '/account/client-accounts/business';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.CreateClientAccountResponse>(url, data, possibleResTypes);
    },
    createClientAccountReceiveOnly: async (params, source) => {
      const url = '/account/client-accounts/receive-only';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.CreateClientAccountResponse>(url, data, possibleResTypes);
    },
    createClientAccountStandard: async (params, source) => {
      const url = '/account/client-accounts/standard';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.CreateClientAccountResponse>(url, data, possibleResTypes);
    },

    editClientAccount: async (params, source) => {
      const url = '/account/client-accounts/edit';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.EditClientAccountResponse>(url, data, possibleResTypes);
    },

    fetchTicketDetailsClient: async (params, source) => {
      const url = '/account/ticket-detail';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.TicketDetailsResponse>(url, data, possibleResTypes);
    },
    fetchTicketsClient: async (params, source) => {
      const url = '/account/tickets';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      const res = verifyResponseType<types.TicketsApiResponse>(url, data, possibleResTypes);

      return {
        ...res,
        Tickets: Object.values(res.Tickets),
      };
    },
    submitClientSupportTicket: async (params, source) => {
      const url = '/account/submit-ticket';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: {},
        body: JSON.stringify(params),
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.SubmitTicketResponse>(url, data, possibleResTypes);
    },
    postSubmitTicketCommentClient: async (params, source) => {
      const url = '/account/submit-ticket-comment';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: {},
        body: JSON.stringify(params),
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.ApiResponse>(url, data, possibleResTypes);
    },
    postSubmitTicketAttachment: async (params, source) => {
      const url = '/partner/ticket-attachment';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: {},
        body: JSON.stringify(params),
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.TicketAttachmentResponse>(url, data, possibleResTypes);
    },
    postSubmitTicketAttachmentClient: async (params, source) => {
      const url = '/account/ticket-attachment';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: {},
        body: JSON.stringify(params),
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.TicketAttachmentResponse>(url, data, possibleResTypes);
    },
    fetchWebhooks: async (params, source) => {
      const url = '/account/webhooks';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.WebhooksResponse>(url, data, possibleResTypes);
    },

    fetchPartnerWebhooks: async (params, source) => {
      const url = '/partner/webhooks';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.WebhooksResponse>(url, data, possibleResTypes);
    },
    fetchWebhookInfo: async (params, source) => {
      const url = '/account/webhook-url/info';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.WebhookResponse>(url, data, possibleResTypes);
    },
    fetchPartnerWebhook: async (params, source) => {
      const url = '/partner/webhook-url/info';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.WebhookResponse>(url, data, possibleResTypes);
    },
    fetchTestWebhook: async (params, source) => {
      const url = '/account/webhook-url/test';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.ApiResponse>(url, data, possibleResTypes);
    },
    fetchPartnerTestWebhook: async (params, source) => {
      const url = '/partner/webhook-url/test';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.ApiResponse>(url, data, possibleResTypes);
    },
    postPartnerUpdateWebhook: async (params, source) => {
      const url = '/partner/webhook-url';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.ApiResponse>(url, data, possibleResTypes);
    },
    postUpdateWebhook: async (params, source) => {
      const url = '/account/webhook-url';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.ApiResponse>(url, data, possibleResTypes);
    },
    postResendWebhook: async (params, source) => {
      const url = '/account/webhook/resend';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.ApiResponse>(url, data, possibleResTypes);
    },
    postPartnerResendWebhook: async (params, source) => {
      const url = '/partner/webhook/resend';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.ApiResponse>(url, data, possibleResTypes);
    },
    postSetPlaidCredentials: async (params, source) => {
      const url = '/account/set-plaid-credentials';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.ApiResponse>(url, data, possibleResTypes);
    },
    postSetFlinksCredentials: async (params, source) => {
      const url = '/account/set-flinks-credentials';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.ApiResponse>(url, data, possibleResTypes);
    },
    postSetInveriteCredentials: async (params, source) => {
      const url = '/account/set-inverite-credentials';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.ApiResponse>(url, data, possibleResTypes);
    },
    postSetGlobalPayCredentials: async (params, source) => {
      const url = '/account/set-global-pay-credentials';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.ApiResponse>(url, data, possibleResTypes);
    },
    postSaveAuthorizedIPs: async (params, source) => {
      const url = '/account/authorized-ips';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.ApiResponse>(url, data, possibleResTypes);
    },
    fetchAuthorizedIPs: async (params, source) => {
      const url = '/account/authorized-ips';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.AuthorizedIPsResponse>(url, data, possibleResTypes);
    },
    fetchELinxHistory: async (params, bearer, source) => {
      const url = '/elinx';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        bearer,
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      const resp = verifyResponseType<types.ELinxHistoryApiResponse>(url, data, possibleResTypes);
      const response: types.ELinxHistoryResponse = {
        ...resp,
        ELinxRequests: Object.values(resp.ELinxRequests).map((item) => ({
          ...item,
          NotificationEnabled: toBool(item.NotificationEnabled),
          ReminderEnabled: toBool(item.ReminderEnabled),
          Recipients: Object.values(item.Recipients),
          CardTypeValidation: toBool(item.CardTypeValidation),
          AcceptedCardBrands: (item.AcceptedCardBrands
            ? item.AcceptedCardBrands.split(',')
            : []) as types.AcceptedCardBrand[],
        })),
      };
      return response;
    },

    cancelElinx: async (params, source) => {
      const url = '/elinx/cancel';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.ApiResponse>(url, data, possibleResTypes);
    },
    fetchPaylinkSettings: async (params, source) => {
      const url = '/elinx/settings';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.ELinxSettingsResponse>(url, data, possibleResTypes);
    },
    postPaylinkSettingsSetSendeeEmail: async (params, source) => {
      const url = '/elinx/settings/setup-email-notifications';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.ApiResponse>(url, data, possibleResTypes);
    },
    postPaylinkSettingsSetSenderEmail: async (params, source) => {
      const url = '/elinx/settings/setup-sender-email';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.ApiResponse>(url, data, possibleResTypes);
    },
    postPaylinkSettingsSetTokenExpiration: async (params, source) => {
      const url = '/elinx/settings/setup-token-expiration';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.ApiResponse>(url, data, possibleResTypes);
    },

    postPaylinkSettingsUpdate: async (params, source) => {
      const url = '/elinx/update-settings';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.ApiResponse>(url, data, possibleResTypes);
    },

    postPaylinkSettingsSetDecline: async (params, source) => {
      const url = '/elinx/update-settings';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.ApiResponse>(url, data, possibleResTypes);
    },
    postDeleteInteracEmail: async (params, source) => {
      const url = '/interac/email/delete';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.ApiResponse>(url, data, possibleResTypes);
    },
    postAddInteracEmail: async (params, source, bearer) => {
      const url = '/interac/email/add';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.InteracAddEmailResponse>(url, data, possibleResTypes);
    },
    postLinkInteracEmail: async (params, source) => {
      const url = '/interac/email/link-email';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.InteracLinkEmailResponse>(url, data, possibleResTypes);
    },
    postIQ11Tokenize: async (params, source) => {
      const url = '/iq11/tokenize';
      const { data } = await http.post<typeof params, unknown>({
        url,
        body: JSON.stringify(params),
        config: {},
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.IQ11TokenizeResponse>(url, data, possibleResTypes);
    },
    fetchiQ11AccessTokenInfo: async (params, source) => {
      const url = '/iq11/access-token';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.AccessTokenInfoResponse>(url, data, possibleResTypes);
    },
    postLinkPushToCardRequest: async (params, source) => {
      const url = '/debit-card/link-card-request';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.PushToCardMapResponse>(url, data, possibleResTypes);
    },
    postOnboardingToken: async (params, source) => {
      const url = '/vopay/onboarding-app/token';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.OnboardingTokenResponse>(url, data, possibleResTypes);
    },
    postOnboardingAppToken: async (params, source) => {
      const url = '/vopay/onboarding-app/token';
      const { data } = await http.post<typeof params, unknown>({
        url,
        body: JSON.stringify(params),
        config: {},
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.OnboardingAppTokenResponse>(url, data, possibleResTypes);
    },
    getAssignedBillingPackage: async (params, source, bearer) => {
      const url = '/account/assigned-billing-package';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        bearer,
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.AssignedBillingPackagesResponse>(url, data, possibleResTypes);
    },
    getAccountTransactionTypes: async (params, source, bearer) => {
      const url = '/account/transactions/types';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        bearer,
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.AccountTransactionTypesResponse];
      return verifyResponseType<types.AccountTransactionTypesResponse>(url, data, possibleResTypes);
    },
    fetchPartnerTransactionTypes: async (params, source, bearer) => {
      const url = '/partner/transactions/types';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        bearer,
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.AccountTransactionTypesResponse];
      const res = verifyResponseType<types.AccountTransactionTypesResponse>(url, data, possibleResTypes);

      return {
        ...res,
        TransactionTypes: Object.values(res.TransactionTypes || {}),
      };
    },
    fetchSubscriptions: async (params, source) => {
      const url = '/report-subscription';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const res = verifyResponseType<types.FetchSubscriptionListResponse>(url, data, possibleResTypes);

      return {
        ...res,
        Subscriptions: Object.values(res.Subscriptions),
      };
    },
    fetchSubscriptionDetail: async (params, source) => {
      const url = '/report-subscription/detail';
      const { data } = await http.get<typeof params, types.ReportDetailApiResponse>({
        url,
        config: { params },
        source,
      });
      const response: types.ReportDetailResponse = {
        ...data,
        AccountSubscriptionDetail: {
          ...data.AccountSubscriptionDetail,
          IncludeSubAccounts: data.AccountSubscriptionDetail.IncludeSubAccounts === '1',
          IncludeGraph: data.AccountSubscriptionDetail.IncludeGraph === '1',
          FilterParams: {
            TransactionReportFailureReasons: Object.values(
              data.AccountSubscriptionDetail.FilterParams.TransactionReportFailureReasons || {},
            ),
          },
        },
      };
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.ReportDetailResponse>(url, response, possibleResTypes);
    },
    postEditReport: async (params, source) => {
      const url = '/report-subscription/edit';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.ApiResponse>(url, data, possibleResTypes);
    },
    postCreateReport: async (params, source) => {
      const url = '/report-subscription';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.CreateReportsResponse>(url, data, possibleResTypes);
    },
    postDownloadReport: async (params, source) => {
      const url = '/report/download';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.DownloadReportsResponse>(url, data, possibleResTypes);
    },
    fetchAccountInvoicePdf: async (params, source) => {
      const url = '/account/invoice-pdf';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.InvoicePdfResponse>(url, data, possibleResTypes);
    },
    fetchAccountStatementPDF: async (params, source) => {
      const url = '/account/statement-pdf';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.StatementPDFResponse>(url, data, possibleResTypes);
    },
    fetchAccountStatementCsv: async (params, source) => {
      const url = '/account/statement-csv';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.StatementCsvResponse>(url, data, possibleResTypes);
    },
    fetchAccountInvoices: async (params, source) => {
      const url = '/account/invoices';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.AccountInvoicesResponse>(url, data, possibleResTypes);
    },
    verifyInterac: async (params, source) => {
      const url = '/account-verification/interac';
      const { data } = await http.post<typeof params, unknown>({
        url,
        body: JSON.stringify(params),
        config: {},
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.VerifyInteracResponse>(url, data, possibleResTypes);
    },
    verifyCard: async (params, source) => {
      const url = '/account-verification/card';
      const { data } = await http.post<typeof params, unknown>({
        url,
        body: JSON.stringify(params),
        config: {},
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.VerifyCardResponse>(url, data, possibleResTypes);
    },
    initiateBankAccountVerification: async (params, source) => {
      const url = '/account-verification/bank-account/initiate';
      const { data } = await http.post<typeof params, unknown>({
        url,
        body: JSON.stringify(params),
        config: {},
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.InitiateBankAccountVerificationResponse>(url, data, possibleResTypes);
    },

    postAccountVerificationConfirm: async (params, source) => {
      const url = '/account-verification/bank-account/confirm';
      const { data } = await http.post<typeof params, unknown>({
        url,
        body: JSON.stringify(params),
        config: {},
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.AccountVerificationConfirmResponse>(url, data, possibleResTypes);
    },
    fetchDebitAuthority: async (params, source) => {
      const url = '/account/debit-authority-agreement';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.GetDebitAuthorityAgreementResponse>(url, data, possibleResTypes);
    },
    postDebitAuthority: async (params, source) => {
      const url = '/account/debit-authority-agreement';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.ApiResponse>(url, data, possibleResTypes);
    },
    fetchAccountBranding: async (params, source, bearer) => {
      const url = '/account/branding-details';
      const { data } = await http.get<typeof params, types.AccountBrandingApiResponse>({
        url,
        config: { params },
        bearer,
        source,
      });

      return {
        ...data,
        AppTitle: data.PageTitle,
      };
    },
    fetchPartnerBranding: async (params, source) => {
      const url = '/partner/branding-details';
      const { data } = await http.get<typeof params, types.AccountBrandingApiResponse>({
        url,
        config: { params },
        source,
      });

      return {
        ...data,
        AppTitle: data.PageTitle,
      };
    },
    fetchPartnerAccountPermissionFields: async (params, source) => {
      const url = '/partner/account/permissions';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.PartnerAccountPermissionFieldsResponse>(url, data, possibleResTypes);
    },
    postAccountBranding: async (params, source) => {
      const url = '/account/branding';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: {},
        body: JSON.stringify(params),
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.ApiResponse>(url, data, possibleResTypes);
    },
    postPartnerBranding: async (params, source) => {
      const url = '/partner/branding';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: {},
        body: JSON.stringify(params),
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.ApiResponse>(url, data, possibleResTypes);
    },
    fetchLoginPageBranding: async (params, source) => {
      const url = '/portal/login-branding-details';
      const { data } = await http.get<typeof params, types.LoginBrandingApiResponse>({
        url,
        config: { params },
        source,
      });

      return {
        ...data,
        AppTitle: data.PageTitle,
      };
    },
    fetchFAQs: async (source) => {
      const url = '/faq';
      const { data } = await http.get<unknown>({
        url,
        config: {},
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.FAQResponse>(url, data, possibleResTypes);
    },
    fetchFAQAllQuestions: async (source) => {
      const url = '/faq-questions';
      const { data } = await http.get<unknown>({
        url,
        config: {},
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.FAQAllQuestionsResponse>(url, data, possibleResTypes);
    },
    postFAQUpdateClickCount: async (params, source) => {
      const url = 'faq/click-count';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.FAQResponse>(url, data, possibleResTypes);
    },
    fetchFAQCategories: async (source) => {
      const url = '/faq-categories';
      const { data } = await http.get<unknown>({
        url,
        config: {},
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.FAQCategoriesResponse>(url, data, possibleResTypes);
    },
    fetchApprovalTransactions: async (params, source) => {
      const url = '/transaction/approval/transactions';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.ApprovalInboxTransactionsResponse>(url, data, possibleResTypes);
    },
    fetchApprovalTransactionsInbox: async (params, bearer, source) => {
      const url = '/transaction/approval/transaction/inbox';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        bearer,
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.ApprovalInboxTransactionsResponse>(url, data, possibleResTypes);
    },
    fetchApprovalUsers: async (source?: CancelTokenSource) => {
      const url = '/transaction/approval/users';
      const { data } = await http.get<unknown, unknown>({
        url,
        config: {},
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const resp = verifyResponseType<types.FetchApprovalUsersApiResponse>(url, data, possibleResTypes);
      return {
        ...resp,
        Users: Object.values(resp.Users || {}),
      };
    },
    postVerifyApprovalTransation: async (params, source?) => {
      const url = '/qa/transaction/approve';
      const { data } = await http.post<typeof params, unknown>({
        url,
        body: JSON.stringify(params),
        config: {},
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.VerifyTrasnsactionApproveResponse>(url, data, possibleResTypes);
    },
    postVerifyMarkAsProceedTransaction: async (params, source) => {
      const url = '/qa/transaction/markAsProcessed';
      const { data } = await http.post<typeof params, unknown>({
        url,
        body: JSON.stringify(params),
        config: {},
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.VerifyTrasnsactionApproveResponse>(url, data, possibleResTypes);
    },
    approveTransaction: async (params, bearer, source) => {
      const url = '/transaction/approval/transaction/approve';
      const { data } = await http.post<typeof params, unknown>({
        url,
        body: JSON.stringify(params),
        config: {},
        bearer,
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.ApiResponse>(url, data, possibleResTypes);
    },
    unapproveTransaction: async (params, bearer, source) => {
      const url = '/transaction/approval/transaction/unapprove';
      const { data } = await http.post<typeof params, unknown>({
        url,
        body: JSON.stringify(params),
        config: {},
        bearer,
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.ApiResponse>(url, data, possibleResTypes);
    },
    rejectTransaction: async (
      params: types.InboxRejectStatusChangeRequest,
      bearer,
      source,
    ): Promise<types.ApiResponse> => {
      const url = '/transaction/approval/transaction/reject';
      const { data } = await http.post<typeof params, unknown>({
        url,
        body: JSON.stringify(params),
        config: {},
        bearer,
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.ApiResponse>(url, data, possibleResTypes);
    },
    fetchUserRoles: async (params: types.UserRolesRequest, source): Promise<types.UserRolesResponse> => {
      const url = '/transaction/approval/user/roles';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      const resp = verifyResponseType<types.UserRolesApiResponse>(url, data, possibleResTypes);
      return {
        ...resp,
        Roles: Object.values(resp.Roles || {}),
        UserRolePermissions: Object.values(resp.UserRolePermissions || {}),
      };
    },
    postCreateUserRole: async (params: types.UpdateUserRoleRequest, source): Promise<types.ApiResponse> => {
      const url = '/transaction/approval/user/role';
      const { data } = await http.post<typeof params, unknown>({
        url,
        body: JSON.stringify(params),
        config: {},
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.ApiResponse>(url, data, possibleResTypes);
    },
    postDeleteUserRole: async (params: types.UpdateUserRoleRequest, source): Promise<types.ApiResponse> => {
      const url = '/transaction/approval/user/role/delete';
      const { data } = await http.post<typeof params, unknown>({
        url,
        body: JSON.stringify(params),
        config: {},
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.ApiResponse>(url, data, possibleResTypes);
    },
    postCreateUserRolePermission: async (
      params: types.UpdateUserRolePermissionRequest,
      source,
    ): Promise<types.ApiResponse> => {
      const url = '/transaction/approval/user/role/permission';
      const { data } = await http.post<typeof params, unknown>({
        url,
        body: JSON.stringify(params),
        config: {},
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.ApiResponse>(url, data, possibleResTypes);
    },
    postUpdateUserRolePermission: async (
      params: types.UpdateUserRolePermissionRequest,
      source,
    ): Promise<types.ApiResponse> => {
      const url = '/transaction/approval/user/role/permission/update';
      const { data } = await http.post<typeof params, unknown>({
        url,
        body: JSON.stringify(params),
        config: {},
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.ApiResponse>(url, data, possibleResTypes);
    },
    postDeleteUserRolePermission: async (
      params: types.UpdateUserRolePermissionRequest,
      source,
    ): Promise<types.ApiResponse> => {
      const url = '/transaction/approval/user/role/permission/delete';
      const { data } = await http.post<typeof params, unknown>({
        url,
        body: JSON.stringify(params),
        config: {},
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.ApiResponse>(url, data, possibleResTypes);
    },
    fetchWorkflows: async (params?: types.PortalUser, bearer?: string): Promise<types.WorkflowsResponse> => {
      const url = '/transaction/approval/workflows';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        bearer,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      const res = verifyResponseType<types.WorkflowsApiResponse>(url, data, possibleResTypes);

      return {
        ...res,
        Workflows: Object.values(res.Workflows || {}).map((group) => ({
          TransactionType: group.TransactionType,
          Workflows: Object.values(group.Workflows || {}).map((wf) => ({
            ...wf,
            IsActive: toBool(wf.IsActive),
          })),
        })),
      };
    },
    postCreateWorkflow: async (params: types.WorkflowCreateRequest, source): Promise<types.ApiResponse> => {
      const url = '/transaction/approval/workflow';
      const { data } = await http.post<typeof params, unknown>({
        url,
        body: JSON.stringify(params),
        config: {},
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.ApiResponse>(url, data, possibleResTypes);
    },
    postUpdateWorkflow: async (params: types.WorkflowUpdateRequest, source): Promise<types.ApiResponse> => {
      const url = '/transaction/approval/workflow/update';
      const { data } = await http.post<typeof params, unknown>({
        url,
        body: JSON.stringify(params),
        config: {},
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.ApiResponse>(url, data, possibleResTypes);
    },
    deleteWorkflow: async (params: types.WorkflowDeleteRequest, source): Promise<types.ApiResponse> => {
      const url = '/transaction/approval/workflow/delete';
      const { data } = await http.post<typeof params, unknown>({
        url,
        body: JSON.stringify(params),
        config: {},
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.ApiResponse>(url, data, possibleResTypes);
    },
    fetchTransactionTypes: async (
      params: types.TransactionTypesRequst,
      bearer?: string,
      source?: CancelTokenSource,
    ): Promise<types.TransactionTypesResponse> => {
      const url = '/account/transactions/types';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        bearer,
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const resp = verifyResponseType<types.TransactionTypesResponseApi>(url, data, possibleResTypes);
      return {
        ...resp,
        TransactionTypes: Object.values(resp.TransactionTypes || {}),
      };
    },
    fetchRailzBusinessAccount: async (params, source) => {
      const url = '/railz/business-accounts';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.RailzBusinessAccountResponse>(url, data, possibleResTypes);
    },
    fetchRailzAccount: async (params, source) => {
      const url = '/railz/account';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.ChartOfAccountResponse>(url, data, possibleResTypes);
    },
    fetchRailzChartOfAccounts: async (params, source) => {
      const url = '/railz/account/railz-accounts';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.ChartOfAccountsResponse>(url, data, possibleResTypes);
    },
    postSetRailzChartOfAccount: async (params, source) => {
      const url = '/railz/account/railz-accounts';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.ApiResponse>(url, data, possibleResTypes);
    },

    fetchRailzBills: async (params, source) => {
      const url = '/railz/account/railz-bills';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const res = verifyResponseType<types.RailzBillsApiResponse>(url, data, possibleResTypes);
      return {
        ...res,
        Bills: Object.values(res.Bills || {}).map((bill) => ({
          ...bill,
          PaymentDetails: Object.values(bill.PaymentDetails || {}),
        })),
      };
    },
    postBills: async (params, source) => {
      const url = '/railz/account/railz-bills';
      const parseParams = {
        ...params,
        Bills: JSON.stringify(
          params.Bills.map((bill) => ({
            ...bill,
            PaymentDetails: arrayToObject(bill.PaymentDetails),
          })),
        ),
      };
      const { data } = await http.post<typeof params, unknown>({
        url,
        body: JSON.stringify(parseParams),
        config: {},
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.ApiResponse>(url, data, possibleResTypes);
    },

    fetchRailzInvoices: async (params, source) => {
      const url = '/railz/account/railz-invoices';
      const { data } = await http.get<typeof params, Record<string, unknown>>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const res = verifyResponseType<types.RailzInvoicesApiResponse>(url, data, possibleResTypes);
      return {
        ...res,
        Invoices: Object.values(res.Invoices || {}).map((invoice) => ({
          ...invoice,
          PaymentDetails: Object.values(invoice.PaymentDetails || {}),
        })),
      };
    },
    postInvoices: async (params, source) => {
      const url = '/railz/account/railz-invoices';
      const parseParams = {
        ...params,
        Invoices: JSON.stringify(
          params.Invoices.map((invoice) => ({
            ...invoice,
            PaymentDetails: arrayToObject(invoice.PaymentDetails),
          })),
        ),
      };

      const { data } = await http.post<typeof params, unknown>({
        url,
        body: JSON.stringify(parseParams),
        config: {},
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.ApiResponse>(url, data, possibleResTypes);
    },
    postRailzSync: async (params, source) => {
      const url = '/railz/data/sync';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.ApiResponse>(url, data, possibleResTypes);
    },
    postCancelInvoice: async (params, source) => {
      const url = '/railz/account/railz-invoices/cancel';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.ApiResponse>(url, data, possibleResTypes);
    },
    postCancelBill: async (params, source) => {
      const url = '/railz/account/railz-bills/cancel';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.ApiResponse>(url, data, possibleResTypes);
    },

    fetchRailzSyncStatus: async (params, source) => {
      const url = '/railz/data/sync/status';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.RailzStatusSyncData>(url, data, possibleResTypes);
    },
    fetchPortalDocument: async (source) => {
      const url = '/media?media_type=pdf';
      const { data } = await learningHttp.get<Record<never, never>, unknown>({
        url,
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.PortalDocumentResponse];
      return verifyResponseType<types.PortalDocumentResponse>(url, data, possibleResTypes);
    },
    postFundMyAccountSchedule: async (params, source) => {
      const url = '/account/fund-my-account/schedule';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.MyAccountScheduleResponse>(url, data, possibleResTypes);
    },
    postWithdrawMyAccountSchedule: async (params, source) => {
      const url = '/account/withdraw-my-account/schedule';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.MyAccountScheduleResponse>(url, data, possibleResTypes);
    },
    fetchAutoBalanceTransferReport: async (params, bearer, source) => {
      const url = '/account/auto-balance-transfer/report';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.AutoBalanceTransferReportResponse>(url, data, possibleResTypes);
    },
    fetchRailzBillCreditNotes: async (params, source) => {
      const url = '/railz/account/railz-bills/credit-notes';
      const { data } = await http.get<typeof params, types.CreditNotesApiResponse>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const res = verifyResponseType<types.CreditNotesApiResponse>(url, data, possibleResTypes);
      return {
        ...res,
        CreditNotes: Object.values(res.CreditNotes || {}),
      };
    },
    fetchRailzInvoiceCreditNotes: async (params, source) => {
      const url = '/railz/account/railz-invoices/credit-notes';
      const { data } = await http.get<typeof params, types.CreditNotesApiResponse>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const res = verifyResponseType<types.CreditNotesApiResponse>(url, data, possibleResTypes);
      return {
        ...res,
        CreditNotes: Object.values(res.CreditNotes || {}),
      };
    },
    fetchContacts: async (params = {}, source) => {
      const url = '/contacts';
      const { data } = await http.get<typeof params, types.ContactsApiResponse>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const res = verifyResponseType<types.ContactsApiResponse>(url, data, possibleResTypes);

      return {
        ...res,
        Contacts: Object.values(data.Contacts || {}).map((contact) => ({
          ...contact,
          IsActive: toBool(contact.IsActive),
          Labels: contact.Labels.split(',').filter((label) => label !== ''),
          AdditionalContacts: {
            email: Object.values(contact.AdditionalContacts.email || {}),
          },
        })),
      };
    },

    fetchRecentGlobalSendTransactions: async (params, bearer, source) => {
      const url = '/gcm/withdraw/transactions';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const res = verifyResponseType<types.GlobalSendRecentTransactionsApiResponse>(url, data, possibleResTypes);
      return {
        ...res,
        Transactions: Object.values(res.Transactions || {}).map((transaction) => ({
          ...transaction,
          FeesIncluded: toBool(transaction.FeesIncluded),
        })),
      };
    },

    postCrossBorderUrl: async (params, source, bearer) => {
      const url = '/gcm/generate-embed-url';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.CrossBorderUrlResponse>(url, data, possibleResTypes);
    },
    fetchCrossBorderUrlInfo: async (params, source) => {
      const url = '/portal/retrieve-single-use-token';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: {
          params: {
            Token: params.Token,
            ApplicationType: 'crossborder',
          },
        },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      const res = verifyResponseType<types.CrossBorderUrlApiInfoResponse>(url, data, possibleResTypes);

      return {
        ...res,
        ClientAccountsEnabled: toBool(res.ClientAccountsEnabled),
        FundingOptions: Object.values(res.FundingOptions || {}),
      };
    },
    fetchGlobalSendAdminUrlInfo: async (params, source) => {
      const url = '/portal/retrieve-single-use-token';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: {
          params: {
            Token: params.Token,
            ApplicationType: 'crossborder-admin',
          },
        },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const res = verifyResponseType<types.GlobalSendAdminUrlInfoResponse>(url, data, possibleResTypes);

      return res;
    },
    generateEmbedUrlDisputeManagement: async (params, source) => {
      const url = '/dispute-management/generate-embed-url';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const res = verifyResponseType<types.GenerateEmbedUrlDisputeManagementResponse>(url, data, possibleResTypes);

      return res;
    },
    fetchDisputeManagementUrlInfo: async (params, source) => {
      const url = '/portal/retrieve-single-use-token';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: {
          params: {
            Token: params.Token,
            ApplicationType: 'dispute-management',
          },
        },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const res = verifyResponseType<types.DisputeManagementUrlInfoResponse>(url, data, possibleResTypes);

      return {
        ...res,
        FeatureList: Object.values(res.FeatureList || {}),
        Permissions: Object.values(res.Permissions || {}),
      };
    },
    fetchBillPayInfo: async (params, source) => {
      const url = '/portal/retrieve-single-use-token';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: {
          params: {
            Token: params.Token,
            ApplicationType: 'bill-pay',
          },
        },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.BillPayInfoResponse>(url, data, possibleResTypes);
    },
    fetchTransactionApprovalInfo: async (params, source) => {
      const url = '/portal/retrieve-single-use-token';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: {
          params: {
            ...params,
            ApplicationType: 'transaction-approval',
          },
        },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.FetchTransactionApprovalInfoResponse>(url, data, possibleResTypes);
    },
    fetchTransactionApprovalHistory: async (params, bearer, source) => {
      const url = '/transaction/approval/history';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: {
          params: {
            TransactionID: params.TransactionID,
            Timezone: params.Timezone,
          },
        },
        bearer,
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      const resp = verifyResponseType<types.FetchTransactionApprovalHistoryApiResponse>(url, data, possibleResTypes);
      return {
        ...resp,
        ApprovalHistory: Object.values(resp.ApprovalHistory || {}),
      };
    },

    fetchWorkflowLogs: async (params, source) => {
      const url = '/transaction/approval/workflow/logs';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      const resp = verifyResponseType<types.FetchWorkflowLogsApiResponse>(url, data, possibleResTypes);
      return {
        ...resp,
        Logs: Object.values(resp.Logs || {}).map((l) => ({
          ...l,
          IsActive: toBool(l.IsActive),
        })),
      };
    },

    fetchCrossBorderCountries: async (params = {}, source) => {
      const url = '/gcm/countries';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const res = verifyResponseType<types.CrossBorderCountriesApiResponse>(url, data, possibleResTypes);
      return {
        ...res,
        Countries: Object.values(res.Countries || {}),
      };
    },
    fetchDeliveryChannelsFields: async (params, source) => {
      const url = '/gcm/delivery-channels/fields';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const res = verifyResponseType<types.DeliveryChannelsFieldsApiResponse>(url, data, possibleResTypes);

      return {
        ...res,
        DeliveryChannelFields: Object.values(res.DeliveryChannelFields || {}).map((field) => ({
          ...field,
          GroupOrder: Number(field.GroupOrder),
        })),
      };
    },
    fetchDeliveryChannelsFieldsData: async (params, source?) => {
      const url = '/gcm/delivery-channels/fields/data';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.DeliveryChannelsFieldsDataResponse>(url, data, possibleResTypes);
    },
    fetchDeliveryChannels: async (params, source) => {
      const url = '/gcm/delivery-channels';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const res = verifyResponseType<types.DeliveryChannelsApiResponse>(url, data, possibleResTypes);
      return {
        ...res,
        DeliveryChannels: Object.values(res.DeliveryChannels || {}),
      };
    },
    postCrossBorderQuotes: async (params, source) => {
      const url = '/gcm/withdraw/quote';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.CrossBorderQuotesResponse>(url, data, possibleResTypes);
    },
    fetchCrossBorderRecipientContacts: async (params, source) => {
      const url = '/gcm/recipient-contacts';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const res = verifyResponseType<types.CrossBorderRecipientContactsApiResponse>(url, data, possibleResTypes);
      return {
        ...res,
        RecipientContacts: Object.values(res.RecipientContacts || {}).map((contact) => ({
          ...contact,
          IsFavorite: toBool(contact.IsFavorite),
        })),
      };
    },
    postAddFavoriteRecipientContact: async (params, source) => {
      const url = '/gcm/recipient-contacts/favorites/add';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.AddFavoriteRecipientContactResponse>(url, data, possibleResTypes);
    },
    postDeleteFavoriteRecipientContact: async (params, source) => {
      const url = '/gcm/recipient-contacts/favorites/delete';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.DeleteFavoriteRecipientContactResponse>(url, data, possibleResTypes);
    },
    fetchRecipientContactDeliveryMethods: async (params, source) => {
      const url = '/gcm/delivery-methods';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const res = verifyResponseType<types.RecipientContactDeliveryMethodsApiResponse>(url, data, possibleResTypes);

      return {
        ...res,
        DeliveryMethods: Object.values(res.DeliveryMethods || {}),
      };
    },
    fetchCrossBorderBankCities: async (params, source) => {
      const url = '/gcm/banks/cities';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const res = verifyResponseType<types.CrossBorderBankCitiesApiResponse>(url, data, possibleResTypes);
      return {
        ...res,
        BankCities: Object.values(res.BankCities || {}),
      };
    },
    fetchCrossBorderBankBranches: async (params, source) => {
      const url = '/gcm/banks/branches';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const res = verifyResponseType<types.CrossBorderBankBranchesApiResponse>(url, data, possibleResTypes);
      return {
        ...res,
        BankBranches: Object.values(res.BankBranches || {}),
      };
    },
    fetchCrossBorderBankNames: async (params, source) => {
      const url = '/gcm/banks/names';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const res = verifyResponseType<types.CrossBorderBankNamesApiResponse>(url, data, possibleResTypes);
      return {
        ...res,
        BankNames: Object.values(res.BankNames || {}),
      };
    },
    fetchCrossBorderBankLocations: async (params, source) => {
      const url = '/gcm/banks/locations';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const res = verifyResponseType<types.CrossBorderBankLocationsApiResponse>(url, data, possibleResTypes);
      return {
        ...res,
        BankLocations: Object.values(res.BankLocations || {}),
      };
    },
    postCrossBorderAddDeliveryMethod: async (params, source) => {
      const url = '/gcm/delivery-methods/add';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.CrossBorderAddDeliveryMethodResponse>(url, data, possibleResTypes);
    },
    fetchGovernmentIdentifier: async (params, source) => {
      const url = '/gcm/government-identifier';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const res = verifyResponseType<types.GovernmentIdentifierApiResponse>(url, data, possibleResTypes);
      return {
        ...res,
        GovernmentIdentifiers: Object.values(res.GovernmentIdentifiers || {}),
      };
    },
    fetchGovernmentIdentifierSchemas: async (params, source?) => {
      const url = '/gcm/government-identifier/schemas';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.GovernmentIdentifierSchemasResponse>(url, data, possibleResTypes);
    },
    postGovernmentIdentifier: async (params, source?) => {
      const url = '/gcm/government-identifier/add';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.AddGovernmentIdentifierResponse>(url, data, possibleResTypes);
    },
    postEditRecipient: async (params, source?) => {
      const url = '/gcm/recipient-contacts/edit';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.EditRecipientResponse>(url, data, possibleResTypes);
    },
    postAddRecipient: async (params, source?) => {
      const url = '/gcm/recipient-contacts/add';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.EditRecipientResponse>(url, data, possibleResTypes);
    },
    postCreateGlobalSendTransaction: async (params, source) => {
      const url = '/gcm/withdraw';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.CreateGlobalSendTransactionResponse>(url, data, possibleResTypes);
    },

    fetchBankSchema: async (params, source) => {
      const url = '/gcm/banks/schemas';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const res = verifyResponseType<types.BankSchemaApiResponse>(url, data, possibleResTypes);
      return {
        ...res,
        BanksSchemaValues: Object.values(res.BanksSchemaValues || {}),
      };
    },
    confirmEmailVerification: async (params, source?) => {
      const url = '/contact-verification/email/confirm';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.ContactVerificationConfirmResponse>(url, data, possibleResTypes);
    },
    confirmPhoneVerification: async (params, source?) => {
      const url = '/contact-verification/phone/confirm';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.ContactVerificationConfirmResponse>(url, data, possibleResTypes);
    },
    postGenerateOtp: async (params, source?) => {
      const url = '/elinx/generate-otp';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.GenerateOtpResponse>(url, data, possibleResTypes);
    },
    postValidateOtp: async (params, source?) => {
      const url = '/elinx/validate-otp';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.ApiResponse>(url, data, possibleResTypes);
    },
    fetchElinxPaymentMethods: async (params, source, bearer) => {
      const url = '/elinx/payment-methods';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        bearer,
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const res = verifyResponseType<types.ElinxPaymentMethodsApiResponse>(url, data, possibleResTypes);
      return {
        ...res,
        PaymentMethods: Object.values(res.PaymentMethods || {}),
      };
    },
    postRequirePadAgreement: async (params, source, bearer) => {
      const url = '/elinx/requires-pad-agreement';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.RequirePadAgreementResponse>(url, data, possibleResTypes);
    },
    postAcceptAgreement: async (params, source) => {
      const url = '/account/accept-agreement';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.ApiResponse>(url, data, possibleResTypes);
    },
    postInterestAccount: async (source): Promise<types.ApiResponse> => {
      const url = '/interest-account/activate';
      const { data } = await http.post<unknown, unknown>({
        url,
        config: {},
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.ApiResponse>(url, data, possibleResTypes);
    },
    fetchInterestAccount: async (params, source): Promise<types.InterestAccountResponse> => {
      const url = '/interest-account';
      const { data } = await http.get<typeof params, types.InterestAccountApiResponse>({
        url,
        config: { params },
        source,
      });
      const response: types.InterestAccountResponse = {
        ...data,
        InterestAccounts: Object.values(data.InterestAccounts || {}),
      };
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.InterestAccountResponse>(url, response, possibleResTypes);
    },
    fetchInterestAccountDetail: async (params, source): Promise<types.InterestAccountDetailResponse> => {
      const url = '/interest-account/detail';
      const { data } = await http.get<typeof params, types.InterestAccountDetailApiResponse>({
        url,
        config: { params },
        source,
      });
      const response: types.InterestAccountDetailResponse = {
        ...data,
        InterestAccruedDetails: Object.values(data.InterestAccrued || {}),
      };
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.InterestAccountDetailResponse>(url, response, possibleResTypes);
    },
    postInterestAccountTransfer: async (params, source): Promise<types.InterestAccountTransferResponse> => {
      const url = '/interest-account/transfer';
      const { data } = await http.post<unknown, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.InterestAccountTransferResponse>(url, data, possibleResTypes);
    },
    fetchInterestAccountRates: async (params, source): Promise<types.InterestAccountRatesResponse> => {
      const url = '/interest-account/rates';
      const { data } = await http.get<typeof params, types.InterestAccountRatesApiResponse>({
        url,
        config: { params },
        source,
      });

      const response: types.InterestAccountRatesResponse = {
        ...data,
        InterestRates: Object.values(data.InterestRates || {}),
      };

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.InterestAccountRatesResponse>(url, response, possibleResTypes);
    },
    fetchInterestAccountAccruedInterest: async (
      params,
      source,
    ): Promise<types.InterestAccountAccruedInterestResponse> => {
      const url = '/interest-account/accrued-interest';
      const { data } = await http.get<typeof params, types.InterestAccountAccruedInterestApiResponse>({
        url,
        config: { params },
        source,
      });
      const response: types.InterestAccountAccruedInterestResponse = {
        ...data,
        AccruedInterestList: Object.values(data.AccruedInterest || {}),
      };
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.InterestAccountAccruedInterestResponse>(url, response, possibleResTypes);
    },
    fetchInterestAccountTransactions: async (params, source): Promise<types.InterestAccountTransactionsResponse> => {
      const url = '/interest-account/transactions';

      const { data } = await http.get<typeof params, types.InterestAccountTransactionsApiResponse>({
        url,
        config: { params },
        source,
      });

      const response: types.InterestAccountTransactionsResponse = {
        ...data,
        InterestAccountTransactions: Object.values(data.Transactions || {}),
      };

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.InterestAccountTransactionsResponse>(url, response, possibleResTypes);
    },
    postRecipientContactGenerateEmbedUrl: async (params, source) => {
      const url = '/gcm/recipient-contacts/generate-embed-url';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.RecipientContactsGenerateEmbedUrlResponse>(url, data, possibleResTypes);
    },
    fetchUserSettings: async (params, source?) => {
      const url = '/account/users/settings';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.UserSettingsResponse>(url, data, possibleResTypes);
    },
    postSubmitUserSettings: async (params, source) => {
      const url = '/account/users/settings';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.UserSettingsResponse>(url, data, possibleResTypes);
    },
    activateClientAccount: async (params, source) => {
      const url = '/account/client-accounts/activate';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.ActivateDeactiveteClientAccountResponse>(url, data, possibleResTypes);
    },
    deactivateClientAccount: async (params, source) => {
      const url = '/account/client-accounts/deactivate';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.ActivateDeactiveteClientAccountResponse>(url, data, possibleResTypes);
    },
    activateContact: async (params, source) => {
      const url = '/contacts/activate';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.ActivateDeactiveteContactResponse>(url, data, possibleResTypes);
    },
    deactivateContact: async (params, source) => {
      const url = '/contacts/deactivate';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.ActivateDeactiveteContactResponse>(url, data, possibleResTypes);
    },
    postRtpWithdraw: async (params, source) => {
      const url = '/rtp/withdraw';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.RtrWithdrawResponse>(url, data, possibleResTypes);
    },
    postFedwireWithdraw: async (params, source) => {
      const url = '/fedwire/withdraw';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.RtrWithdrawResponse>(url, data, possibleResTypes);
    },
    postFedwireFund: async (params, source) => {
      const url = '/fedwire/fund';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.FedwireFundResponse>(url, data, possibleResTypes);
    },
    fetchAccountExtendedInfo: async (bearer, source) => {
      const url = '/account/submit-extended-info';
      const { data } = await http.get<unknown>({
        url,
        source,
        bearer,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.AccountExtendedInfoResponse>(url, data, possibleResTypes);
    },
    postELinxSettingsSetNotification: async (params, source) => {
      const url = '/elinx/settings/setup-notification';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.ApiResponse>(url, data, possibleResTypes);
    },
    postELinxSettingsSetReminder: async (params, source) => {
      const url = '/elinx/settings/setup-reminder';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.ApiResponse>(url, data, possibleResTypes);
    },
    fetchWatchList: async (params, source?) => {
      const url = '/gcm/watchlist';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const resp = verifyResponseType<types.WatchListBaseResponse>(url, data, possibleResTypes);
      const response: types.WatchListResponse = {
        ...resp,
        Watchlist: Object.values(resp.Watchlist || {}),
      };
      return response;
    },

    addWatchList: async (params, source?) => {
      const url = '/gcm/watchlist/add';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.ApiResponse>(url, data, possibleResTypes);
    },
    postEditInteracEmail: async (params, source, bearer) => {
      const url = '/interac/email/edit';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.InteracEditEmailResponse>(url, data, possibleResTypes);
    },

    addContact: async (params, source) => {
      const url = '/contacts/add';

      const { data } = await http.post<typeof params, types.AddContactResponse>({
        url,
        config: { params },
        source,
      });
      return data;
    },

    editContact: async (params, source) => {
      const url = '/contacts/edit';

      const { data } = await http.post<typeof params, types.EditContactResponse>({
        url,
        config: { params },
        source,
      });
      return data;
    },

    deleteWatchList: async (params, source?) => {
      const url = '/gcm/watchlist/delete';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.ApiResponse>(url, data, possibleResTypes);
    },

    setWatchList: async (params, source?) => {
      const url = '/gcm/watchlist/set';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.ApiResponse>(url, data, possibleResTypes);
    },
    fetchCurrencies: async (params, source?) => {
      const url = '/gcm/currencies';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const resp = verifyResponseType<types.CurrenciesApiResponse>(url, data, possibleResTypes);
      return {
        ...resp,
        Currencies: Object.values(resp.Currencies || {}),
      };
    },
    generateGCMEmbedUrl: async (params, source) => {
      const url = '/gcm/generate-embed-url';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.GenerateGCMEmbedUrlResponse>(url, data, possibleResTypes);
    },
    fetchGCMBalance: async (params, source) => {
      const url = '/gcm/balance';
      const { data } = await http.get<typeof params, types.GCMBalanceResponse>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.GCMBalanceResponse>(url, data, possibleResTypes);
    },
    fetchGCMRatesFeesComparison: async (params, source) => {
      const url = '/gcm/rates-fees-comparison';
      const { data } = await http.get<typeof params, types.GCMRatesFeesComparisonApiResponse>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const res = verifyResponseType<types.GCMRatesFeesComparisonApiResponse>(url, data, possibleResTypes);
      return {
        ...res,
        Results: Object.values(res.Results || {}),
      };
    },
    railzCreateInvoice: async (params, source) => {
      const url = '/railz/account/railz-invoices/create';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: {},
        body: JSON.stringify({
          ...params,
          Invoice: JSON.stringify(params.Invoice),
        }),
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.ApiResponse>(url, data, possibleResTypes);
    },
    railzCustomers: async (params, source) => {
      const url = '/railz/account/railz-customers';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const res = verifyResponseType<types.RailzCustomerBaseApiResponse>(url, data, possibleResTypes);
      return {
        ...res,
        Customers: Object.values(res.Customers || {}),
      };
    },
    railzInventory: async (params, source) => {
      const url = '/railz/account/railz-inventory';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const res = verifyResponseType<types.RailzInventoryBaseApiResponse>(url, data, possibleResTypes);
      return {
        ...res,
        Inventory: Object.values(res.Inventory || {}).map((inventory) => {
          return {
            ...inventory,
            quantityOnHand: Object.values(inventory.quantityOnHand || {}),
          };
        }),
      };
    },
    railzTaxRates: async (params, source) => {
      const url = '/railz/account/railz-tax-rates';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const res = verifyResponseType<types.RailzTaxRatesBaseApiResponse>(url, data, possibleResTypes);
      return {
        ...res,
        TaxRates: Object.values(res.TaxRates || {}).map((taxRate) => ({
          ...taxRate,
          components: Object.values(taxRate.components || {}),
        })),
      };
    },
    railzTrackingCategories: async (params, source) => {
      const url = '/railz/account/railz-tracking-categories';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const res = verifyResponseType<types.RailzTrackingCategoriesBaseApiResponse>(url, data, possibleResTypes);
      return {
        ...res,
        TrackingCategories: Object.values(res.TrackingCategories || {}),
      };
    },
    railzCreateBill: async (params, source) => {
      const url = '/railz/account/railz-bills/create';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: {},
        body: JSON.stringify({
          ...params,
          Bill: JSON.stringify(params.Bill),
        }),
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.ApiResponse>(url, data, possibleResTypes);
    },
    railzVendors: async (params, source) => {
      const url = '/railz/account/railz-vendors';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const res = verifyResponseType<types.RailzVendorsBaseApiResponse>(url, data, possibleResTypes);
      return {
        ...res,
        Vendors: Object.values(res.Vendors || {}),
      };
    },
    railzCategories: async (params, source) => {
      const url = '/railz/account/railz-categories';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const res = verifyResponseType<types.RailzCategoriesBaseApiResponse>(url, data, possibleResTypes);
      return {
        ...res,
        Categories: Object.values(res.Categories || {}).map((category) => {
          return {
            ...category,
            subAccounts: Object.values(category.subAccounts || {}),
          };
        }),
      };
    },
    authenticateMerchantUser: async (source) => {
      const url = '/partner/onboarding/merchant/user/authenticate';
      const { data } = await http.post<never, unknown>({
        url,
        config: {},
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.AuthenticateMerchantUserResponse>(url, data, possibleResTypes);
    },
    fetchCrossBorderCashPickupStates: async (params, source) => {
      const url = '/gcm/cash-pickup/states';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const res = verifyResponseType<types.CrossBorderCashPickupStatesResponse>(url, data, possibleResTypes);
      return {
        ...res,
        CashPickupStates: Object.values(res.CashPickupStates || {}),
      };
    },
    fetchCrossBorderCashPickupCities: async (params, source) => {
      const url = '/gcm/cash-pickup/cities';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const res = verifyResponseType<types.CrossBorderCashPickupCitiesResponse>(url, data, possibleResTypes);
      return {
        ...res,
        CashPickupCities: Object.values(res.CashPickupCities || {}),
      };
    },
    fetchCrossBorderCashPickupLocations: async (params, source) => {
      const url = '/gcm/cash-pickup/locations';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const res = verifyResponseType<types.CrossBorderCashPickupLocationsResponse>(url, data, possibleResTypes);
      return {
        ...res,
        CashPickupLocations: Object.values(res.CashPickupLocations || {}),
      };
    },
    fetchPartnerSigningAuthorityInfo: async (bearer, source) => {
      const url = '/partner/submit-extended-info/signing-authority-info';

      const { data } = await http.get<unknown>({
        url,
        config: {},
        source,
        bearer,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      const res = verifyResponseType<types.PartnerSigningAuthorityInfoApiResponse>(url, data, possibleResTypes);
      return {
        ...res,
        SigningAuthority: Object.values(res.SigningAuthority || {}),
      };
    },
    postAddPartnerSigner: async (params, bearer, source) => {
      const url = `/partner/submit-extended-info/signing-authority-info`;
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.AddSignerResponse>(url, data, possibleResTypes);
    },
    postDeletePartnerSigner: async (params, bearer, source) => {
      const url = '/partner/submit-extended-info/delete-signing-authority';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.DeleteSignerResponse>(url, data, possibleResTypes);
    },
    postEditPartnerSigner: async (params, bearer, source) => {
      const url = `/partner/submit-extended-info/signing-authority-info/${params?.SigningAuthorityID || ''}`;
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.EditSignerResponse>(url, data, possibleResTypes);
    },
    fetchGLCodes: async (source) => {
      const url = '/account/transactions/gl-codes';
      const { data } = await http.get<unknown, types.GLCodesApiResponse>({
        url,
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const res = verifyResponseType<types.GLCodesApiResponse>(url, data, possibleResTypes);
      return {
        ...res,
        GLCodes: Object.values(res.GLCodes || {}),
      };
    },
    fetchTransactionCodes(source) {
      const url = '/account/transactions/codes';
      return http
        .get<unknown, types.TransactionCodesApiResponse>({
          url,
          source,
        })
        .then((data) => {
          const res = data.data;

          return {
            ...res,
            TransactionCodes: Object.values(res.TransactionCodes || {}),
          };
        });
    },
    editDeliveryMethod: async (params, source?) => {
      const url = '/gcm/delivery-methods/edit';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.ApiResponse>(url, data, possibleResTypes);
    },

    fetchGCMFundingRails: async (source) => {
      const url = '/gcm/withdraw/funding-options';

      const { data } = await http.get<unknown>({
        url,
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.GCMFundingRailsResponse>(url, data, possibleResTypes);
    },
    postLinkPaypal: async (params, bearer, source) => {
      const url = '/paypal/link-recipient';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.LinkPaypalResponse>(url, data, possibleResTypes);
    },
    postLinkVenmo: async (params, bearer, source) => {
      const url = '/venmo/link-recipient';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.LinkVenmoResponse>(url, data, possibleResTypes);
    },

    fetchGlobalSendAdminDeliveryChannels: async () => {
      const url = '/gcm/partner/delivery-channels';

      const { data } = await http.get<Record<never, never>, unknown>({
        url,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.GlobalSendDeliveryChannelsResponse>(url, data, possibleResTypes);
    },
    publishGlobalSendAdminDeliveryChannels: async (params) => {
      const url = '/gcm/partner/delivery-channels';

      const { data } = await http.post<Record<never, never>, unknown>({
        url,
        config: {},
        body: JSON.stringify({ ...params, DeliveryChannelsData: JSON.stringify(params.DeliveryChannelsData) }),
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.ApiResponse>(url, data, possibleResTypes);
    },

    fetchGlobalSendAdminFundingOptions: async () => {
      const url = 'gcm/partner/funding-options';

      const { data } = await http.get<Record<never, never>, unknown>({
        url,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.FetchGlobalSendAdminFundingOptionsResponse>(url, data, possibleResTypes);
    },

    setMyGooglePay: async (params, source) => {
      const url = '/google-pay/set-my-google-pay';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.SetMyGooglePayResponse>(url, data, possibleResTypes);
    },

    postChargeGooglePay: async (params, source) => {
      const url = '/google-pay/charge';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.ChargeGooglePayResponse>(url, data, possibleResTypes);
    },
    submitGlobalSendAdminFundingOptions: async ({ FundingOptions }, source) => {
      const url = 'gcm/partner/funding-options';

      const { data } = await http.post<Record<never, never>, unknown>({
        url,
        config: {},
        body: JSON.stringify({
          // I'm doing the join here to enforce valid TS types
          FundingOptions: FundingOptions.join(','),
        }),
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.FetchGlobalSendAdminFundingOptionsResponse>(url, data, possibleResTypes);
    },
    fetchBillPaymentAccountMerchants: async (params, source) => {
      const url = '/bill-pay/account/merchants';

      const { data } = await http.get<typeof params, types.BillPaymentAccountMerchantsApiResponse>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      const res = verifyResponseType<types.BillPaymentAccountMerchantsApiResponse>(url, data, possibleResTypes);

      return {
        ...res,
        AccountBillPayMerchants: Object.values(res.AccountBillPayMerchants || {}),
      };
    },
    fetchBillPaymentMerchants: async (params, source) => {
      const url = '/bill-pay/merchants';

      const { data } = await http.get<typeof params, types.BillPaymentMerchantsApiResponse>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      const res = verifyResponseType<types.BillPaymentMerchantsApiResponse>(url, data, possibleResTypes);
      return {
        ...res,
        Merchants: Object.values(res.Merchants || {}),
      };
    },
    createBillPayment: async (params, source) => {
      const url = '/bill-pay/create';

      const { data } = await http.post<typeof params, types.BillPayCreateResponse>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.BillPayCreateResponse>(url, data, possibleResTypes);
    },
    addBillPaymentMerchant: async (params, source) => {
      const url = '/bill-pay/account/merchants/add';

      const { data } = await http.post<typeof params, types.BillPayAccountMerchantAddResponse>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.BillPayAccountMerchantAddResponse>(url, data, possibleResTypes);
    },
    editBillPaymentMerchant: async (params, source) => {
      const url = '/bill-pay/account/merchants/edit';

      const { data } = await http.post<typeof params, types.BillPayAccountMerchantEditResponse>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.BillPayAccountMerchantEditResponse>(url, data, possibleResTypes);
    },
    fetchDisputeManagementOverview: async (params, source) => {
      const url = '/dispute-management/report/dispute-overview';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const res = verifyResponseType<types.DisputeManagementOverviewResponse>(url, data, possibleResTypes);

      return res;
    },
    fetchDisputeManagementList: async (params, source) => {
      const url = '/dispute-management/list';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      const res = verifyResponseType<types.DisputeManagementListApiResponse>(url, data, possibleResTypes);
      return {
        ...res,
        Disputes: Object.values(res.Disputes || {}),
      };
    },
    fetchInteracAutoDepositStatus: async (params, bearer, source) => {
      const url = '/interac/auto-deposit';

      const { data } = await http.get<typeof params, types.InteracAutoDepositStatusResponse>({
        url,
        config: { params },
        source,
        bearer,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.InteracAutoDepositStatusResponse>(url, data, possibleResTypes);
    },
    fetchDisputeStages: async (params, source) => {
      const url = '/dispute-management/report/dispute-stages';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const res = verifyResponseType<types.DisputeStagesApiResponse>(url, data, possibleResTypes);
      return {
        ...res,
        DisputeStages: Object.values(res.DisputeStages || {}),
      };
    },
    fetchDisputeDetails: async (params, source) => {
      const url = '/dispute-management/detail';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const res = verifyResponseType<types.DisputeDetailsResponse>(url, data, possibleResTypes);

      return {
        ...res,
        Dispute: {
          ...res.Dispute,
          Documents: Object.values(res.Dispute.Documents || {}),
          Notes: Object.values(res.Dispute.Notes || {}),
        },
      };
    },
    fetchDisputeDocuments: async (params, source) => {
      const url = '/dispute-management/document';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const res = verifyResponseType<types.DisputeDocumentsResponse>(url, data, possibleResTypes);
      return res;
    },
    respondToDispute: async (params, source) => {
      const url = '/dispute-management/respond';

      const { data } = await http.post<typeof params, unknown>({
        url,
        body: JSON.stringify(params),
        config: {},
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.RespondAndChallengeDisputeResponse>(url, data, possibleResTypes);
    },
    challengeDispute: async (params, source) => {
      const url = '/dispute-management/challenge';

      const { data } = await http.post<typeof params, unknown>({
        url,
        body: JSON.stringify(params),
        config: {},
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.RespondAndChallengeDisputeResponse>(url, data, possibleResTypes);
    },
    addDisputeNote: async (params, source) => {
      const url = '/dispute-management/add-note';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.AddDisputeNoteResponse>(url, data, possibleResTypes);
    },
    acceptDispute: async (params, source) => {
      const url = '/dispute-management/accept';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.AcceptDisputeResponse>(url, data, possibleResTypes);
    },
    fetchDisputeReasons: async (params, source) => {
      const url = '/dispute-management/report/dispute-reasons';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.DisputeReasonsResponse>(url, data, possibleResTypes);
    },
    fetchConvenienceFees: async (params, source) => {
      const url = '/account/convenience-fees';

      const { data } = await http.get<undefined, types.ConvenienceFeesResponseApi>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      const response = verifyResponseType<types.ConvenienceFeesResponseApi>(url, data, possibleResTypes);
      return {
        ...response,
        FeeSettings: Object.values(response.FeeSettings || {}),
        Restrictions: Object.values(response.Restrictions || {}),
      };
    },
    fetchGlobalSendTransactionDetails: async (params, source) => {
      const url = '/gcm/withdraw/transaction';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.GlobalSendTransactionDetailsResponse>(url, data, possibleResTypes);
    },
    generateBillPayEmbedUrl: async (params, source) => {
      const url = '/bill-pay/generate-embed-url';

      const { data } = await http.post<typeof params, types.BillPayGenerateEmbedUrlResponse>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.BillPayGenerateEmbedUrlResponse>(url, data, possibleResTypes);
    },
    fetchBillPayTransaction: async (params, bearer, source) => {
      const url = '/bill-pay/transaction';

      const { data } = await http.get<typeof params, types.BillPayTransactionResponse>({
        url,
        config: { params },
        source,
        bearer,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.BillPayTransactionResponse>(url, data, possibleResTypes);
    },
    fetchMobileWalletProviders: async (params, source) => {
      const url = '/gcm/mobile-wallets/providers';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const res = verifyResponseType<types.MobileWalletProvidersApiResponse>(url, data, possibleResTypes);

      return {
        ...res,
        MobileWalletProviders: Object.values(res.MobileWalletProviders || {}),
      };
    },
    fetchGooglePayChargeTransaction: async (params, bearer, source) => {
      const url = '/google-pay/charge/transaction';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.GooglePayChargeTransactionResponse>(url, data, possibleResTypes);
    },
    fetchPayPalWithdrawTransaction: async (params, bearer, source) => {
      const url = '/paypal/withdraw/transaction';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.PayPalWithdrawTransactionResponse>(url, data, possibleResTypes);
    },
    fetchVenmoWithdrawTransaction: async (params, bearer, source) => {
      const url = '/venmo/withdraw/transaction';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.VenmoWithdrawTransactionResponse>(url, data, possibleResTypes);
    },
    postVenmoWithdraw: async (params, source) => {
      const url = '/venmo/withdraw';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.VenmoWithdrawResponse>(url, data, possibleResTypes);
    },
    postPayPalWithdraw: async (params, source) => {
      const url = '/paypal/withdraw';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.PayPalWithdrawResponse>(url, data, possibleResTypes);
    },
    fetchVenmoRecipients: async (params, source) => {
      const url = '/venmo/recipients';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const resp = verifyResponseType<types.VenmoRecipientsApiResponse>(url, data, possibleResTypes);
      return {
        ...resp,
        VenmoRecipients: Object.values(resp.VenmoRecipients || {}).map((recipient) => ({
          ...recipient,
          IsDefault: toBool(recipient.IsDefault || '0'),
        })),
      };
    },
    fetchPayPalRecipients: async (params, source) => {
      const url = '/paypal/recipients';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const resp = verifyResponseType<types.PayPalRecipientsApiResponse>(url, data, possibleResTypes);
      return {
        ...resp,
        PaypalRecipients: Object.values(resp.PaypalRecipients || {}).map((recipient) => ({
          ...recipient,
          IsDefault: toBool(recipient.IsDefault || '0'),
        })),
      };
    },
    fetchPayPalDefaultRecipient: async (params, source) => {
      const url = '/paypal/default-recipient';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const resp = verifyResponseType<types.PayPalDefaultRecipientApiResponse>(url, data, possibleResTypes);
      const recipients = Object.values(resp.DefaultPayPalRecipient || {});
      return {
        ...resp,
        DefaultPayPalRecipient:
          recipients.length > 0
            ? {
                ...recipients[0],
                IsActive: toBool(recipients[0].IsActive || '0'),
                IsDefault: toBool(recipients[0].IsDefault || '0'),
              }
            : null,
      };
    },
    fetchVenmoDefaultRecipient: async (params, source) => {
      const url = '/venmo/default-recipient';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const resp = verifyResponseType<types.VenmoDefaultRecipientApiResponse>(url, data, possibleResTypes);
      const recipients = Object.values(resp.DefaultVenmoRecipient || {});
      return {
        ...resp,
        DefaultVenmoRecipient:
          recipients.length > 0
            ? {
                ...recipients[0],
                IsActive: toBool(recipients[0].IsActive || '0'),
                IsDefault: toBool(recipients[0].IsDefault || '0'),
              }
            : null,
      };
    },
    AFTPull: async (params, source) => {
      const url = '/debit-card/aft/pull-funds';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.AFTPullResponse>(url, data, possibleResTypes);
    },
    OCTPush: async (params, source) => {
      const url = '/debit-card/oct/push-funds';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.OCTPushResponse>(url, data, possibleResTypes);
    },
    fetchSubAccountPermissions: async (params, source) => {
      const url = '/account/subaccount/permissions';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const resp = verifyResponseType<types.SubAccountPermissionApiResponse>(url, data, possibleResTypes);
      return {
        ...resp,
        PermissionValues: Object.values(resp.PermissionValues || {}).map((permission) => ({
          ...permission,
          ExtraFields: {
            ...permission.ExtraFields,
            EnumValues: permission.Type === 'enum' ? Object.values(permission.ExtraFields.EnumValues || {}) : undefined,
          },
        })),
      };
    },
    saveAccountExtendedInfo: async (params, bearer, source) => {
      const url = '/account/submit-extended-info/save';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.ApiResponse>(url, data, possibleResTypes);
    },
    savePartnerExtendedInfo: async (params, bearer, source) => {
      const url = '/partner/submit-extended-info';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.ApiResponse>(url, data, possibleResTypes);
    },
    fetchPartnerExtendedInfo: async (bearer, source) => {
      const url = '/partner/extended-info';
      const { data } = await http.get<unknown>({
        url,
        source,
        bearer,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.PartnerExtendedInfoResponse>(url, data, possibleResTypes);
    },
    uploadDocument: async (params, source?) => {
      const url = '/document';

      const { data } = await http.post<typeof params, unknown>({
        url,
        body: JSON.stringify(params),
        config: {},
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.DocumentUploadResponse];

      return verifyResponseType<types.DocumentUploadResponse>(url, data, possibleResTypes);
    },
    blockAccounts: async (params, source) => {
      const url = '/account/blocked-account/block';

      const { data } = await http.post<types.BlockAccountsRequest, unknown>({
        url,
        config: {},
        body: JSON.stringify(params),
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.ApiResponse>(url, data, possibleResTypes);
    },
    unblockAccounts: async (params, source) => {
      const url = '/account/blocked-account/unblock';

      const { data } = await http.post<types.BlockAccountsRequest, unknown>({
        url,
        config: {},
        body: JSON.stringify(params),
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<types.ApiResponse>(url, data, possibleResTypes);
    },
    fetchDocument: async (params, bearer?: string, source?) => {
      const url = `/document/${params.DocumentID}`;
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
        ...(bearer ? { bearer } : {}),
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.FetchDocumentResponse>(url, data, possibleResTypes);
    },
    fetchAccountTransfer: async (params, bearer, source) => {
      const url = '/account/transfer';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<types.AccountTransferResponse>(url, data, possibleResTypes);
    },
    fetchTransactionHistory: async (params, bearer, source) => {
      const url = '/account/transaction/history';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        bearer,
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const res = verifyResponseType<types.TransactionHistoryApiResponse>(url, data, possibleResTypes);
      return {
        ...res,
        TransactionHistory: Object.values(res.TransactionHistory || {}),
      };
    },
  };
  function withApiComponent<P>(C: FunctionComponent<Omit<P, 'api'> & { api: Api }>): FunctionComponent<Omit<P, 'api'>> {
    return (props: Omit<P, 'api'>) => C({ ...props, api });
  }

  function withApiHook<P, R>(Hook: (hookProps: Omit<P, 'api'> & { api: Api }) => R): (props: Omit<P, 'api'>) => R {
    return (props: Omit<P, 'api'>) => Hook({ ...props, api });
  }

  return {
    api,
    withApiComponent,
    withApiHook,
  };
};

export default createVoPayApi;

export type Api = ContactVerificationApi &
  AddressApi & {
    fetchAccountTranslationsUrl: (
      params: types.FetchTranslationsUrlRequest,
      options?: {
        bearer?: string;
        source?: CancelTokenSource;
      },
    ) => Promise<types.FetchTranslationsUrlResponse>;
    generateEmployeeOTP: (
      body: types.GenerateEmployeeOTPRequest,
      source?: CancelTokenSource,
    ) => Promise<types.GenerateEmployeeOTPResponse>;
    validateEmployeeOTP: (
      body: types.ValidateEmployeeOTPRequest,
      source?: CancelTokenSource,
    ) => Promise<types.ValidateEmployeeOTPResponse>;
    fetchDomain: (source?: CancelTokenSource) => Promise<types.FetchDomainResponse>;
    convertFile: (
      params: types.FileConversionRequest,
      source?: CancelTokenSource,
    ) => Promise<types.FileConversionResponse>;
    getFileConversionConfigurations: (
      source?: CancelTokenSource,
    ) => Promise<types.FileConversionConfigurationsResponse>;
    getFileConversionHistory: (
      params: types.FileConversionHistoryRequest,
      source?: CancelTokenSource,
    ) => Promise<types.FileConversionHistoryResponse>;
    downloadFileConversion: (
      params: types.FileConversionDownloadRequest,
      source?: CancelTokenSource,
    ) => Promise<types.FileConversionDownloadResponse>;
    downloadSourceFileConversion: (
      params: types.FileConversionDownloadSourceRequest,
      source?: CancelTokenSource,
    ) => Promise<types.FileConversionDownloadSourceResponse>;
    submitFileConversion: (
      params: types.FileConversionSubmitRequest,
      source?: CancelTokenSource,
    ) => Promise<types.FileConversionSubmitResponse>;
    uploadFileConversion: (
      params: types.FileConversionUploadRequest,
      source?: CancelTokenSource,
    ) => Promise<types.FileConversionUploadResponse>;
    getFileConversionPreprocessingConfigs: (
      params: types.FileConversionPreprocessingConfigsRequest,
      source?: CancelTokenSource,
    ) => Promise<types.FileConversionPreprocessingConfigsResponse>;
    deleteDomain: (
      params: types.DeleteDomainRequest,
      source?: CancelTokenSource,
    ) => Promise<types.DeleteDomainResponse>;
    addDomain: (params: types.AddDomainRequest, source?: CancelTokenSource) => Promise<types.AddDomainResponse>;
    verifyDomain: (
      params: types.VerifyDomainRequest,
      source?: CancelTokenSource,
    ) => Promise<types.VerifyDomainResponse>;
    linkGooglePay: (
      params: types.LinkGooglePayRequest,
      source?: CancelTokenSource,
    ) => Promise<types.LinkGooglePayResponse>;

    fetchAccountBillingSettings: (
      params: { VopayAccountID: string },
      source?: CancelTokenSource,
    ) => Promise<types.AccountBillingSettingsResponse>;

    fetchAccountTransactions: (
      params: types.AccountTransactionsRequest,
      bearer: string,
      source?: CancelTokenSource,
    ) => Promise<types.AccountTransactions>;

    fetchAccountUsers: (
      params: types.TimezoneRequest,
      bearer?: string,
      source?: CancelTokenSource,
    ) => Promise<types.FetchAccountUsersResponse>;

    fetchBusinessTypes: (source?: CancelTokenSource) => Promise<types.BusinessTypesResponse>;

    fetchBillingPackages: (source?: CancelTokenSource) => Promise<types.PartnerBillingPackagesResponse>;

    fetchBillingPackageDetails: (
      params: types.PartnerBillingPackageDetailsRequest,
      source?: CancelTokenSource,
    ) => Promise<types.PartnerBillingPackageDetailsResponse>;

    fetchBillingSettingsDetails: (
      params: types.PartnerBillingSettingsDetailsRequest,
      source?: CancelTokenSource,
    ) => Promise<types.PartnerBillingSettingsDetailsResponse>;

    fetchCreditCardTransaction: (
      params: types.TransactionId,
      bearer?: string,
      source?: CancelTokenSource,
    ) => Promise<types.CreditCardTransactionResponse>;

    fetchEFTFundTransaction: (
      params: types.TransactionRequest,
      bearer?: string,
      source?: CancelTokenSource,
    ) => Promise<types.EftFundingTransactionResponse>;

    fetchEFTWithdrawTransaction: (
      params: types.TransactionRequest,
      bearer?: string,
      source?: CancelTokenSource,
    ) => Promise<types.EftWithdrawTransactionResponse>;
    fetchFinancialInstitutionBranches: (
      params: types.FinancialInstitutionBranchesRequest,
      source?: CancelTokenSource,
    ) => Promise<types.FinancialInstitutionBranchesResponse>;
    fetchFinancialInstitutions: (
      params: types.FinancialInstitutionsRequest,
      source?: CancelTokenSource,
    ) => Promise<types.FinancialInstitutionsResponse>;
    fetchGlobalPayToken: (
      params: types.GlobalPayTokenRequest,
      bearer: string | undefined,
      source?: CancelTokenSource,
    ) => Promise<types.GlobalPayTokenResponse>;
    fetchInteracBulkPayoutTransaction: (
      params: types.TransactionId,
      bearer: string,
      source?: CancelTokenSource,
    ) => Promise<types.InteracBulkPayoutTransactionResponse>;

    fetchInteracMoneyRequestTransaction: (
      params: types.TransactionId,
      bearer: string,
      source?: CancelTokenSource,
    ) => Promise<types.InteracTransactionResponse>;

    fetchRTRWithdrawTransaction: (
      params: types.TransactionId,
      bearer: string,
      source?: CancelTokenSource,
    ) => Promise<types.RTRWithdrawTransactionResponse>;

    fetchFedwireFundTransaction: (
      params: types.TransactionRequest,
      bearer: string,
      source?: CancelTokenSource,
    ) => Promise<types.FedwireTransactionResponse>;
    fetchFedwireWithdrawTransaction: (
      params: types.TransactionRequest,
      bearer: string,
      source?: CancelTokenSource,
    ) => Promise<types.FedwireTransactionResponse>;
    fetchRTPWithdrawTransaction: (
      params: types.TransactionId,
      bearer: string,
      source?: CancelTokenSource,
    ) => Promise<types.RTPWithdrawTransactionResponse>;

    fetchiQ11TokenInfo: (
      params: types.TokenInfoRequest,
      source?: CancelTokenSource,
    ) => Promise<types.TokenInfoResponse>;
    fetchLeadsAgentId: (params: types.LeadsAgentRequest, source?: CancelTokenSource) => Promise<types.LeadsResponse>;
    fetchLeadsPartnerId: (
      params: types.LeadsParentRequest,
      source?: CancelTokenSource,
    ) => Promise<types.PartnerBillingPackagesResponse>;
    fetchLeadsDetails: (params: types.LeadsDetailsRequest, source?: CancelTokenSource) => Promise<types.LeadsResponse>;

    fetchPartnerAccounts: (
      params: types.PartnerAccountsRequest,
      source?: CancelTokenSource,
    ) => Promise<types.PartnerAccounts>;

    fetchPartnerAccountSummary: (
      params: types.PartnerAccountSummaryRequest,
      source?: CancelTokenSource,
    ) => Promise<types.PartnerAccountSummaryResponse>;

    fetchPartnerAccountSummaryDetails: (
      params: types.PartnerAccountSummaryDetailsRequest,
      source?: CancelTokenSource,
    ) => Promise<types.PartnerAccountSummaryDetailsResponse>;

    fetchPartnerInvoiceDetails: (
      params: types.PartnerInvoiceDetailsRequest,
      source?: CancelTokenSource,
    ) => Promise<types.PartnerInvoiceDetailsResponse>;

    fetchPartnerInvoicePdf: (
      params: types.InvoicePdfRequest,
      source?: CancelTokenSource,
    ) => Promise<types.InvoicePdfResponse>;

    fetchPartnerInvoices: (
      params: types.InvoicesRequest,
      source?: CancelTokenSource,
    ) => Promise<types.PartnerInvoicesResponse>;

    fetchPartnerStatementCsv: (
      params: types.StatementCsvRequest,
      source?: CancelTokenSource,
    ) => Promise<types.StatementCsvResponse>;
    fetchPartnerStatementPdf: (
      params: types.PartnerStatementPdfRequest,
      source?: CancelTokenSource,
    ) => Promise<types.PartnerStatementPdfResponse>;

    fetchPartnerStatistics: (source?: CancelTokenSource) => Promise<types.PartnerStatisticsResponse>;

    fetchPartners: (source?: CancelTokenSource) => Promise<types.PartnersResponse>;

    fetchUserLists: (params: types.UserListsRequest, source?: CancelTokenSource) => Promise<types.UserListsResponse>;

    fetchSingleUseToken: (
      params: types.SingleUseTokenRequest,
      source?: CancelTokenSource,
    ) => Promise<types.SingleUseTokenResponse>;

    fetchOnboardingAppUrl: (
      params: types.OnboardingAppUrlRequest,
      source?: CancelTokenSource,
    ) => Promise<types.OnboardingAppUrlResponse>;

    fetchAccountsOverviewActive: (source?: CancelTokenSource) => Promise<types.PartnerStatisticsDataResponse>;
    fetchTransactionsOverview: (source?: CancelTokenSource) => Promise<types.PartnerStatisticsDataResponse>;
    fetchPerformanceOverview: (source?: CancelTokenSource) => Promise<types.PartnerStatisticsDataResponse>;
    fetchUserDataOverview: (source?: CancelTokenSource) => Promise<types.UserDataOverviewResponse>;
    fetchActivityOverview: (
      params: types.ActivityOverviewRequest,
      source?: CancelTokenSource,
    ) => Promise<types.ActivityOverviewResponse>;

    fetchPartnerAccountUsers: (source?: CancelTokenSource) => Promise<types.FetchPartnerAccountUsersResponse>;

    fetchCreditCards: (
      params: types.CreditCardRequest,
      bearer?: string,
      source?: CancelTokenSource,
    ) => Promise<types.CreditCardResponse>;

    fetchBankAccounts: (
      params: types.BankAccountsRequest,
      bearer: string,
      source?: CancelTokenSource,
    ) => Promise<types.BankAccountsResponse>;

    fetchDebitCards: (
      params: types.GetDebitCardsRequest,
      source?: CancelTokenSource,
    ) => Promise<types.GetDebitCardsResponse>;

    fetchPaylinkSingleUseToken: (
      params: types.ELinxSingleUseTokenRequest,
      source?: CancelTokenSource,
    ) => Promise<types.ELinxSingleUseTokenResponse>;
    fetchAccountUserPermissions: (
      params: types.AccountUserPermissionsRequest,
      bearer?: string,
      source?: CancelTokenSource,
    ) => Promise<types.AccountUserPermissionsResponse>;

    fetchAgreementDetails: (bearer: string, source?: CancelTokenSource) => Promise<types.AgreementDetailsResponse>;
    fetchAgreementDownload: (
      params: types.AgreementDownloadRequest,
      bearer: string,
      source?: CancelTokenSource,
    ) => Promise<types.AgreementDownloadResponse>;
    fetchSigningAuthorityInfo: (
      bearer: string,
      source?: CancelTokenSource,
    ) => Promise<types.SigningAuthorityInfoResponse>;

    fetchAccountLimits: (
      params: types.AccountLimitsRequest,
      source?: CancelTokenSource,
    ) => Promise<types.AccountLimitsResponse>;

    fetchBankHolidays: (source?: CancelTokenSource) => Promise<types.BankHolidaysResponse>;

    fetchClientBankAccounts: (
      params: types.ClientBankAccountsRequest,
      source?: CancelTokenSource,
    ) => Promise<types.ClientBankAccountsResponse>;

    fetchClientCreditCards: (
      params: types.ClientCreditCardsRequest,
      bearer?: string,
      source?: CancelTokenSource,
    ) => Promise<types.ClientCreditCardsResponse>;

    fetchClientDebitCards: (
      params: types.ClientDebitCardsRequest,
      source?: CancelTokenSource,
    ) => Promise<types.ClientDebitCardsResponse>;

    fetchClientTransactions: (
      params: types.ClientTransactionsRequest,
      source?: CancelTokenSource,
    ) => Promise<types.ClientTransactionsResponse>;

    fetchClientBalance: (
      params: types.ClientBalanceRequest,
      source?: CancelTokenSource,
    ) => Promise<types.ClientBalanceResponse>;

    fetchAccountPermissions: (
      params: types.AccountPermissionsRequest,
      source?: CancelTokenSource,
    ) => Promise<types.AccountPermissionsResponse>;

    fetchAutoBalanceTransfer: (
      params: types.AutoBalanceTransferRequest,
      source?: CancelTokenSource,
    ) => Promise<types.AutoBalanceTransferResponse>;

    fetchSubAccounts: (
      params: types.SubAccountsRequest,
      source?: CancelTokenSource,
    ) => Promise<types.SubAccountsResponse>;

    fetchContactGroups: (
      params: types.TimezoneRequest,
      source?: CancelTokenSource,
    ) => Promise<types.ContactGroupsResponse>;

    fetchGroupContact: (
      params: types.GroupContactRequest,
      source?: CancelTokenSource,
    ) => Promise<types.GroupContactResponse>;

    fetchScheduledTransactions: (
      params: types.ScheduledTransactionsRequest,
      source?: CancelTokenSource,
    ) => Promise<types.ScheduledTransactionsResponse>;

    fetchInboundETransferTransaction: (
      params: types.InboundETransferTransactionRequest,
      bearer?: string,
      source?: CancelTokenSource,
    ) => Promise<types.InboundETransferTransactionResponse>;

    fetchVisaDirectPushTransaction: (
      params: types.TransactionId,
      bearer?: string,
      source?: CancelTokenSource,
    ) => Promise<types.VisaDirectPushTransactionResponse>;

    fetchCurrencyConversionTransaction: (
      params: types.CurrencyConversionTransactionRequest,
      bearer?: string,
      source?: CancelTokenSource,
    ) => Promise<types.CurrencyConversionTransactionResponse>;

    fetchClientAccountList: (
      params: types.ClientAccountListRequest,
      source?: CancelTokenSource,
    ) => Promise<types.ClientAccountListResponse>;

    fetchClientAccountBalanceRange: (source?: CancelTokenSource) => Promise<types.ClientAccountBalanceRangeResponse>;

    fetchClientAccountPaymentMethods: (
      params: types.ClientAccountPaymentMethodRequest,
      source?: CancelTokenSource,
    ) => Promise<types.PaymentMethodResponse>;

    fetchContactPaymentMethods: (
      params: types.ContactPaymentMethodRequest,
      source?: CancelTokenSource,
    ) => Promise<types.PaymentMethodResponse>;

    fetchClientBusinessTypes: (source?: CancelTokenSource) => Promise<types.BusinessTypesResponse>;

    fetchClientAccountBalance: (
      params: types.ClientAccountBalanceRequest,
      source?: CancelTokenSource,
    ) => Promise<types.ClientAccountBalanceResponse>;

    fetchDefaultBankAccount: (
      params: types.DefaultBankAccountRequest,
      source?: CancelTokenSource,
    ) => Promise<types.DefaultBankAccountResponse>;

    fetchPaasTransactions: (
      params: types.PaasTransactionsRequest,
      source?: CancelTokenSource,
    ) => Promise<types.PaasTransactionsResponse>;

    fetchBatchFileTransactions: (
      params: types.BatchFileTransactionsRequest,
      source?: CancelTokenSource,
    ) => Promise<types.BatchFileTransactionsResponse>;

    fetchInteracEmail: (
      params: types.InteracEmailRequest,
      source?: CancelTokenSource,
    ) => Promise<types.InteracEmailResponse>;
    fetchClientAccounts: (
      params: types.ClientAccountsRequest,
      bearer?: string,
      source?: CancelTokenSource,
    ) => Promise<types.ClientAccountsResponse>;

    fetchSendLegalAgreement: (bearer?: string, source?: CancelTokenSource) => Promise<types.SendLegalAgreementResponse>;

    fetchAccountVerification: (
      params: types.AccountVerificationRequest,
      source?: CancelTokenSource,
    ) => Promise<types.AccountVerificationResponse>;

    postVoidAgreement: (
      params: types.VoidAgreementRequest,
      bearer: string,
      source?: CancelTokenSource,
    ) => Promise<types.ApiResponse>;
    postDeleteSigner: (
      params: types.DeleteSignerRequest,
      bearer: string,
      source?: CancelTokenSource,
    ) => Promise<types.DeleteSignerResponse>;
    postAddSigner: (
      params: types.AddSignerRequest,
      bearer: string,
      source?: CancelTokenSource,
    ) => Promise<types.AddSignerResponse>;
    postEditSigner: (
      params: types.EditSignerRequest,
      bearer: string,
      source?: CancelTokenSource,
    ) => Promise<types.EditSignerResponse>;

    postSendOnboardingEmail: (
      params: types.SendOnboardingEmailRequest,
      source?: CancelTokenSource,
    ) => Promise<types.ApiResponse>;

    postCreateUser: (params: types.CreateUserRequest, source?: CancelTokenSource) => Promise<types.CreateUserResponse>;
    postDeleteUser: (params: types.DeleteUserRequest, source?: CancelTokenSource) => Promise<types.DeleteUserResponse>;

    postUserChangePassword: (
      params: types.UserChangePasswordRequest,
      source?: CancelTokenSource,
    ) => Promise<types.UserChangePasswordResponse>;

    postUserChangeRole: (params: types.UserChangeRoleRequest, source?: CancelTokenSource) => Promise<types.ApiResponse>;

    generateSingleUseToken: (
      params: types.SingleUseTokenRequest,
      source?: CancelTokenSource,
    ) => Promise<types.ApiResponse>;

    postAccountUsersChangePassword: (
      params: types.AccountUsersChangePasswordRequest,
      bearer?: string,
      source?: CancelTokenSource,
    ) => Promise<types.AccountUsersChangePasswordResponse>;

    postCreateAccountUser: (
      params: types.CreateAccountUserRequest,
      bearer?: string,
      source?: CancelTokenSource,
    ) => Promise<types.CreateAccountUserResponse>;
    postCreateLead: (params: types.CreateLeadRequest, source?: CancelTokenSource) => Promise<types.ApiResponse>;
    postDeleteAccountUser: (
      params: types.DeleteAccountUserRequest,
      bearer?: string,
      source?: CancelTokenSource,
    ) => Promise<types.DeleteAccountUserResponse>;
    postIFrameEvent: (params: types.IFrameEventRequest, source?: CancelTokenSource) => Promise<types.ApiResponse>;
    postIQ11Complete: (
      params: types.IQ11CompleteRequest,
      source?: CancelTokenSource,
    ) => Promise<types.IQ11TokenResponse>;
    postLinkBankAccountManually: (
      params: types.LinkBankAccountManuallyRequest,
      source?: CancelTokenSource,
    ) => Promise<types.IQ11TokenResponse>;
    postLinkCreditCard: (
      params: types.LinkCreditCardRequest,
      source?: CancelTokenSource,
    ) => Promise<types.IQ11TokenResponse>;

    postLinkPushToCard: (
      params: types.LinkPushToCardRequest,
      source?: CancelTokenSource,
    ) => Promise<types.IQ11TokenResponse>;

    postPartnerAccountBusinessCase: (
      params: types.PartnerAccountBusinessCaseRequest,
      source?: CancelTokenSource,
    ) => Promise<types.ApiResponse>;

    postPartnerAccountMatrix: (
      params: types.PartnerAccountMatrixRequest,
      source?: CancelTokenSource,
    ) => Promise<types.ApiResponse>;

    postPartnerAccount: (
      params: types.PartnerAccountRequest,
      source?: CancelTokenSource,
      overrideAuth?: { AccountID: string; Key: string; Signature: string },
    ) => Promise<types.PartnerAccountCreationResponse | types.PartnerAccountCreationLinkResponse | types.ApiResponse>;

    postPartnerAccountPermissions: (
      params: types.AccountPermissionRequest,
      source?: CancelTokenSource,
    ) => Promise<types.ApiResponse>;

    postAccountUserPermissions: (
      params: types.AccountUserPermissionsPostRequest,
      bearer?: string,
      source?: CancelTokenSource,
    ) => Promise<types.ApiResponse>;

    postPartnerAccountUsersChangePassword: (
      params: types.PartnerAccountUsersChangePasswordRequest,
      source?: CancelTokenSource,
    ) => Promise<types.PartnerAccountUsersChangePasswordResponse>;

    postPartnerCustomPackage: (
      params: types.PartnerCustomPackageRequest,
      source?: CancelTokenSource,
    ) => Promise<types.PartnerCustomPackageResponse>;

    postPaylinkDecline: (params: types.ELinxDeclineRequest, source?: CancelTokenSource) => Promise<types.ApiResponse>;
    postPaylinkSetAccount: (
      params: types.ELinxSetAccountRequest,
      source?: CancelTokenSource,
    ) => Promise<types.ApiResponse>;

    postPortalPartnerLogin: (params: types.Login, source?: CancelTokenSource) => Promise<types.AuthorizationResponse>;
    postPartnerAuthPing: (source?: CancelTokenSource) => Promise<types.ApiResponse>;

    postIQ11Login: (params: types.IQ11Login, source?: CancelTokenSource) => Promise<types.IQ11AuthorizationResponse>;
    postIQ11AuthPing: (bearer: string, source?: CancelTokenSource) => Promise<types.ApiResponse>;

    postIQ11CreditCardEmbedUrl: (
      params: types.IQ11CreditCardEmbedUrlRequest,
      bearer: string,
      source?: CancelTokenSource,
    ) => Promise<types.IQ11EmbedUrlResponse>;
    postIQ11EmbedUrl: (
      params: types.IQ11EmbedUrlRequest,
      bearer: string,
      source?: CancelTokenSource,
    ) => Promise<types.IQ11EmbedUrlResponse>;
    postDeleteCreditCard: (
      params: types.DeleteCreditCardRequest,
      bearer: string,
      source?: CancelTokenSource,
    ) => Promise<types.ApiResponse>;

    postSetMyCreditCard: (
      params: types.SetMyCreditCardRequest,
      bearer: string,
      source?: CancelTokenSource,
    ) => Promise<types.SetMyCreditCardResponse>;

    postDeleteBankAccount: (
      params: types.DeleteBankAccountRequest,
      bearer: string,
      source?: CancelTokenSource,
    ) => Promise<types.ApiResponse>;

    postDeleteBankAccountDocuments: (
      params: types.DeleteBankAccountDocumentsRequest,
      bearer: string,
      source?: CancelTokenSource,
    ) => Promise<types.ApiResponse>;

    postSetMyBankAccount: (
      params: types.SetMyBankAccountRequest,
      bearer: string,
      source?: CancelTokenSource,
    ) => Promise<types.SetMyBankAccountResponse>;
    postSetMyBankAccountWithPurpose: (
      params: types.SetMyBankAccountPurposeRequest,
      bearer: string,
      source?: CancelTokenSource,
    ) => Promise<types.SetMyBankAccountPurposeResponse>;
    fetchMyBankAccount: (
      params: types.MyBankAccountRequest,
      bearer: string,
      source?: CancelTokenSource,
    ) => Promise<types.MyBankAccountResponse>;

    postTransactionRefund: (
      params: types.TransactionsRefundRequest,
      bearer: string,
      source?: CancelTokenSource,
    ) => Promise<types.TransactionsRefundResponse>;

    postTransactionCancel: (
      params: types.TransactionsCancelRequest,
      bearer: string,
      source?: CancelTokenSource,
    ) => Promise<types.TransactionsCancelResponse>;

    postBankAccountConnection: (
      params: types.BankAccountConnectionRequest,
      source?: CancelTokenSource,
    ) => Promise<types.BankAccountConnectionResponse>;

    postLinkBankAccount: (
      params: types.LinkBankAccountRequest,
      source?: CancelTokenSource,
    ) => Promise<types.IQ11TokenResponse>;

    postClientChangePassword: (
      params: types.ClientChangePasswordRequest,
      source?: CancelTokenSource,
    ) => Promise<types.ApiResponse>;

    postPortalClientLogin: (
      params: types.ClientLogin,
      source?: CancelTokenSource,
    ) => Promise<types.ClientAuthorizationResponse>;
    postClientAuthPing: (source?: CancelTokenSource) => Promise<types.ApiResponse>;
    postClientResetPasswordEmail: (
      params: types.ClientResetPasswordEmailRequest,
      source?: CancelTokenSource,
    ) => Promise<types.ClientResetPasswordEmailResponse>;

    postPortalClientLoginHistory: (
      params: types.PortalClientLoginHistoryRequest,
      source?: CancelTokenSource,
    ) => Promise<types.PortalClientLoginHistoryResponse>;
    postVerifyRecaptcha: (
      params: types.VerifyRecaptchaRequest,
      source?: CancelTokenSource,
    ) => Promise<types.VerifyRecaptchaResponse>;

    postPortalGenerateQRCode: (
      params: types.PortalGenerateQRCodeRequest,
      source?: CancelTokenSource,
    ) => Promise<types.PortalGenerateQRCodeResponse>;
    postPortalAuthenticate: (
      params: types.PortalAuthenticateRequest,
      source?: CancelTokenSource,
    ) => Promise<types.PortalAuthenticateResponse>;
    postPortalChangePassword: (
      params: types.PortalChangePasswordRequest,
      source?: CancelTokenSource,
    ) => Promise<types.ApiResponse>;
    postPortalResetPassword: (
      params: types.PortalResetPasswordRequest,
      source?: CancelTokenSource,
    ) => Promise<types.ApiResponse>;

    postAccountBalance: (
      params: types.AccountBalanceRequest,
      source?: CancelTokenSource,
    ) => Promise<types.AccountBalanceResponse>;

    postDeleteClientBankAccount: (
      params: types.DeleteClientBankAccountRequest,
      source?: CancelTokenSource,
    ) => Promise<types.ApiResponse>;
    postDeletePaypalAccount: (
      params: types.DeletePaypalAccountRequest,
      source?: CancelTokenSource,
    ) => Promise<types.ApiResponse>;
    postDeleteVenmoAccount: (
      params: types.DeleteVenmoAccountRequest,
      source?: CancelTokenSource,
    ) => Promise<types.ApiResponse>;
    postSetMyClientBankAccount: (
      params: types.SetMyBankAccountRequest,
      source?: CancelTokenSource,
    ) => Promise<types.SetMyBankAccountResponse>;
    postSetClientMyCreditCard: (
      params: types.SetMyCreditCardRequest,
      source?: CancelTokenSource,
    ) => Promise<types.SetMyCreditCardResponse>;

    postSetClientMyDebitCard: (
      params: types.SetMyDebitCardRequest,
      source?: CancelTokenSource,
    ) => Promise<types.SetMyDebitCardResponse>;

    postSetClientEmail: (
      params: types.SetClientEmailRequest,
      source?: CancelTokenSource,
    ) => Promise<types.SetClientEmailResponse>;

    postSetClientGooglePay: (
      params: types.SetMyGooglePayRequest,
      source?: CancelTokenSource,
    ) => Promise<types.SetMyGooglePayResponse>;

    postSetClientApplePay: (
      params: types.SetMyApplePayRequest,
      source?: CancelTokenSource,
    ) => Promise<types.SetMyApplePayResponse>;

    postSetClientPayPal: (
      params: types.SetMyPayPalRequest,
      source?: CancelTokenSource,
    ) => Promise<types.SetMyPayPalResponse>;

    postSetClientVenmo: (
      params: types.SetMyVenmoRequest,
      source?: CancelTokenSource,
    ) => Promise<types.SetMyVenmoResponse>;
    postDeleteClientCreditCard: (
      params: types.DeleteClientCreditCardRequest,
      source?: CancelTokenSource,
    ) => Promise<types.ApiResponse>;

    postDeleteDebitCard: (
      params: types.DeleteDebitCardRequest,
      source?: CancelTokenSource,
    ) => Promise<types.ApiResponse>;

    postEmbedUrl: (
      params: types.EmbedUrlRequest,
      source?: CancelTokenSource,
      bearer?: string,
    ) => Promise<types.EmbedUrlResponse>;

    postSendPaylink: (params: types.SendELinxRequest, source?: CancelTokenSource) => Promise<types.SendELinxResponse>;

    postEftFund: (params: types.EftFundRequest, source?: CancelTokenSource) => Promise<types.EftFundResponse>;

    postScheduledEftFund: (
      params: types.ScheduledEftFundRequest,
      source?: CancelTokenSource,
    ) => Promise<types.ScheduledEftFundResponse>;

    postCancelAutoBalanceTransfer: (
      params: types.CancelAutoBalanceTransferRequest,
      source?: CancelTokenSource,
    ) => Promise<types.ApiResponse>;

    postChargeCreditCard: (
      params: types.ChargeCreditCardRequest,
      source?: CancelTokenSource,
    ) => Promise<types.ChargeCreditCardResponse>;

    postIdVerificationRequest: (
      params: types.IdVerificationRequest,
      source?: CancelTokenSource,
    ) => Promise<types.IdVerificationResponse>;

    postInteracMoneyRequest: (
      params: types.InteracMoneyRequestRequest,
      source?: CancelTokenSource,
    ) => Promise<types.InteracMoneyRequestResponse>;

    postBulkPayoutRequest: (
      params: types.InteracBulkPayoutRequest,
      source?: CancelTokenSource,
    ) => Promise<types.InteracBulkPayoutResponse>;

    postCancelInteracMoneyRequest: (
      params: types.CancelInteracMoneyRequest,
      bearer?: string,
      source?: CancelTokenSource,
    ) => Promise<types.CancelInteracMoneyResponse>;

    postCancelInteracBulkPayout: (
      params: types.CancelInteracMoneyRequest,
      bearer?: string,
      source?: CancelTokenSource,
    ) => Promise<types.CancelInteracMoneyResponse>;

    postCancelVoPayInstant: (
      params: types.CancelVoPayInstantRequest,
      bearer?: string,
      source?: CancelTokenSource,
    ) => Promise<types.CancelVoPayInstantResponse>;

    postEftWithdraw: (
      params: types.EftWithdrawRequest,
      source?: CancelTokenSource,
    ) => Promise<types.EftWithdrawResponse>;

    postRtrWithdraw: (
      params: types.RtrWithdrawRequest,
      source?: CancelTokenSource,
    ) => Promise<types.RtrWithdrawResponse>;

    postScheduledEftWithdraw: (
      params: types.ScheduledEftWithdrawRequest,
      source?: CancelTokenSource,
    ) => Promise<types.ScheduledEftWithdrawResponse>;

    postCreateAutoBalanceTransfer: (
      params: types.CreateAutoBalanceTransferRequest,
      source?: CancelTokenSource,
    ) => Promise<types.ApiResponse>;

    saveSubAccountPermissions: (
      params: types.EditAccountPermissionsRequest,
      source?: CancelTokenSource,
    ) => Promise<types.ApiResponse>;

    postAccountTransferFrom: (
      params: types.AccountTransferFromRequest,
      source?: CancelTokenSource,
    ) => Promise<types.AccountTransferFromResponse>;

    postAccountTransferTo: (
      params: types.AccountTransferToRequest,
      source?: CancelTokenSource,
    ) => Promise<types.AccountTransferToResponse>;

    postAccountTransferFund: (
      params: types.AccountTransferFundRequest,
      source?: CancelTokenSource,
    ) => Promise<types.AccountTransferFundResponse>;

    postAccountTransferWithdraw: (
      params: types.AccountTransferWithdrawRequest,
      source?: CancelTokenSource,
    ) => Promise<types.AccountTransferWithdrawResponse>;

    postAccountTransferFundWithdraw: (
      params: types.AccountTransferFundWithdrawRequest,
      source?: CancelTokenSource,
    ) => Promise<types.AccountTransferFundWithdrawResponse>;

    postAccountTransferFundSchedule: (
      params: types.AccountTransferScheduleRequest,
      source?: CancelTokenSource,
    ) => Promise<types.AccountTransferScheduleResponse>;

    postAccountTransferWithdrawSchedule: (
      params: types.AccountTransferScheduleRequest,
      source?: CancelTokenSource,
    ) => Promise<types.AccountTransferScheduleResponse>;

    postAccountTransferFundWithdrawSchedule: (
      params: types.AccountTransferScheduleRequest,
      source?: CancelTokenSource,
    ) => Promise<types.AccountTransferScheduleResponse>;

    fetchAccountTransferSchedules: (
      params: types.AccountTransferSchedulesRequest,
      source?: CancelTokenSource,
    ) => Promise<types.AccountTransferSchedulesResponse>;

    postAccountTransferScheduleCancel: (
      params: types.AccountTransferScheduleCancelRequest,
      source?: CancelTokenSource,
    ) => Promise<types.AccountTransferScheduleCancelResponse>;

    fetchTransactionsHistory: (
      params: types.TransactionsHistoryRequest,
      source?: CancelTokenSource,
    ) => Promise<types.TransactionsHistoryResponse>;

    getSubAccountTransactionsHistory: (
      params: types.SubAccountTransactionsHistoryRequest,
      source?: CancelTokenSource,
    ) => Promise<types.SubAccountTransactionsHistoryResponse>;

    postCreateSubAccount: (
      params: types.CreateSubAccountRequest,
      source?: CancelTokenSource,
    ) => Promise<types.CreateSubAccountResponse>;

    postCreateGroup: (
      params: types.CreateGroupRequest,
      source?: CancelTokenSource,
    ) => Promise<types.CreateGroupResponse>;

    postEditGroup: (params: types.EditGroupRequest, source?: CancelTokenSource) => Promise<types.ApiResponse>;

    postDeleteGroup: (params: types.DeleteGroupRequest, source?: CancelTokenSource) => Promise<types.ApiResponse>;

    postAddGroupContact: (
      params: types.AddGroupContactRequest,
      source?: CancelTokenSource,
    ) => Promise<types.AddGroupContactResponse>;

    postDeleteGroupContact: (
      params: types.DeleteGroupContactRequest,
      source?: CancelTokenSource,
    ) => Promise<types.ApiResponse>;

    postBulkPayment: (params: types.BulkPaymentRequest, source?: CancelTokenSource) => Promise<types.BulkResponse>;

    postBulkCollection: (
      params: types.BulkCollectionRequest,
      source?: CancelTokenSource,
    ) => Promise<types.BulkResponse>;

    postBulkRTRPayment: (params: types.BulkPaymentRequest, source?: CancelTokenSource) => Promise<types.BulkResponse>;

    postBulkRTRCollection: (
      params: types.BulkCollectionRequest,
      source?: CancelTokenSource,
    ) => Promise<types.BulkResponse>;
    postBulkMoneyRequest: (params: types.BulkPaymentRequest, source?: CancelTokenSource) => Promise<types.BulkResponse>;

    postBulkBulkPayout: (params: types.BulkPaymentRequest, source?: CancelTokenSource) => Promise<types.BulkResponse>;
    postBulkPaylink: (params: types.BulkCollectionRequest, source?: CancelTokenSource) => Promise<types.BulkResponse>;
    postBulkPushToCardPayment: (
      params: types.BulkPaymentRequest,
      source?: CancelTokenSource,
    ) => Promise<types.BulkResponse>;
    postBulkCreditCardCollection: (
      params: types.BulkCollectionRequest,
      source?: CancelTokenSource,
    ) => Promise<types.BulkResponse>;
    postBulkACHCollection: (
      params: types.BulkCollectionRequest,
      source?: CancelTokenSource,
    ) => Promise<types.BulkResponse>;
    postBulkACHPayment: (params: types.BulkPaymentRequest, source?: CancelTokenSource) => Promise<types.BulkResponse>;
    PostBulkRTPPayment: (params: types.BulkPaymentRequest, source?: CancelTokenSource) => Promise<types.BulkResponse>;
    postBulkFedwireCollection: (
      params: types.BulkCollectionRequest,
      source?: CancelTokenSource,
    ) => Promise<types.BulkResponse>;
    postBulkFedwirePayment: (
      params: types.BulkPaymentRequest,
      source?: CancelTokenSource,
    ) => Promise<types.BulkResponse>;
    postBulkClientAccountTransfer: (
      params: types.BulkPaymentRequest,
      source?: CancelTokenSource,
    ) => Promise<types.BulkResponse>;

    fetchUploadedFiles: (
      params: types.UploadBatchRequest,
      source?: CancelTokenSource,
    ) => Promise<types.UploadBatchFileResponse>;
    fetchUploadedFileDetails: (
      params: types.UploadBatchFileDetailsRequest,
      source?: CancelTokenSource,
    ) => Promise<types.UploadBatchFileDetailseResponse>;

    fetchDefaultPaymentMethod: (
      params: types.DefaultPaymentMethodRequest,
      source?: CancelTokenSource,
    ) => Promise<types.DefaultPaymentMethodResponse>;
    postSetDefaultPaymentMethod: (
      params: types.SetDefaultPaymentMethodRequest,
      source?: CancelTokenSource,
      bearer?: string,
    ) => Promise<types.ApiResponse>;

    postCancelScheduledWithdraw: (
      params: types.CancelScheduledWithdrawRequest,
      source?: CancelTokenSource,
    ) => Promise<types.CancelScheduledWithdrawResponse>;

    postCancelScheduledFund: (
      params: types.CancelScheduledFundRequest,
      source?: CancelTokenSource,
    ) => Promise<types.CancelScheduledFundResponse>;

    postEditScheduledWithdraw: (
      params: types.EditScheduledWithdrawRequest,
      source?: CancelTokenSource,
    ) => Promise<types.ApiResponse>;

    postEditScheduledFund: (
      params: types.EditScheduledFundRequest,
      source?: CancelTokenSource,
    ) => Promise<types.ApiResponse>;

    postConfirmTransaction: (
      params: types.ConfirmTransactionRequest,
      source?: CancelTokenSource,
    ) => Promise<types.ApiResponse>;

    fetchTransactionCounts: (
      params: types.GetTransactionCountsRequest,
      source?: CancelTokenSource,
    ) => Promise<types.GetTransactionCountsResponse>;

    postCreateClientAccountIndividual: (
      params: types.CreateClientAccountIndividualRequest,
      source?: CancelTokenSource,
    ) => Promise<types.CreateClientAccountResponse>;

    postCreateClientAccountBusiness: (
      params: types.CreateClientAccountBusinessRequest,
      source?: CancelTokenSource,
    ) => Promise<types.CreateClientAccountResponse>;

    createClientAccountReceiveOnly: (
      params: types.CreateClientAccountReceiveOnlyRequest,
      source?: CancelTokenSource,
    ) => Promise<types.CreateClientAccountResponse>;

    createClientAccountStandard: (
      params: types.CreateClientAccountStandardRequest,
      source?: CancelTokenSource,
    ) => Promise<types.CreateClientAccountResponse>;

    editClientAccount: (
      params: types.EditClientAccountRequest,
      source?: CancelTokenSource,
    ) => Promise<types.EditClientAccountResponse>;

    postRenewAccount: (params: types.RenewAccountRequest, source?: CancelTokenSource) => Promise<types.ApiResponse>;
    template: (params: unknown, source?: CancelTokenSource) => Promise<unknown>;
    fetchClientAccountLabels: (
      params: types.FetchClientAccountLabelsRequest,
      source?: CancelTokenSource,
    ) => Promise<types.FetchLabelsResponse>;
    addClientAccountLabel: (
      params: types.AddClientAccountLabelRequest,
      source?: CancelTokenSource,
    ) => Promise<types.AddLabelResponse>;
    deleteClientAccountLabel: (
      params: types.DeleteClientAccountLabelRequest,
      source?: CancelTokenSource,
    ) => Promise<types.DeleteLabelResponse>;
    fetchContactLabels: (
      params: types.FetchContactLabelsRequest,
      source?: CancelTokenSource,
    ) => Promise<types.FetchLabelsResponse>;
    addContactLabel: (
      params: types.AddContactLabelRequest,
      source?: CancelTokenSource,
    ) => Promise<types.AddLabelResponse>;
    deleteContactLabel: (
      params: types.DeleteContactLabelRequest,
      source?: CancelTokenSource,
    ) => Promise<types.DeleteLabelResponse>;
    fetchTicketDetails: (
      params: types.TicketDetialRequest,
      source?: CancelTokenSource,
    ) => Promise<types.TicketDetailsResponse>;
    fetchTickets: (params?: types.EmailRequest, source?: CancelTokenSource) => Promise<types.TicketsResponse>;
    submitPartnerSupportTicket: (
      params: types.SubmitTicketRequest,
      source?: CancelTokenSource,
    ) => Promise<types.SubmitTicketResponse>;
    postSubmitTicketComment: (
      params: types.SubmitTicketCommentRequst,
      source?: CancelTokenSource,
    ) => Promise<types.ApiResponse>;
    postTicketAttachment: (
      params: types.TicketAttachmentRequst,
      source?: CancelTokenSource,
    ) => Promise<types.ApiResponse>;
    fetchTicketDetailsClient: (
      params: types.TicketDetailsClientRequest,
      source?: CancelTokenSource,
    ) => Promise<types.TicketDetailsResponse>;
    fetchTicketsClient: (
      params?: types.TicketClientsRequest,
      source?: CancelTokenSource,
    ) => Promise<types.TicketsResponse>;
    submitClientSupportTicket: (
      params: types.SubmitTicketRequest,
      source?: CancelTokenSource,
    ) => Promise<types.SubmitTicketResponse>;
    postSubmitTicketCommentClient: (
      params: types.SubmitTicketCommentRequst,
      source?: CancelTokenSource,
    ) => Promise<types.ApiResponse>;
    postSubmitTicketAttachment: (
      params: types.TicketAttachmentRequest,
      source?: CancelTokenSource,
    ) => Promise<types.TicketAttachmentResponse>;
    postSubmitTicketAttachmentClient: (
      params: types.TicketAttachmentRequest,
      source?: CancelTokenSource,
    ) => Promise<types.TicketAttachmentResponse>;
    fetchWebhookInfo: (params: types.PortalUser, source?: CancelTokenSource) => Promise<types.WebhookResponse>;
    fetchWebhooks: (params: types.ClientWebhooksRequest, source?: CancelTokenSource) => Promise<types.WebhooksResponse>;
    fetchPartnerWebhooks: (
      params: types.WebhooksRequest,
      source?: CancelTokenSource,
    ) => Promise<types.WebhooksResponse>;
    fetchPartnerWebhook: (params: types.PartnerUser, source?: CancelTokenSource) => Promise<types.WebhookResponse>;
    fetchTestWebhook: (params: types.PortalUser, source?: CancelTokenSource) => Promise<types.ApiResponse>;
    fetchPartnerTestWebhook: (params: types.PortalUser, source?: CancelTokenSource) => Promise<types.ApiResponse>;
    postPartnerUpdateWebhook: (
      params: types.UpdateWebhookRequest,
      source?: CancelTokenSource,
    ) => Promise<types.ApiResponse>;
    postUpdateWebhook: (params: types.UpdateWebhookRequest, source?: CancelTokenSource) => Promise<types.ApiResponse>;
    postResendWebhook: (
      params: types.RetriggerWebhookRequest,
      source?: CancelTokenSource,
    ) => Promise<types.ApiResponse>;
    postPartnerResendWebhook: (
      params: types.RetriggerWebhookRequest,
      source?: CancelTokenSource,
    ) => Promise<types.ApiResponse>;
    postSetPlaidCredentials: (
      params: types.SetPlaidCredentialsRequest,
      source?: CancelTokenSource,
    ) => Promise<types.ApiResponse>;
    postSetFlinksCredentials: (
      params: types.SetFlinksCredentialsRequest,
      source?: CancelTokenSource,
    ) => Promise<types.ApiResponse>;
    postSetInveriteCredentials: (
      params: types.SetInveriteCredentialsRequest,
      source?: CancelTokenSource,
    ) => Promise<types.ApiResponse>;
    postSetGlobalPayCredentials: (
      params: types.SetGlobalPayCredentialsRequest,
      source?: CancelTokenSource,
    ) => Promise<types.ApiResponse>;
    postSaveAuthorizedIPs: (
      params: types.SaveAuthorizedIPsRequest,
      source?: CancelTokenSource,
    ) => Promise<types.ApiResponse>;
    fetchAuthorizedIPs: (params: types.PortalUser, source?: CancelTokenSource) => Promise<types.AuthorizedIPsResponse>;
    fetchELinxHistory: (
      params: types.ELinxHistoryRequest,
      bearer?: string,
      source?: CancelTokenSource,
    ) => Promise<types.ELinxHistoryResponse>;
    fetchPaylinkSettings: (
      params: types.PortalUser,
      source?: CancelTokenSource,
    ) => Promise<types.ELinxSettingsResponse>;
    postPaylinkSettingsSetSendeeEmail: (
      params: types.ELinxSettingsSetSendeeEmailRequest,
      source?: CancelTokenSource,
    ) => Promise<types.ApiResponse>;
    postPaylinkSettingsSetSenderEmail: (
      params: types.ELinxSettingsSetSenderEmailRequest,
      source?: CancelTokenSource,
    ) => Promise<types.ApiResponse>;
    postPaylinkSettingsSetTokenExpiration: (
      params: types.ELinxSettingsSetTokenExpirationRequest,
      source?: CancelTokenSource,
    ) => Promise<types.ApiResponse>;
    postPaylinkSettingsUpdate: (
      params: types.PostELinxSettingsUpdateRequestRequest,
      source?: CancelTokenSource,
    ) => Promise<types.ApiResponse>;
    postPaylinkSettingsSetDecline: (
      params: types.ELinxSettingsSetDeclineRequest,
      source?: CancelTokenSource,
    ) => Promise<types.ApiResponse>;
    cancelElinx: (params: types.ELinxCancelRequest, source?: CancelTokenSource) => Promise<types.ApiResponse>;
    postDeleteInteracEmail: (
      params: types.DeleteInteracEmailRequest,
      source?: CancelTokenSource,
    ) => Promise<types.ApiResponse>;
    postAddInteracEmail: (
      params: types.InteracAddEmailRequest,
      source?: CancelTokenSource,
      bearer?: string,
    ) => Promise<types.InteracAddEmailResponse>;
    postLinkInteracEmail: (
      params: types.InteracLinkEmailRequest,
      source?: CancelTokenSource,
    ) => Promise<types.IQ11TokenResponse>;
    postIQ11Tokenize: (
      params: types.IQ11TokenizeRequest,
      source?: CancelTokenSource,
    ) => Promise<types.IQ11TokenizeResponse>;
    fetchiQ11AccessTokenInfo: (
      params: types.AccessTokenInfoRequest,
      source?: CancelTokenSource,
    ) => Promise<types.AccessTokenInfoResponse>;
    postLinkPushToCardRequest: (
      params: types.LinkPushToCardMapRequest,
      source?: CancelTokenSource,
    ) => Promise<types.PushToCardMapResponse>;
    fetchPartnerAccountTransactions: (
      params: types.ParentAccountTransactionsRequest,
      source?: CancelTokenSource,
    ) => Promise<types.PartnerAccountTransactions>;
    fetchHubSpotDropdownData: (source?: CancelTokenSource) => Promise<types.HubSpotDropdownDataResponse>;
    fetchHubSpotDealData: (
      params: types.HubSpotDealDataRequest,
      source?: CancelTokenSource,
    ) => Promise<types.HubSpotDealDetailsResponse>;
    postAccountBillingSavePaymentInformation: (
      params: types.AccountBillingSavePaymentInformationRequest,
      source?: CancelTokenSource,
      bearer?: string,
    ) => Promise<types.AccountBillingSavePaymentInformationResponse>;
    fetchAccountBillingDetails: (
      params: types.AccountBillingDetailsRequest,
      source?: CancelTokenSource,
      bearer?: string,
    ) => Promise<types.AccountBillingDetailsResponse>;
    postAccountBillingDetails: (
      params: types.AccountBillingDetailsSaveRequest,
      source?: CancelTokenSource,
      bearer?: string,
    ) => Promise<types.ApiResponse>;
    postAccountBillingForDisconnection: (
      params: types.AccountBillingDisconnectionRequest,
      source?: CancelTokenSource,
      bearer?: string,
    ) => Promise<types.ApiResponse>;
    fetchAccountDocument: (
      params: types.AccountDocumentRequest,
      source?: CancelTokenSource,
      bearer?: string,
    ) => Promise<types.AccountDocumentResponse>;
    postHubSpotDealUpdate: (
      params: types.HubSpotDealUpdateRequest,
      source?: CancelTokenSource,
    ) => Promise<types.ApiResponse>;
    postAccountDocumentUpload: (
      params: types.DocumentUploadRequest,
      bearer: string,
      source?: CancelTokenSource,
    ) => Promise<types.DocumentUploadResponse>;
    postBankAccountDocumentUpload: (
      params: types.BankAccountDocumentUploadRequest,
      bearer: string,
      source?: CancelTokenSource,
    ) => Promise<types.DocumentUploadResponse>;
    postBankAccountDocumentDelete: (
      params: types.BankAccountDocumentDeleteRequest,
      bearer: string,
      source?: CancelTokenSource,
    ) => Promise<types.ApiResponse>;
    postPartnerAccountTransfer: (
      params: types.PartnerAccountTransferRequest,
      source?: CancelTokenSource,
    ) => Promise<types.PartnerAccountTransferResponse>;
    postPartnerAccountFundAndTransfer: (
      params: types.PartnerAccountTransferRequest,
      source?: CancelTokenSource,
    ) => Promise<types.PartnerAccountFundAndTransferResponse>;
    postPartnerAccountTransferAndWithdraw: (
      params: types.PartnerAccountTransferRequest,
      source?: CancelTokenSource,
    ) => Promise<types.PartnerAccountTransferAndWithdrawResponse>;
    postPartnerAccountFundTransferAndWithdraw: (
      params: types.PartnerAccountTransferRequest,
      source?: CancelTokenSource,
    ) => Promise<types.PartnerAccountFundTransferAndWithdrawResponse>;
    postWithdrawMyAccount: (
      params: types.WithdrawMyAccountRequest,
      source?: CancelTokenSource,
    ) => Promise<types.WithdrawMyAccountResponse>;
    postFundMyAccount: (
      params: types.FundMyAccountRequest,
      source?: CancelTokenSource,
    ) => Promise<types.FundMyAccountResponse>;
    postVoidACHTransaction: (
      params: types.VoidACHTransactionRequest,
      bearer?: string,
      source?: CancelTokenSource,
    ) => Promise<types.VoidACHTransactionResponse>;
    postRefundACHTransaction: (
      params: types.RefundACHTransactionRequest,
      bearer?: string,
      source?: CancelTokenSource,
    ) => Promise<types.RefundACHTransactionResponse>;
    postACHFund: (params: types.ACHFundRequest, source?: CancelTokenSource) => Promise<types.ACHFundResponse>;
    postACHWithdraw: (
      params: types.ACHWithdrawRequest,
      source?: CancelTokenSource,
    ) => Promise<types.ACHWithdrawResponse>;
    postACHFundSchedule: (
      params: types.ACHFundScheduleRequest,
      source?: CancelTokenSource,
    ) => Promise<types.ACHFundScheduleResponse>;
    postACHWithdrawSchedule: (
      params: types.ACHFundScheduleRequest,
      source?: CancelTokenSource,
    ) => Promise<types.ACHFundScheduleResponse>;
    fetchACHFundTransaction: (
      params: types.ACHFundTransactionRequest,
      bearer?: string,
      source?: CancelTokenSource,
    ) => Promise<types.ACHFundTransactionResponse>;
    fetchACHWithdrawTransaction: (
      params: types.ACHWithdrawTransactionRequest,
      bearer?: string,
      source?: CancelTokenSource,
    ) => Promise<types.ACHWithdrawTransactionResponse>;
    fetchACHSheduleTransaction: (
      params: types.ACHSheduleTransactionRequest,
      source?: CancelTokenSource,
    ) => Promise<types.ACHSheduleTransactionResponse>;
    postRefundCreditCardTransaction: (
      params: types.RefundCreditCardTransactionRequest,
      bearer?: string,
      source?: CancelTokenSource,
    ) => Promise<types.RefundCreditCardTransactionResponse>;
    voidCreditCardTransaction: (
      params: types.VoidCreditCardTransactionRequest,
      bearer?: string,
      source?: CancelTokenSource,
    ) => Promise<types.VoidCreditCardTransactionResponse>;

    postOnboardingToken: (
      params: types.OnboardingTokenRequest,
      source?: CancelTokenSource,
    ) => Promise<types.OnboardingTokenResponse>;

    postSendLegalAgreement: (bearer?: string, source?: CancelTokenSource) => Promise<types.PostLegalAgreementResponse>;

    fetchTicketCategories: (source?: CancelTokenSource) => Promise<types.TicketCategoriesResponse>;
    fetchClientDashboardTicketCategories: (source?: CancelTokenSource) => Promise<types.TicketCategoriesResponse>;
    fetchGeolocation: (source?: CancelTokenSource) => Promise<types.GeoLocationResponse>;

    fetchPortalVideos: (
      params: types.PortalVideoRequest,
      source?: CancelTokenSource,
    ) => Promise<types.PortalVideoResponse>;

    postOnboardingAppToken: (
      params: types.OnboardingAppTokenRequest,
      source?: CancelTokenSource,
    ) => Promise<types.OnboardingAppTokenResponse>;

    getAssignedBillingPackage: (
      params: types.AssignedBillingPackagesRequest,
      source?: CancelTokenSource,
      bearer?: string,
    ) => Promise<types.AssignedBillingPackagesResponse>;

    getAccountTransactionTypes: (
      params: types.AccountTransactionTypesRequest,
      source?: CancelTokenSource,
      bearer?: string,
    ) => Promise<types.AccountTransactionTypesResponse>;

    fetchPartnerTransactionTypes: (
      params: types.AccountTransactionTypesRequest,
      source?: CancelTokenSource,
      bearer?: string,
    ) => Promise<types.AccountTransactionTypesPartnerResponse>;

    fetchSubscriptions: (
      params: types.ReportsRequest,
      source?: CancelTokenSource,
    ) => Promise<types.FetchSubscriptionListResponse>;

    fetchSubscriptionDetail: (
      params: types.ReportsRequest,
      source?: CancelTokenSource,
    ) => Promise<types.ReportDetailResponse>;

    postEditReport: (params: types.EditReportRequest, source?: CancelTokenSource) => Promise<types.ApiResponse>;

    postCreateReport: (
      params: types.CreateReportRequest,
      source?: CancelTokenSource,
    ) => Promise<types.CreateReportsResponse>;

    postDownloadReport: (
      params: types.DownloadReportRequest,
      source?: CancelTokenSource,
    ) => Promise<types.DownloadReportsResponse>;

    fetchAccountInvoices: (
      params: types.InvoicesRequest,
      source?: CancelTokenSource,
    ) => Promise<types.AccountInvoicesResponse>;
    fetchAccountInvoicePdf: (
      params: types.InvoicePdfRequest,
      source?: CancelTokenSource,
    ) => Promise<types.InvoicePdfResponse>;
    fetchAccountStatementCsv: (
      params: types.StatementCsvRequest,
      source?: CancelTokenSource,
    ) => Promise<types.StatementCsvResponse>;
    fetchAccountStatementPDF: (
      params: types.StatementCsvRequest,
      source?: CancelTokenSource,
    ) => Promise<types.StatementPDFResponse>;

    initiateBankAccountVerification: (
      params: types.InitiateBankAccountVerificationRequest,
      source?: CancelTokenSource,
    ) => Promise<types.InitiateBankAccountVerificationResponse>;

    verifyInterac: (
      params: types.VerifyInteracRequest,
      source?: CancelTokenSource,
    ) => Promise<types.VerifyInteracResponse>;

    verifyCard: (params: types.VerifyCardRequest, source?: CancelTokenSource) => Promise<types.VerifyCardResponse>;

    postAccountVerificationConfirm: (
      params: types.AccountVerificationConfirmRequest,
      source?: CancelTokenSource,
    ) => Promise<types.AccountVerificationConfirmResponse>;

    fetchDebitAuthority: (
      params: types.GetDebitAuthorityAgreementRequest,
      source?: CancelTokenSource,
    ) => Promise<types.GetDebitAuthorityAgreementResponse>;

    postDebitAuthority: (
      params: types.CreateDebitAuthorityAgreementRequest,
      source?: CancelTokenSource,
    ) => Promise<types.ApiResponse>;
    fetchAccountBranding: (
      params: types.AccountBrandingRequest,
      source?: CancelTokenSource,
      bearer?: string,
    ) => Promise<types.AccountBrandingResponse>;
    fetchPartnerBranding: (
      params: types.AccountBrandingRequest,
      source?: CancelTokenSource,
    ) => Promise<types.AccountBrandingResponse>;
    fetchPartnerAccountPermissionFields: (
      // eslint-disable-next-line @typescript-eslint/no-empty-object-type
      params: {},
      source?: CancelTokenSource,
    ) => Promise<types.PartnerAccountPermissionFieldsResponse>;
    postAccountBranding: (
      params: types.SaveAccountBrandingRequest,
      source?: CancelTokenSource,
    ) => Promise<types.ApiResponse>;
    postPartnerBranding: (
      params: types.SaveAccountBrandingRequest,
      source?: CancelTokenSource,
    ) => Promise<types.ApiResponse>;
    fetchFAQs: (source?: CancelTokenSource) => Promise<types.FAQResponse>;
    fetchFAQAllQuestions: (source?: CancelTokenSource) => Promise<types.FAQAllQuestionsResponse>;
    postFAQUpdateClickCount: (params: types.FAQRequest, source?: CancelTokenSource) => Promise<types.FAQResponse>;
    fetchFAQCategories: (source?: CancelTokenSource) => Promise<types.FAQCategoriesResponse>;
    fetchLoginPageBranding: (
      params: types.AccountBrandingLoginPageRequest,
      source?: CancelTokenSource,
    ) => Promise<types.LoginBrandingResponse>;
    fetchApprovalTransactions: (
      params: types.ApprovalInboxTransactionsRequest,
      source?: CancelTokenSource,
    ) => Promise<types.ApprovalInboxTransactionsResponse>;
    fetchApprovalTransactionsInbox: (
      params: types.ApprovalInboxTransactionsRequest,
      bearer?: string,
      source?: CancelTokenSource,
    ) => Promise<types.ApprovalInboxTransactionsResponse>;
    postVerifyApprovalTransation: (
      params: types.VerifyTransactionApproveRequest,
      source?: CancelTokenSource,
    ) => Promise<types.ApiResponse>;
    postVerifyMarkAsProceedTransaction: (
      params: types.VerifyTransactionApproveRequest,
      source?: CancelTokenSource,
    ) => Promise<types.ApiResponse>;
    approveTransaction: (
      params: types.InboxStatusChangeRequest,
      bearer?: string,
      source?: CancelTokenSource,
    ) => Promise<types.ApiResponse>;
    unapproveTransaction: (
      params: types.InboxStatusChangeRequest,
      bearer?: string,
      source?: CancelTokenSource,
    ) => Promise<types.ApiResponse>;
    rejectTransaction: (
      params: types.InboxRejectStatusChangeRequest,
      bearer?: string,
      source?: CancelTokenSource,
    ) => Promise<types.ApiResponse>;

    fetchUserRoles: (params: types.UserRolesRequest, source?: CancelTokenSource) => Promise<types.UserRolesResponse>;

    postCreateUserRole: (params: types.UpdateUserRoleRequest, source?: CancelTokenSource) => Promise<types.ApiResponse>;
    postDeleteUserRole: (params: types.UpdateUserRoleRequest, source?: CancelTokenSource) => Promise<types.ApiResponse>;
    postCreateUserRolePermission: (
      params: types.UpdateUserRolePermissionRequest,
      source?: CancelTokenSource,
    ) => Promise<types.ApiResponse>;
    postUpdateUserRolePermission: (
      params: types.UpdateUserRolePermissionRequest,
      source?: CancelTokenSource,
    ) => Promise<types.ApiResponse>;
    postDeleteUserRolePermission: (
      params: types.UpdateUserRolePermissionRequest,
      source?: CancelTokenSource,
    ) => Promise<types.ApiResponse>;

    fetchWorkflows: (params?: types.PortalUser, bearer?: string) => Promise<types.WorkflowsResponse>;
    postCreateWorkflow: (params: types.WorkflowCreateRequest, source?: CancelTokenSource) => Promise<types.ApiResponse>;
    postUpdateWorkflow: (params: types.WorkflowUpdateRequest, source?: CancelTokenSource) => Promise<types.ApiResponse>;
    deleteWorkflow: (params: types.WorkflowDeleteRequest, source?: CancelTokenSource) => Promise<types.ApiResponse>;

    fetchTransactionTypes: (
      params: types.TransactionTypesRequst,
      bearer?: string,
      source?: CancelTokenSource,
    ) => Promise<types.TransactionTypesResponse>;
    fetchRailzBusinessAccount: (
      params: types.PortalUser,
      source?: CancelTokenSource,
    ) => Promise<types.RailzBusinessAccountResponse>;
    fetchRailzAccount: (
      params: types.GetChartOfAccountRequest,
      source?: CancelTokenSource,
    ) => Promise<types.ChartOfAccountResponse>;
    fetchRailzChartOfAccounts: (
      params: types.GetChartOfAccountRequest,
      source?: CancelTokenSource,
    ) => Promise<types.ChartOfAccountsResponse>;
    postSetRailzChartOfAccount: (
      params: types.ChartOfAccountRequest,
      source?: CancelTokenSource,
    ) => Promise<types.ApiResponse>;

    fetchRailzBills: (
      params: types.RailzBillsOrInvoicesRequest,
      source?: CancelTokenSource,
    ) => Promise<types.RailzBillsResponse>;
    postBills: (params: types.PayBillsRequest, source?: CancelTokenSource) => Promise<types.ApiResponse>;
    fetchRailzInvoices: (
      params: types.RailzBillsOrInvoicesRequest,
      source?: CancelTokenSource,
    ) => Promise<types.RailzInvoicesResponse>;
    postInvoices: (params: types.PayInvoicesRequest, source?: CancelTokenSource) => Promise<types.ApiResponse>;
    postRailzSync: (params: types.BaseRailzeRequest, source?: CancelTokenSource) => Promise<types.ApiResponse>;
    fetchRailzSyncStatus: (
      params: types.BaseRailzeRequest,
      source?: CancelTokenSource,
    ) => Promise<types.RailzStatusSyncData>;
    fetchPortalDocument: (source?: CancelTokenSource) => Promise<types.PortalDocumentResponse>;
    postCancelInvoice: (
      params: types.RailzInvoiceCancelRequest,
      source?: CancelTokenSource,
    ) => Promise<types.ApiResponse>;
    postCancelBill: (params: types.RailzBillsCancelRequest, source?: CancelTokenSource) => Promise<types.ApiResponse>;
    postFundMyAccountSchedule: (
      paramse: types.MyAccountScheduleRequest,
      source?: CancelTokenSource,
    ) => Promise<types.MyAccountScheduleResponse>;
    postWithdrawMyAccountSchedule: (
      paramse: types.MyAccountScheduleRequest,
      source?: CancelTokenSource,
    ) => Promise<types.MyAccountScheduleResponse>;
    fetchAutoBalanceTransferReport: (
      params: types.TransactionId,
      bearer?: string,
      source?: CancelTokenSource,
    ) => Promise<types.AutoBalanceTransferReportResponse>;
    fetchRailzBillCreditNotes: (
      params: types.CreditNotesBillsRequest,
      source?: CancelTokenSource,
    ) => Promise<types.CreditNotesResponse>;
    fetchRailzInvoiceCreditNotes: (
      params: types.CreditNotesInvoicesRequest,
      source?: CancelTokenSource,
    ) => Promise<types.CreditNotesResponse>;
    fetchContacts: (params?: types.ContactsRequest, source?: CancelTokenSource) => Promise<types.ContactsResponse>;

    fetchRecentGlobalSendTransactions: (
      params: types.GlobalSendRecentTransactionsRequest,
      bearer?: string,
      source?: CancelTokenSource,
    ) => Promise<types.GlobalSendRecentTransactionsResponse>;

    postCrossBorderUrl: (
      params: types.CrossBorderUrlRequest,
      source?: CancelTokenSource,
      bearer?: string,
    ) => Promise<types.CrossBorderUrlResponse>;
    fetchCrossBorderUrlInfo: (
      params: types.CrossBorderUrlInfoRequest,
      source?: CancelTokenSource,
    ) => Promise<types.CrossBorderUrlInfoResponse>;
    generateEmbedUrlDisputeManagement: (
      params: types.GenerateEmbedUrlDisputeManagementRequest,
      source?: CancelTokenSource,
    ) => Promise<types.GenerateEmbedUrlDisputeManagementResponse>;
    fetchDisputeManagementUrlInfo: (
      params: types.DisputeManagementUrlInfoRequest,
      source?: CancelTokenSource,
    ) => Promise<types.DisputeManagementUrlInfoResponse>;
    fetchCrossBorderBankCities: (
      params: types.CrossBorderBankCitiesRequest,
      source?: CancelTokenSource,
    ) => Promise<types.CrossBorderBankCitiesResponse>;
    fetchCrossBorderBankBranches: (
      params: types.CrossBorderBankBranchesRequest,
      source?: CancelTokenSource,
    ) => Promise<types.CrossBorderBankBranchesResponse>;
    fetchCrossBorderBankNames: (
      params: types.CrossBorderBankNamesRequest,
      source?: CancelTokenSource,
    ) => Promise<types.CrossBorderBankNamesResponse>;
    fetchCrossBorderBankLocations: (
      params: types.CrossBorderBankLocationsRequest,
      source?: CancelTokenSource,
    ) => Promise<types.CrossBorderBankLocationsResponse>;
    postCrossBorderAddDeliveryMethod: (
      params: types.CrossBorderAddDeliveryMethodRequest,
      source?: CancelTokenSource,
    ) => Promise<types.CrossBorderAddDeliveryMethodResponse>;

    fetchCrossBorderCountries: (
      params?: types.CrossBorderCountriesRequest,
      source?: CancelTokenSource,
    ) => Promise<types.CrossBorderCountriesResponse>;
    fetchDeliveryChannelsFields: (
      params: types.DeliveryChannelsFieldsRequest,
      source?: CancelTokenSource,
    ) => Promise<types.DeliveryChannelsFieldsResponse>;
    fetchDeliveryChannelsFieldsData: (
      params: types.DeliveryChannelsFieldsDataRequest,
      source?: CancelTokenSource,
    ) => Promise<types.DeliveryChannelsFieldsDataResponse>;
    fetchDeliveryChannels: (
      params: types.DeliveryChannelsRequest,
      source?: CancelTokenSource,
    ) => Promise<types.DeliveryChannelsResponse>;
    postCrossBorderQuotes: (
      paramse: types.CrossBorderQuotesRequest,
      source?: CancelTokenSource,
    ) => Promise<types.CrossBorderQuotesResponse>;
    fetchCrossBorderRecipientContacts: (
      params: types.CrossBorderRecipientContactsRequest,
      source?: CancelTokenSource,
    ) => Promise<types.CrossBorderRecipientContactsResponse>;
    postAddFavoriteRecipientContact: (
      params: types.AddFavoriteRecipientContactRequest,
      source?: CancelTokenSource,
    ) => Promise<types.AddFavoriteRecipientContactResponse>;
    postDeleteFavoriteRecipientContact: (
      params: types.DeleteFavoriteRecipientContactRequest,
      source?: CancelTokenSource,
    ) => Promise<types.DeleteFavoriteRecipientContactResponse>;
    fetchRecipientContactDeliveryMethods: (
      params: types.RecipientContactDeliveryMethodsRequest,
      source?: CancelTokenSource,
    ) => Promise<types.RecipientContactDeliveryMethodsResponse>;
    fetchGovernmentIdentifier: (
      params: types.GovernmentIdentifierRequest,
      source?: CancelTokenSource,
    ) => Promise<types.GovernmentIdentifierResponse>;
    fetchGovernmentIdentifierSchemas: (
      params: types.GovernmentIdentifierSchemasRequest,
      source?: CancelTokenSource,
    ) => Promise<types.GovernmentIdentifierSchemasResponse>;
    postGovernmentIdentifier: (
      params: types.AddGovernmentIdentifierRequest,
      source?: CancelTokenSource,
    ) => Promise<types.AddGovernmentIdentifierResponse>;
    postEditRecipient: (
      params: types.EditRecipientRequest,
      source?: CancelTokenSource,
    ) => Promise<types.EditRecipientResponse>;
    postAddRecipient: (
      params: types.AddRecipientRequest,
      source?: CancelTokenSource,
    ) => Promise<types.AddRecipientResponse>;
    postCreateGlobalSendTransaction: (
      params: types.CreateGlobalSendTransactionRequest,
      source?: CancelTokenSource,
    ) => Promise<types.CreateGlobalSendTransactionResponse>;

    fetchBankSchema: (params: types.BankSchemaRequest, source?: CancelTokenSource) => Promise<types.BankSchemaResponse>;
    confirmEmailVerification: (
      params: types.ContactVerificationConfirmRequest,
      source?: CancelTokenSource,
    ) => Promise<types.ContactVerificationConfirmResponse>;
    confirmPhoneVerification: (
      params: types.ContactVerificationConfirmRequest,
      source?: CancelTokenSource,
    ) => Promise<types.ContactVerificationConfirmResponse>;
    postGenerateOtp: (
      params: types.GenerateOtpRequest,
      source?: CancelTokenSource,
    ) => Promise<types.GenerateOtpResponse>;
    postValidateOtp: (params: types.ValidateOtpRequest, source?: CancelTokenSource) => Promise<types.ApiResponse>;
    fetchElinxPaymentMethods: (
      params: types.ElinxPaymentMethodsRequest,
      source?: CancelTokenSource,
      bearer?: string,
    ) => Promise<types.ElinxPaymentMethodsResponse>;
    postRequirePadAgreement: (
      params: types.RequirePadAgreementRequest,
      source: CancelTokenSource,
      bearer: string,
    ) => Promise<types.RequirePadAgreementResponse>;
    postAcceptAgreement: (
      params: types.AcceptAgreementRequest,
      source?: CancelTokenSource,
    ) => Promise<types.ApiResponse>;
    postInterestAccount: (source?: CancelTokenSource) => Promise<types.ApiResponse>;
    fetchInterestAccount: (
      params: types.InterestAccountRequest,
      source?: CancelTokenSource,
    ) => Promise<types.InterestAccountResponse>;
    fetchInterestAccountDetail: (
      params: types.InterestAccountDetailRequest,
      source?: CancelTokenSource,
    ) => Promise<types.InterestAccountDetailResponse>;
    postInterestAccountTransfer: (
      params: types.InterestAccountTransferRequest,
      source?: CancelTokenSource,
    ) => Promise<types.InterestAccountTransferResponse>;
    fetchInterestAccountRates: (
      params: types.InterestAccountRatesRequest,
      source?: CancelTokenSource,
    ) => Promise<types.InterestAccountRatesResponse>;
    fetchInterestAccountAccruedInterest: (
      params: types.InterestAccountAccruedInterestRequest,
      source?: CancelTokenSource,
    ) => Promise<types.InterestAccountAccruedInterestResponse>;
    fetchInterestAccountTransactions: (
      params: types.InterestAccountTransactionsRequest,
      source?: CancelTokenSource,
    ) => Promise<types.InterestAccountTransactionsResponse>;
    fetchUserSettings: (
      params: types.UserSettingsRequest,
      source?: CancelTokenSource,
    ) => Promise<types.UserSettingsResponse>;
    postSubmitUserSettings: (
      params: types.SubmitUserSettingsRequest,
      source?: CancelTokenSource,
    ) => Promise<types.UserSettingsResponse>;
    postRecipientContactGenerateEmbedUrl: (
      params: types.RecipientContactsGenerateEmbedUrlRequest,
      source?: CancelTokenSource,
    ) => Promise<types.RecipientContactsGenerateEmbedUrlResponse>;
    activateClientAccount: (
      params: types.ActivateDeactiveteClientAccountRequest,
      source?: CancelTokenSource,
    ) => Promise<types.ActivateDeactiveteClientAccountResponse>;
    deactivateClientAccount: (
      params: types.ActivateDeactiveteClientAccountRequest,
      source?: CancelTokenSource,
    ) => Promise<types.ActivateDeactiveteClientAccountResponse>;
    activateContact: (
      params: types.ActivateDeactiveteContactRequest,
      source?: CancelTokenSource,
    ) => Promise<types.ActivateDeactiveteContactResponse>;
    deactivateContact: (
      params: types.ActivateDeactiveteContactRequest,
      source?: CancelTokenSource,
    ) => Promise<types.ActivateDeactiveteContactResponse>;
    postRtpWithdraw: (
      params: types.RtpWithdrawRequest,
      source?: CancelTokenSource,
    ) => Promise<types.RtpWithdrawResponse>;
    postFedwireWithdraw: (
      params: types.FedwireWithdrawRequest,
      source?: CancelTokenSource,
    ) => Promise<types.FedwireWithdrawResponse>;
    fetchAccountExtendedInfo: (
      bearer?: string,
      source?: CancelTokenSource,
    ) => Promise<types.AccountExtendedInfoResponse>;
    postELinxSettingsSetNotification: (
      params: types.ELinxSettingsSetNotificationRequest,
      source?: CancelTokenSource,
    ) => Promise<types.ApiResponse>;
    postELinxSettingsSetReminder: (
      params: types.ELinxSettingsSetReminderRequest,
      source?: CancelTokenSource,
    ) => Promise<types.ApiResponse>;
    fetchWatchList: (params: types.WatchListRequest, source?: CancelTokenSource) => Promise<types.WatchListResponse>;
    addWatchList: (params: types.AddWatchListRequest, source?: CancelTokenSource) => Promise<types.ApiResponse>;
    deleteWatchList: (params: types.DeleteWatchListRequest, source?: CancelTokenSource) => Promise<types.ApiResponse>;
    setWatchList: (params: types.SetWatchListRequest, source?: CancelTokenSource) => Promise<types.ApiResponse>;
    fetchCurrencies: (params: types.CurrenciesRequest, source?: CancelTokenSource) => Promise<types.CurrenciesResponse>;
    postEditInteracEmail: (
      params: types.InteracEditEmailRequest,
      source?: CancelTokenSource,
      bearer?: string,
    ) => Promise<types.InteracEditEmailResponse>;
    addContact: (params: types.AddContactRequest, source?: CancelTokenSource) => Promise<types.AddContactResponse>;
    generateGCMEmbedUrl: (
      params: types.GenerateGCMEmbedUrlRequest,
      source?: CancelTokenSource,
    ) => Promise<types.GenerateGCMEmbedUrlResponse>;
    railzCreateInvoice: (
      params: types.RailzCreateInvoiceRequest,
      source?: CancelTokenSource,
    ) => Promise<types.ApiResponse>;
    railzCustomers: (
      params: types.RailzCustomersRequest,
      source?: CancelTokenSource,
    ) => Promise<types.RailzCustomersResponse>;
    railzInventory: (
      params: types.RailzInventoryRequest,
      source?: CancelTokenSource,
    ) => Promise<types.RailzInventoryResponse>;
    railzTaxRates: (
      params: types.RailzTaxRatesRequest,
      source?: CancelTokenSource,
    ) => Promise<types.RailzTaxRatesResponse>;
    railzTrackingCategories: (
      params: types.RailzTrackingCategoriesRequest,
      source?: CancelTokenSource,
    ) => Promise<types.RailzTrackingCategoriesResponse>;
    railzCreateBill: (params: types.RailzCreateBillRequest, source?: CancelTokenSource) => Promise<types.ApiResponse>;
    railzVendors: (
      params: types.RailzVendorsRequest,
      source?: CancelTokenSource,
    ) => Promise<types.RailzVendorsResponse>;
    railzCategories: (
      params: types.RailzCategories,
      source?: CancelTokenSource,
    ) => Promise<types.RailzCategoriesResponse>;
    authenticateMerchantUser: (source?: CancelTokenSource) => Promise<types.AuthenticateMerchantUserResponse>;
    postFedwireFund: (
      params: types.FedwireFundRequest,
      source?: CancelTokenSource,
    ) => Promise<types.FedwireFundResponse>;

    fetchCrossBorderCashPickupStates: (
      params: types.CrossBorderCashPickupStatesRequest,
      source?: CancelTokenSource,
    ) => Promise<types.CrossBorderCashPickupStatesResponse>;
    fetchCrossBorderCashPickupCities: (
      params: types.CrossBorderCashPickupCitiesRequest,
      source?: CancelTokenSource,
    ) => Promise<types.CrossBorderCashPickupCitiesResponse>;
    fetchCrossBorderCashPickupLocations: (
      params: types.CrossBorderCashPickupLocationsRequest,
      source?: CancelTokenSource,
    ) => Promise<types.CrossBorderCashPickupLocationsResponse>;
    fetchPartnerSigningAuthorityInfo: (
      bearer: string,
      source?: CancelTokenSource,
    ) => Promise<types.PartnerSigningAuthorityInfoResponse>;
    postAddPartnerSigner: (
      params: types.AddSignerRequest,
      bearer: string,
      source?: CancelTokenSource,
    ) => Promise<types.AddSignerResponse>;
    postEditPartnerSigner: (
      params: types.EditSignerRequest,
      bearer: string,
      source?: CancelTokenSource,
    ) => Promise<types.EditSignerResponse>;
    postDeletePartnerSigner: (
      params: types.DeleteSignerRequest,
      bearer: string,
      source?: CancelTokenSource,
    ) => Promise<types.DeleteSignerResponse>;
    fetchGCMBalance: (params: types.GCMBalanceRequest, source?: CancelTokenSource) => Promise<types.GCMBalanceResponse>;
    fetchGCMRatesFeesComparison: (
      params: types.GCMRatesFeesComparisonRequest,
      source?: CancelTokenSource,
    ) => Promise<types.GCMRatesFeesComparisonResponse>;
    editDeliveryMethod: (
      params: types.EditDeliveryMethodRequest,
      source?: CancelTokenSource,
    ) => Promise<types.ApiResponse>;
    fetchGCMFundingRails: (source?: CancelTokenSource) => Promise<types.GCMFundingRailsResponse>;
    postLinkPaypal: (
      params: types.LinkPaypalRequest,
      bearer: string,
      source?: CancelTokenSource,
    ) => Promise<types.LinkPaypalResponse>;
    postLinkVenmo: (
      params: types.LinkVenmoRequest,
      bearer: string,
      source?: CancelTokenSource,
    ) => Promise<types.LinkVenmoResponse>;
    setMyGooglePay: (
      params: types.SetMyGooglePayRequest,
      source?: CancelTokenSource,
    ) => Promise<types.SetMyGooglePayResponse>;
    postChargeGooglePay: (
      params: types.ChargeGooglePayRequest,
      source?: CancelTokenSource,
    ) => Promise<types.ChargeGooglePayResponse>;
    editContact: (params: types.EditContactRequest, source?: CancelTokenSource) => Promise<types.EditContactResponse>;
    fetchBillPaymentMerchants: (
      params: types.BillPaymentMerchantsRequest,
      source?: CancelTokenSource,
    ) => Promise<types.BillPaymentMerchantsResponse>;
    fetchBillPaymentAccountMerchants: (
      params: types.BillPaymentAccountMerchantsRequest,
      source?: CancelTokenSource,
    ) => Promise<types.BillPaymentAccountMerchantsResponse>;
    fetchBillPayInfo: (
      params: types.BillPayInfoRequest,
      source?: CancelTokenSource,
    ) => Promise<types.BillPayInfoResponse>;
    fetchTransactionApprovalInfo: (
      params: types.TransactionApprovalInfoRequest,
      source?: CancelTokenSource,
    ) => Promise<types.FetchTransactionApprovalInfoResponse>;
    fetchTransactionApprovalHistory: (
      params: types.TransactionApprovalHistoryRequest,
      bearer?: string,
      source?: CancelTokenSource,
    ) => Promise<types.FetchTransactionApprovalHistoryResponse>;
    fetchWorkflowLogs: (
      params: types.WorkflowLogsRequest,
      source?: CancelTokenSource,
    ) => Promise<types.FetchWorkflowLogsResponse>;
    createBillPayment: (
      params: types.BillPayCreateRequest,
      source?: CancelTokenSource,
    ) => Promise<types.BillPayCreateResponse>;
    addBillPaymentMerchant: (
      params: types.BillPayAccountMerchantAddRequest,
      source?: CancelTokenSource,
    ) => Promise<types.BillPayAccountMerchantAddResponse>;
    editBillPaymentMerchant: (
      params: types.BillPayAccountMerchantEditRequest,
      source?: CancelTokenSource,
    ) => Promise<types.BillPayAccountMerchantEditResponse>;
    fetchGlobalSendAdminUrlInfo: (
      params: types.GlobalSendAdminUrlInfoRequest,
      source?: CancelTokenSource,
    ) => Promise<types.GlobalSendAdminUrlInfoResponse>;
    fetchGlobalSendAdminDeliveryChannels: () => Promise<types.GlobalSendDeliveryChannelsResponse>;
    publishGlobalSendAdminDeliveryChannels: (
      params: types.PublishGlobalSendAdminDeliveryChannelsRequest,
      source?: CancelTokenSource,
    ) => Promise<types.ApiResponse>;
    fetchGlobalSendAdminFundingOptions: () => Promise<types.FetchGlobalSendAdminFundingOptionsResponse>;
    fetchDisputeManagementOverview: (
      params: types.DisputeManagementOverviewRequest,
      source?: CancelTokenSource,
    ) => Promise<types.DisputeManagementOverviewResponse>;
    fetchDisputeManagementList: (
      params: types.DisputeManagementListRequest,
      source?: CancelTokenSource,
    ) => Promise<types.DisputeManagementListResponse>;
    fetchDisputeStages: (
      params: types.DisputeStagesRequest,
      source?: CancelTokenSource,
    ) => Promise<types.DisputeStagesResponse>;
    fetchDisputeDetails: (
      params: types.DisputeDetailsRequest,
      source?: CancelTokenSource,
    ) => Promise<types.DisputeDetailsResponse>;
    fetchDisputeDocuments: (
      params: types.DisputeDocumentsRequest,
      source?: CancelTokenSource,
    ) => Promise<types.DisputeDocumentsResponse>;
    respondToDispute: (
      params: types.RespondAndChallengeDisputeRequest,
      source?: CancelTokenSource,
    ) => Promise<types.RespondAndChallengeDisputeResponse>;
    challengeDispute: (
      params: types.RespondAndChallengeDisputeRequest,
      source?: CancelTokenSource,
    ) => Promise<types.RespondAndChallengeDisputeResponse>;
    addDisputeNote: (
      params: types.AddDisputeNoteRequest,
      source?: CancelTokenSource,
    ) => Promise<types.AddDisputeNoteResponse>;
    acceptDispute: (
      params: types.AcceptDisputeRequest,
      source?: CancelTokenSource,
    ) => Promise<types.AcceptDisputeResponse>;
    fetchDisputeReasons: (
      params: types.DisputeReasonsRequest,
      source?: CancelTokenSource,
    ) => Promise<types.DisputeReasonsResponse>;
    submitGlobalSendAdminFundingOptions: (
      params: types.SubmitGlobalSendAdminFundingOptionsRequest,
      source?: CancelTokenSource,
    ) => Promise<types.ApiResponse>;
    fetchInteracAutoDepositStatus: (
      params: types.InteracAutoDepositStatusRequest,
      bearer?: string,
      source?: CancelTokenSource,
    ) => Promise<types.InteracAutoDepositStatusResponse>;
    fetchConvenienceFees: (
      params: types.ConvenienceFeesRequest,
      source?: CancelTokenSource,
    ) => Promise<types.ConvenienceFeesResponse>;
    fetchGlobalSendTransactionDetails: (
      params: types.GlobalSendTransactionDetailsRequest,
      source?: CancelTokenSource,
    ) => Promise<types.GlobalSendTransactionDetailsResponse>;
    fetchBillPayTransaction: (
      params: types.BillPayTransactionRequest,
      bearer?: string,
      source?: CancelTokenSource,
    ) => Promise<types.BillPayTransactionResponse>;
    generateBillPayEmbedUrl: (
      params: types.BillPayGenerateEmbedUrlRequest,
      source?: CancelTokenSource,
    ) => Promise<types.BillPayGenerateEmbedUrlResponse>;
    fetchMobileWalletProviders: (
      params: types.MobileWalletProvidersRequest,
      source?: CancelTokenSource,
    ) => Promise<types.MobileWalletProvidersResponse>;
    fetchGooglePayChargeTransaction: (
      params: types.TransactionRequest,
      bearer?: string,
      source?: CancelTokenSource,
    ) => Promise<types.GooglePayChargeTransactionResponse>;
    fetchPayPalWithdrawTransaction: (
      params: types.TransactionRequest,
      bearer?: string,
      source?: CancelTokenSource,
    ) => Promise<types.PayPalWithdrawTransactionResponse>;
    fetchVenmoWithdrawTransaction: (
      params: types.TransactionRequest,
      bearer?: string,
      source?: CancelTokenSource,
    ) => Promise<types.VenmoWithdrawTransactionResponse>;
    postVenmoWithdraw: (
      params: types.VenmoWithdrawRequest,
      source?: CancelTokenSource,
    ) => Promise<types.VenmoWithdrawResponse>;
    postPayPalWithdraw: (
      params: types.PayPalWithdrawRequest,
      source?: CancelTokenSource,
    ) => Promise<types.PayPalWithdrawResponse>;
    fetchVenmoRecipients: (
      params: types.VenmoRecipientsRequest,
      source?: CancelTokenSource,
    ) => Promise<types.VenmoRecipientsResponse>;
    fetchPayPalRecipients: (
      params: types.PayPalRecipientsRequest,
      source?: CancelTokenSource,
    ) => Promise<types.PayPalRecipientsResponse>;
    fetchPayPalDefaultRecipient: (
      params: types.PayPalDefaultRecipientRequest,
      source?: CancelTokenSource,
    ) => Promise<types.PayPalDefaultRecipientResponse>;
    fetchVenmoDefaultRecipient: (
      params: types.VenmoDefaultRecipientRequest,
      source?: CancelTokenSource,
    ) => Promise<types.VenmoDefaultRecipientResponse>;
    fetchAccountUserPermissionsNew: (
      params: types.AccountUserPermissionsRequest,
      bearer?: string,
      source?: CancelTokenSource,
    ) => Promise<types.AccountUserPermissionsNewResponse>;
    fetchSubAccountPermissions: (
      params: types.SubAccountPermissionsRequest,
      source?: CancelTokenSource,
    ) => Promise<types.SubAccountPermissionResponse>;
    saveAccountExtendedInfo: (
      params: types.SaveExtendedInfoRequest,
      bearer?: string,
      source?: CancelTokenSource,
    ) => Promise<types.ApiResponse>;
    savePartnerExtendedInfo: (
      params: types.SaveExtendedInfoRequest,
      bearer?: string,
      source?: CancelTokenSource,
    ) => Promise<types.ApiResponse>;
    fetchPartnerExtendedInfo: (
      bearer?: string,
      source?: CancelTokenSource,
    ) => Promise<types.PartnerExtendedInfoResponse>;
    uploadDocument: (
      params: types.DocumentUploadRequest,
      source?: CancelTokenSource,
    ) => Promise<types.DocumentUploadResponse>;
    AFTPull: (params: types.AFTPullRequest, source?: CancelTokenSource) => Promise<types.AFTPullResponse>;
    OCTPush: (params: types.OCTPushRequest, source?: CancelTokenSource) => Promise<types.OCTPushResponse>;
    fetchGLCodes: (source?: CancelTokenSource) => Promise<types.GLCodesResponse>;
    fetchTransactionCodes: (source?: CancelTokenSource) => Promise<types.TransactionCodesResponse>;
    blockAccounts: (params?: types.BlockAccountsRequest, source?: CancelTokenSource) => Promise<types.ApiResponse>;
    unblockAccounts: (params?: types.BlockAccountsRequest, source?: CancelTokenSource) => Promise<types.ApiResponse>;
    fetchApprovalUsers: (source?: CancelTokenSource) => Promise<types.FetchApprovalUsersResponse>;
    fetchDocument: (
      params: types.FetchDocumentRequest,
      bearer?: string,
      source?: CancelTokenSource,
    ) => Promise<types.FetchDocumentResponse>;
    fetchAccountTransfer: (
      params: types.AccountTransferRequest,
      bearer?: string,
      source?: CancelTokenSource,
    ) => Promise<types.AccountTransferResponse>;
    fetchTransactionHistory: (
      params: types.TransactionHistoryRequest,
      bearer?: string,
      source?: CancelTokenSource,
    ) => Promise<types.TransactionHistoryResponse>;
    fetchAccountBalances: (
      params: types.FetchAccountBalancesRequest,
      source?: CancelTokenSource,
    ) => Promise<types.FetchAccountBalancesResponse>;
    fetchVirtualAccounts: (
      params: types.FetchVirtualAccountsRequest,
      source?: CancelTokenSource,
    ) => Promise<types.FetchVirtualAccountsResponse>;
    createVirtualAccount: (
      params: types.CreateVirtualAccountRequest,
      source?: CancelTokenSource,
    ) => Promise<types.CreateVirtualAccountResponse>;
    fetchVirtualAccountCurrencies: (source?: CancelTokenSource) => Promise<types.FetchVirtualAccountCurrenciesResponse>;
    fetchMasterVirtualAccounts: (source?: CancelTokenSource) => Promise<types.FetchMasterVirtualAccountsResponse>;
    simulateVirtualAccountCredit: (
      params: types.SimulateVirtualAccountCreditRequest,
      source?: CancelTokenSource,
    ) => Promise<types.SimulateVirtualAccountCreditResponse>;
    fetchReconciliationTransactions: (
      params: types.ReconciliationTransactionsRequest,
      source?: CancelTokenSource,
    ) => Promise<types.ReconciliationTransactionsResponse>;
  };
